import { useState, useRef, useEffect } from "react";
import { saveProfile, saveChild, loadChildren, updateChild, removeChild as removeChildDB, saveStaff, loadStaff, recordSignIn, recordSignOut, loadTodayAttendance, loadTodayStaffAttendance, recordStaffClockIn, recordStaffClockOut, exportAttendanceCSV, saveTransportLog, loadTransportLog } from "./db";
import { enqueue, getPending, removeItem } from "./offlineQueue";

const F = "'Plus Jakarta Sans', sans-serif";
const shortName = name => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0];
  return parts[0] + " " + parts[parts.length-1][0] + ".";
};
const SMS_ENABLED = true; // Telnyx 10DLC approved and active
const formatPhone = (phone) => {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return '+1' + digits;
  if (digits.length === 11 && digits[0] === '1') return '+' + digits;
  return phone;
};

// ── TOTO PROFESSIONAL PLAYMATE PALETTE ──────────────────
const NAVY    = "#1A365D";   // Primary headers, back buttons, authority
const CRIMSON = "#991B1B";   // Sign In/Out primary action
const SKY     = "#0EA5E9";   // Transport, Staff accents
const SUN     = "#FBBF24";   // Messages, highlights
const OFF_WHITE = "#F8FAFC"; // App background
const SUCCESS = "#166534";   // Verified signature checkmark
const RADIUS  = 16;          // Universal border-radius

const SIGNIN_TILE = { id:"signin", label:"Children", sub:"Sign In / Out", color:CRIMSON, icon:"signin" };
const TILES = [
  { id:"transport", label:"Transport",       sub:"Pickup & drop-off",   color:SKY,       tint:"#EBF8FF", icon:"transport" },
  { id:"messages",  label:"Messages",        sub:"Contact families",    color:"#718096", tint:"#E2E8F0", icon:"messages"  },
  { id:"staff",     label:"Staff",           sub:"Clock in & out",      color:SKY,       tint:"#EBF8FF", icon:"staff"     },
  { id:"reports",   label:"Reports",         sub:"Attendance & export", color:NAVY,      tint:"#DBEAFE", icon:"reports"   },
  { id:"incident",  label:"Incident",        sub:"Log & document",      color:NAVY,      tint:"#DBEAFE", icon:"incident"  },
  { id:"settings",  label:"Settings",        sub:"Profile & billing",   color:"#718096", tint:"#E2E8F0", icon:"settings"  },
];

const initKids = [
  { id:1, name:"Sofia Ramirez", age:4, parent:"Maria Ramirez",  initials:"SR", hue:"#E8394D", status:"in",  signInTime:"7:42 AM", auth:"WCCC",    ageGroup:"preschool", transport:true,  transportStatus:null,        destination:"Lincoln Elem" },
  { id:2, name:"James Chen",    age:2, parent:"Wei Chen",        initials:"JC", hue:"#1AABDB", status:"in",  signInTime:"8:05 AM", auth:"Private", ageGroup:"toddler",   transport:false, transportStatus:null,        destination:null           },
  { id:3, name:"Amara Hassan",  age:5, parent:"Fatuma Hassan",   initials:"AH", hue:"#F5A623", status:"out", signInTime:null,      auth:"WCCC",    ageGroup:"preschool", transport:true,  transportStatus:"picked_up", destination:"Park Elem"    },
  { id:4, name:"Omar Diallo",   age:1, parent:"Aissata Diallo",  initials:"OD", hue:"#2ECC8A", status:"in",  signInTime:"7:55 AM", auth:"WCCC",    ageGroup:"infant",    transport:false, transportStatus:null,        destination:null           },
  { id:5, name:"Luna Park",     age:3, parent:"Jin Park",        initials:"LP", hue:"#9B59B6", status:"in",  signInTime:"8:20 AM", auth:"Private", ageGroup:"preschool", transport:false, transportStatus:null,        destination:null           },
];
const initStaff = [
  { id:101, name:"Rosa Mendoza",  role:"Provider",   initials:"RM", hue:"#E8394D", status:"in",  clockIn:"7:30 AM", pin:"1234", driver:true  },
  { id:102, name:"Aisha Johnson", role:"Assistant",  initials:"AJ", hue:"#1AABDB", status:"in",  clockIn:"8:00 AM", pin:"5678", driver:false },
  { id:103, name:"Mei Zhang",     role:"Substitute", initials:"MZ", hue:"#F5A623", status:"out", clockIn:null,      pin:"9012", driver:true  },
];
const CHILD_HUES = ["#E8394D","#1AABDB","#F5A623","#2ECC8A","#9B59B6","#FF8FAB","#F4A261","#87CEEB"];
const STAFF_HUES = ["#E8394D","#4A6CF7","#F5A623","#9B59B6","#2ECC8A","#1AABDB"];

function ratio(kids, staff) {
  const inK = kids.filter(k => k.status === "in");
  const inS = staff.filter(s => s.status === "in").length;
  const inf = inK.filter(k => k.ageGroup === "infant").length;
  const max = inS === 0 ? 0 : inS === 1 ? 6 : 12;
  const mxi = inS === 0 ? 0 : inS === 1 ? 2 : 4;
  return { ok: inK.length <= max && inf <= mxi, count: inK.length, max, staff: inS };
}

// ── ICONS ──────────────────────────────────────────────────
function Icon({ type, size = 26, color = "#fff" }) {
  const s = { width: size, height: size };
  if (type === "signin")    return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M6 20v-1a6 6 0 0112 0v1"/><polyline points="16 11 18 13 22 9"/></svg>;
  if (type === "staff")     return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="7" r="3"/><path d="M3 20v-1a5 5 0 0110 0v1"/><circle cx="17" cy="9" r="2.5"/><path d="M14 20v-1a4 4 0 018 0v1"/></svg>;
  if (type === "transport") return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>;
  if (type === "reports")   return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/></svg>;
  if (type === "incident")  return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="2"/><path d="M9 12l2 2 4-4"/></svg>;
  if (type === "ai")        return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/><circle cx="12" cy="16" r="1.5" fill={color} stroke="none"/></svg>;
  if (type === "messages")  return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/><circle cx="9" cy="10" r="1" fill={color}/><circle cx="12" cy="10" r="1" fill={color}/><circle cx="15" cy="10" r="1" fill={color}/></svg>;
  if (type === "home")      return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
  if (type === "back")      return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>;
  if (type === "phone")     return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .82h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>;
  if (type === "settings")  return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>;
  if (type === "arrow")     return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>;
  return null;
}

// ── AVATAR ─────────────────────────────────────────────────
function Avatar({ person, size = 46 }) {
  if (person.photo) {
    return (
      <div style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden", flexShrink: 0 }}>
        <img src={person.photo} alt={person.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: person.hue, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.36, fontFamily: F, fontWeight: 900, color: "#fff", flexShrink: 0 }}>
      {person.initials}
    </div>
  );
}

// ── TOAST ──────────────────────────────────────────────────
function Toast({ msg }) {
  return msg ? (
    <div style={{ position: "fixed", bottom: 90, left: "50%", transform: "translateX(-50%)", background: "#2D3748", color: "#fff", padding: "12px 20px", borderRadius: 12, fontSize: 14, fontFamily: F, fontWeight: 700, zIndex: 9999, whiteSpace: "nowrap", boxShadow: "0 4px 20px rgba(0,0,0,0.2)" }}>
      {msg}
    </div>
  ) : null;
}

// ── BOTTOM SHEET ───────────────────────────────────────────
function Sheet({ children, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "flex-end" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }} onClick={onClose} />
      <div style={{ position: "relative", background: "#fff", borderRadius: "22px 22px 0 0", padding: "0 10px 36px", width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 -4px 30px rgba(0,0,0,0.12)" }}>
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 18px" }}>
          <div style={{ width: 32, height: 4, borderRadius: 2, background: "#E2E8F0" }} />
        </div>
        {children}
      </div>
    </div>
  );
}

// ── SIGNATURE PAD ──────────────────────────────────────────
function SigPad({ child, action, onConfirm, onCancel }) {
  const ref = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [signed, setSigned] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const isIn = action === "in";
  const accentColor = "#E8394D";
  const label = action === "in" ? "Sign In" : "Sign Out";

  const pos = (e, c) => { const r = c.getBoundingClientRect(), s = e.touches ? e.touches[0] : e; const scaleX = c.width / r.width, scaleY = c.height / r.height; return { x: (s.clientX - r.left) * scaleX, y: (s.clientY - r.top) * scaleY }; };
  const start = e => { const c = ref.current, ctx = c.getContext("2d"), p = pos(e, c); ctx.beginPath(); ctx.moveTo(p.x, p.y); setDrawing(true); setSigned(true); };
  const draw  = e => { if (!drawing) return; e.preventDefault(); const c = ref.current, ctx = c.getContext("2d"), p = pos(e, c); ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#2D3748"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.stroke(); };
  const stop  = () => setDrawing(false);
  const handleConfirm = () => {
    const sigData = ref.current.toDataURL("image/png");
    setShowSuccess(true);
    setTimeout(() => { setShowSuccess(false); onConfirm(sigData); }, 800);
  };
  const clear = () => { ref.current.getContext("2d").clearRect(0, 0, ref.current.width, ref.current.height); setSigned(false); };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#fff", zIndex: 9999, display: "flex", flexDirection: "column", padding: "16px 16px 24px" }}>
      {/* Header */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');`}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <div style={{ width: 46, height: 46, borderRadius: 14, background: accentColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon type="signin" size={24} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 20, fontFamily: "'Playfair Display', serif", color: "#2D3748", fontWeight: 700, lineHeight: 1.2 }}>{shortName(child.name)}</div>
          <div style={{ fontSize: 13, fontFamily: "'Playfair Display', serif", color: accentColor, fontWeight: 600, marginTop: 2 }}>{label}</div>
        </div>
        <button onClick={onCancel} style={{ width: 36, height: 36, borderRadius: 10, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 18, cursor: "pointer", color: "#718096", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: F }}>✕</button>
      </div>

      {/* Signature label */}
      <div style={{ fontSize: 11, fontFamily: "'Playfair Display', serif", fontWeight: 700, color: "#A0AEC0", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Parent Signature</div>

      {/* Canvas — fills remaining space */}
      <div style={{ flex: 1, border: "2px dashed #E2E8F0", borderRadius: 16, background: "#FAFAFA", position: "relative", touchAction: "none", marginBottom: 14, minHeight: 0 }}>
        <canvas ref={ref} width={1000} height={1000}
          style={{ display: "block", width: "100%", height: "100%", borderRadius: 14, cursor: "crosshair" }}
          onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={stop} />
        {!signed && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
            <div style={{ fontSize: 56, marginBottom: 12 }}>✍️</div>
            <div style={{ color: "#94A3B8", fontSize: 26, fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>Sign here</div>
            <div style={{ color: "#CBD5E0", fontSize: 14, fontFamily: "'Playfair Display', serif", fontWeight: 600, marginTop: 6, fontStyle: "italic" }}>Use your finger to sign above</div>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: "15px", borderRadius: 14, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 13, fontFamily: F, fontWeight: 700, cursor: "pointer", color: "#718096" }}>Cancel</button>
        <button onClick={clear} style={{ flex: 1, padding: "15px", borderRadius: 14, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 13, fontFamily: F, fontWeight: 700, cursor: "pointer", color: "#718096" }}>Clear</button>
        <button onClick={handleConfirm} disabled={!signed} style={{ flex: 2, padding: "15px 16px", borderRadius: 14, border: "none", background: signed ? accentColor : "#E2E8F0", color: signed ? "#fff" : "#A0AEC0", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: signed ? "pointer" : "not-allowed" }}>
          ✓ Confirm {label}
        </button>
      </div>
    </div>
  );
}

// ── PIN PAD ────────────────────────────────────────────────
function PinPad({ person, onConfirm, onCancel }) {
  const [pin, setPin] = useState(""), [err, setErr] = useState(false);
  const accentColor = "#4A6CF7";

  const tap = n => {
    if (err) { setErr(false); setPin(""); return; }
    const next = pin + n;
    if (next.length === 4) {
      if (next === person.pin) onConfirm();
      else { setErr(true); setTimeout(() => { setErr(false); setPin(""); }, 600); }
    } else setPin(next);
  };

  const nums = [1, 2, 3, 4, 5, 6, 7, 8, 9, "", 0, "⌫"];
  return (
    <Sheet onClose={onCancel}>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ width: 64, height: 64, borderRadius: 20, background: accentColor, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
          <Icon type="staff" size={32} />
        </div>
        <div style={{ fontSize: 20, fontFamily: F, color: "#2D3748", fontWeight: 800 }}>{person.name}</div>
        <div style={{ fontSize: 13, fontFamily: F, color: accentColor, marginTop: 4, fontWeight: 700 }}>{person.status === "in" ? "Clock Out" : "Clock In"}</div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 18 }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={{ width: 14, height: 14, borderRadius: "50%", background: err ? "#E8394D" : i < pin.length ? "#2D3748" : "#E2E8F0", transition: "background .15s" }} />
          ))}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
        {nums.map((n, i) => (
          <button key={i} onClick={() => { if (n === "⌫") setPin(p => p.slice(0, -1)); else if (n !== "") tap(String(n)); }}
            style={{ padding: "16px 0", borderRadius: 14, border: n === "" ? "none" : "1.5px solid #E2E8F0", background: n === "" ? "transparent" : "#fff", fontSize: 20, fontFamily: F, fontWeight: 700, cursor: n === "" ? "default" : "pointer", color: "#2D3748" }}>
            {n}
          </button>
        ))}
      </div>
      <button onClick={onCancel} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 13, fontFamily: F, fontWeight: 700, cursor: "pointer", color: "#718096" }}>Cancel</button>
    </Sheet>
  );
}

// ── ADD CHILD ──────────────────────────────────────────────
function AddChild({ onAdd, onClose }) {
  const [f, setF] = useState({ name: "", age: "", parent: "", parentPhone: "", parentEmail: "", auth: "WCCC", ageGroup: "preschool", transport: false, destination: "", ec1Name: "", ec1Phone: "", ec1Rel: "", ec2Name: "", ec2Phone: "", ec2Rel: "" });
  const [err, setErr] = useState("");
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));
  const go = () => {
    if (!f.name.trim()) { setErr("Name required"); return; }
    if (!f.age || isNaN(f.age)) { setErr("Valid age required"); return; }
    const initials = f.name.trim().split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
    onAdd({ id: Date.now(), name: f.name.trim(), age: parseInt(f.age), parent: f.parent.trim() || "Unknown", parentPhone: f.parentPhone, parentEmail: f.parentEmail, initials, hue: CHILD_HUES[Math.floor(Math.random() * CHILD_HUES.length)], status: "out", signInTime: null, auth: f.auth, ageGroup: f.ageGroup, transport: f.transport, transportStatus: null, destination: f.destination.trim() || null, ec1Name: f.ec1Name, ec1Phone: f.ec1Phone, ec1Rel: f.ec1Rel, ec2Name: f.ec2Name, ec2Phone: f.ec2Phone, ec2Rel: f.ec2Rel });
  };
  const inp = { width: "100%", padding: "14px 16px", borderRadius: 12, border: "1.5px solid #E2E8F0", fontSize: 16, fontFamily: F, outline: "none", color: "#1F2937", boxSizing: "border-box", marginBottom: 14, background: "#fff", transition: "border-color 0.15s" };
  const lbl = { fontSize: 15, fontFamily: F, fontWeight: 800, color: "#A0AEC0", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 5, display: "block" };
  const RELS = ["Father","Mother","Grandparent","Aunt/Uncle","Sibling","Family Friend","Other"];
  return (
    <Sheet onClose={onClose}>
      <div style={{ fontSize: 22, fontFamily: F, color: "#2D3748", fontWeight: 800, marginBottom: 20 }}>Add a child</div>

      {/* Basic info */}
      <label style={lbl}>Full Name *</label>
      <input style={inp} value={f.name} onChange={e => s("name", e.target.value)} placeholder="e.g. Sofia Ramirez" />
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1 }}><label style={lbl}>Age *</label><input style={{ ...inp }} type="number" min="0" max="12" value={f.age} onChange={e => s("age", e.target.value)} placeholder="4" /></div>
        <div style={{ flex: 2 }}><label style={lbl}>Age Group</label>
          <select style={{ ...inp, cursor: "pointer" }} value={f.ageGroup} onChange={e => s("ageGroup", e.target.value)}>
            <option value="infant">Infant (0–1)</option><option value="toddler">Toddler (1–2)</option>
            <option value="preschool">Preschool (3–5)</option><option value="school">School Age (5+)</option>
          </select>
        </div>
      </div>
      <label style={lbl}>Parent / Guardian</label>
      <input style={inp} value={f.parent} onChange={e => s("parent", e.target.value)} placeholder="e.g. Maria Ramirez" />
      <label style={lbl}>Parent Phone</label>
      <input style={inp} type="tel" value={f.parentPhone} onChange={e => s("parentPhone", e.target.value)} placeholder="e.g. (206) 555-1234" />
      <label style={lbl}>Parent Email</label>
      <input style={inp} type="email" value={f.parentEmail} onChange={e => s("parentEmail", e.target.value)} placeholder="e.g. parent@email.com" />
      <label style={lbl}>Payment</label>
      <select style={{ ...inp, cursor: "pointer" }} value={f.auth} onChange={e => s("auth", e.target.value)}>
        <option value="WCCC">WCCC Subsidy</option><option value="Private">Private Pay</option>
      </select>
      <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, color: "#2D3748", marginBottom: 14, fontFamily: F }}>
        <input type="checkbox" checked={f.transport} onChange={e => s("transport", e.target.checked)} style={{ width: 18, height: 18, accentColor: "#E8394D" }} />
        🚐 Needs transport
      </label>
      {f.transport && <><label style={lbl}>Destination</label><input style={inp} value={f.destination} onChange={e => s("destination", e.target.value)} placeholder="e.g. Lincoln Elementary" /></>}

      {/* Emergency contacts */}
      <div style={{ background: "#F8F9FB", borderRadius: 16, padding: "16px", border: "1.5px solid #F0F2F5", marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#2D3748", marginBottom: 14 }}>🚨 Emergency Contacts</div>

        {/* Contact 1 */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#E8394D", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Contact 1</div>
          <label style={lbl}>Full Name</label>
          <input style={inp} value={f.ec1Name} onChange={e => s("ec1Name", e.target.value)} placeholder="e.g. Juan Ramirez" />
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Phone</label>
              <input style={inp} type="tel" value={f.ec1Phone} onChange={e => s("ec1Phone", e.target.value)} placeholder="(206) 555-0001" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Relationship</label>
              <select style={{ ...inp, cursor: "pointer", WebkitAppearance: "menulist", appearance: "menulist" }} value={f.ec1Rel} onChange={e => s("ec1Rel", e.target.value)} onBlur={e => s("ec1Rel", e.target.value)}>
                <option value="">Select...</option>
                {RELS.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div style={{ borderTop: "1px solid #E2E8F0", marginBottom: 14 }} />

        {/* Contact 2 */}
        <div>
          <div style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#4A6CF7", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Contact 2</div>
          <label style={lbl}>Full Name</label>
          <input style={inp} value={f.ec2Name} onChange={e => s("ec2Name", e.target.value)} placeholder="e.g. Ana Ramirez" />
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Phone</label>
              <input style={inp} type="tel" value={f.ec2Phone} onChange={e => s("ec2Phone", e.target.value)} placeholder="(206) 555-0002" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Relationship</label>
              <select style={{ ...inp, cursor: "pointer", WebkitAppearance: "menulist", appearance: "menulist" }} value={f.ec2Rel} onChange={e => s("ec2Rel", e.target.value)} onBlur={e => s("ec2Rel", e.target.value)}>
                <option value="">Select...</option>
                {RELS.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {err && <div style={{ color: "#E8394D", fontSize: 13, fontFamily: F, fontWeight: 700, marginBottom: 10 }}>⚠ {err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <button onClick={onClose} style={{ flex: 1, padding: "13px", borderRadius: 12, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 13, fontFamily: F, fontWeight: 700, cursor: "pointer", color: "#718096" }}>Cancel</button>
        <button onClick={go} style={{ flex: 2, padding: "14px 24px", borderRadius: 12, border: "none", background: "#E8394D", color: "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>Add child</button>
      </div>
    </Sheet>
  );
}

// ── ADD STAFF ──────────────────────────────────────────────
function AddStaff({ onAdd, onClose }) {
  const [f, setF] = useState({ name: "", role: "assistant", pin: "", driver: false });
  const [err, setErr] = useState("");
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));
  const go = () => {
    if (!f.name.trim()) { setErr("Name required"); return; }
    if (f.pin.length !== 4 || isNaN(f.pin)) { setErr("PIN must be 4 digits"); return; }
    const initials = f.name.trim().split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
    onAdd({ id: Date.now(), name: f.name.trim(), role: f.role, initials, hue: STAFF_HUES[Math.floor(Math.random() * STAFF_HUES.length)], status: "out", clockIn: null, pin: f.pin, driver: f.driver });
  };
  const inp = { width: "100%", padding: "13px 14px", borderRadius: 12, border: "1.5px solid #E2E8F0", fontSize: 15, fontFamily: F, outline: "none", color: "#2D3748", boxSizing: "border-box", marginBottom: 12, background: "#fff" };
  const lbl = { fontSize: 15, fontFamily: F, fontWeight: 800, color: "#A0AEC0", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 5, display: "block" };
  return (
    <Sheet onClose={onClose}>
      <div style={{ fontSize: 22, fontFamily: F, color: "#2D3748", fontWeight: 800, marginBottom: 20 }}>Add staff</div>
      <label style={lbl}>Full Name *</label>
      <input style={inp} value={f.name} onChange={e => s("name", e.target.value)} placeholder="e.g. Maria Lopez" />
      <label style={lbl}>Role</label>
      <select style={{ ...inp, cursor: "pointer" }} value={f.role} onChange={e => s("role", e.target.value)}>
        <option value="provider">Provider</option><option value="assistant">Assistant</option><option value="substitute">Substitute</option>
      </select>
      <label style={lbl}>4-Digit PIN *</label>
      <input style={inp} type="password" maxLength={4} value={f.pin} onChange={e => s("pin", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="••••" />
      <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, color: "#2D3748", marginBottom: 14, fontFamily: F }}>
        <input type="checkbox" checked={f.driver} onChange={e => s("driver", e.target.checked)} style={{ width: 18, height: 18, accentColor: "#4A6CF7" }} />
        🚐 Authorized driver
      </label>
      {err && <div style={{ color: "#E8394D", fontSize: 13, fontFamily: F, fontWeight: 700, marginBottom: 10 }}>⚠ {err}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onClose} style={{ flex: 1, padding: "13px", borderRadius: 12, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 13, fontFamily: F, fontWeight: 700, cursor: "pointer", color: "#718096" }}>Cancel</button>
        <button onClick={go} style={{ flex: 2, padding: "14px 24px", borderRadius: 12, border: "none", background: "#4A6CF7", color: "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>Add staff</button>
      </div>
    </Sheet>
  );
}

// ── SCREEN HEADER ──────────────────────────────────────────
function ScreenHeader({ tile, onBack }) {
  return (
    <div style={{ borderTop: `4px solid ${tile.color}`, display: "flex", alignItems: "center", gap: 14, padding: "52px 18px 16px", borderBottom: "1.5px solid #E2E8F0", background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
      <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 7, padding: "13px 22px", borderRadius: 12, background: "#EFF6FF", border: "2px solid #1A365D", cursor: "pointer", flexShrink: 0 }}>
        <Icon type="back" size={20} color={NAVY} />
        <span style={{ fontSize: 16, fontFamily: F, fontWeight: 900, color: NAVY }}>Back</span>
      </button>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: tile.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: `0 3px 8px ${tile.color}44` }}>
        <Icon type={tile.icon} size={22} />
      </div>
      <div style={{ fontSize: 22, fontFamily: F, color: "#1A202C", fontWeight: 900, letterSpacing: "-0.3px" }}>{tile.label}</div>
    </div>
  );
}

// ── HOME ───────────────────────────────────────────────────
function Home({ kids, staff, rat, lang, setLang, onNav, profile }) {
  const inKids = kids.filter(k => k.status === "in").length;
  const now = new Date();
  const hour = now.getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const dateStr = now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const daycareN = profile?.daycareN || "Your Daycare";
  const providerFirst = profile?.providerN?.split(" ")[0] || "there";
  const [langToast, setLangToast] = useState(false);

  const handleLang = () => {
    setLangToast(true);
    setTimeout(() => setLangToast(false), 2500);
  };

  return (
    <div style={{ background: "#F0F4F8", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <style>{`
        .toto-dashboard-inner { display: flex; flex-direction: column; gap: 14px; padding: 0 14px 14px; flex: 1; }
        @media (min-width: 1180px) {
          .toto-dashboard-inner { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; padding: 24px 32px 32px; align-items: start; min-height: calc(100vh - 90px); }
          .toto-left-col { display: flex; flex-direction: column; gap: 16px; }
          .toto-stats-strip { display: flex; flex-direction: column; gap: 10px; }
          .toto-stat-item { display: flex; align-items: center; gap: 14px; padding: 14px 18px; background: #fff; border-radius: 16px; border-left: 4px solid; box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
          .toto-stat-val { font-size: 32px !important; }
          .toto-stat-sub { display: block !important; }
          .toto-tile-grid { gap: 14px !important; }
          .toto-tile-sub-text { display: none !important; }
          @media (min-width: 768px) and (max-width: 1179px) {
            .toto-tile-grid { gap: 16px !important; }
            .toto-tile-label { font-size: 14px !important; }
            .toto-tile-sub { font-size: 12px !important; }
          }
          .toto-section-label { display: block !important; }
        }
      `}</style>
      <div style={{ background: OFF_WHITE, flex: 1, display: "flex", flexDirection: "column", minHeight: "calc(100vh - 90px)" }}>

        {/* Header card */}
        <div style={{ background: "#fff", boxShadow: "0 2px 12px rgba(26,54,93,0.06)", padding: "14px 24px 14px", marginBottom: 0, position: "relative", borderBottom: "1px solid #E2E8F0" }}>
          <style>{`.toto-header-row { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 14px; } @media (min-width: 1180px) { .toto-header-row { align-items: center; margin-bottom: 0; } .toto-header-greeting { display: none; } .toto-header-greeting-desktop { display: block !important; } }`}</style>

          {/* Top row */}
          <div className="toto-header-row" style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <img src="/logo512.png" alt="Toto logo" style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0 }} />
              <span style={{ fontSize: 24, fontFamily: "Georgia, serif", fontWeight: 900, color: "#b03020", letterSpacing: "-1px", lineHeight: 1 }}>toto.</span>
            </div>
            {/* Greeting — centered in header on desktop only */}
            <div className="toto-header-greeting-desktop" style={{ display: "none", textAlign: "center", flex: 1 }}>
              <div style={{ fontSize: 17, fontFamily: F, fontWeight: 700, color: NAVY, letterSpacing: "-0.01em" }}>{daycareN}</div>
              <div style={{ fontSize: 11, fontFamily: F, color: "#718096", fontWeight: 500, marginTop: 2 }}>{greet}, {providerFirst} 👋 &nbsp;·&nbsp; {dateStr}</div>
            </div>
            {/* Unified icon button group */}
            <div style={{ display: "flex", alignItems: "center", gap: 2, background: "#fff", borderRadius: 100, border: "1.5px solid #E2E8F0", padding: "4px 6px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              {/* Language */}
              <button onClick={handleLang} style={{ width: 52, height: 52, borderRadius: "50%", background: "transparent", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.background = "#EFF6FF"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                onTouchStart={e => e.currentTarget.style.background = "#EFF6FF"}
                onTouchEnd={e => e.currentTarget.style.background = "transparent"}>
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#38A169" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
                </svg>
              </button>
              {/* Divider */}
              <div style={{ width: 1, height: 18, background: "#E2E8F0" }} />
              {/* Support */}
              <button onClick={() => onNav("support")} style={{ width: 52, height: 52, borderRadius: "50%", background: "transparent", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.background = "#FFF5F5"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                onTouchStart={e => e.currentTarget.style.background = "#FFF5F5"}
                onTouchEnd={e => e.currentTarget.style.background = "transparent"}>
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#E8394D" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><circle cx="12" cy="17" r="0.5" fill="#E8394D"/>
                </svg>
              </button>
              {/* Divider */}
              <div style={{ width: 1, height: 18, background: "#E2E8F0" }} />
              {/* Settings */}
              <button onClick={() => onNav("settings")} style={{ width: 52, height: 52, borderRadius: "50%", background: "transparent", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.background = "#F4F6F9"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                onTouchStart={e => e.currentTarget.style.background = "#F4F6F9"}
                onTouchEnd={e => e.currentTarget.style.background = "transparent"}>
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#718096" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Daycare name + greeting — mobile only */}
          <div className="toto-header-greeting" style={{ textAlign: "center" }}>
            <div style={{ fontSize: 18, fontFamily: F, fontWeight: 900, color: NAVY, letterSpacing: "-0.02em" }}>{daycareN}</div>
            <div style={{ fontSize: 12, fontFamily: F, color: "#718096", fontWeight: 500, marginTop: 3 }}>{greet}, {providerFirst} 👋 &nbsp;·&nbsp; {dateStr}</div>

          </div>

          {/* Coming soon toast */}
          {langToast && (
            <div style={{ position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)", background: "#1A202C", color: "#fff", padding: "10px 18px", borderRadius: 12, fontSize: 13, fontFamily: F, fontWeight: 700, whiteSpace: "nowrap", boxShadow: "0 4px 20px rgba(0,0,0,0.2)", zIndex: 999, display: "flex", alignItems: "center", gap: 8 }}>
              <span>🌐</span> Spanish coming soon!
            </div>
          )}
        </div>

        {/* ── RESPONSIVE DASHBOARD BODY ── */}
        <div className="toto-dashboard-inner">

          {/* LEFT COL: stats + sign in/out */}
          <div className="toto-left-col">

            {/* Stats */}
            <div>
              <div style={{ fontSize: 12, fontFamily: F, fontWeight: 600, color: "#94A3B8", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.10em" }}>Today's Overview</div>
              <div className="toto-stats-strip" style={{ display: "flex", gap: 8 }}>
                {[
                  { val: inKids, label: "In Care", sub: "Children signed in", color: "#166534", bg: "#F0FFF4" },
                  { val: kids.filter(k => k.transport && k.transportStatus === "picked_up").length, label: "In Transit", sub: "On pickup or drop-off", color: "#F5A623", bg: "#FFFBF0" },
                  { val: staff.filter(s => s.status === "in").length, label: "Staff On", sub: "Clocked in & active", color: "#4A6CF7", bg: "#EBF4FF" },
                ].map(s => (
                  <div key={s.label} className="toto-stat-item" style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: s.bg, borderRadius: 14, borderLeft: `4px solid ${s.color}` }}>
                    <div className="toto-stat-val" style={{ fontSize: 28, fontFamily: F, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.val}</div>
                    <div>
                      <div style={{ fontSize: 14, fontFamily: F, color: s.color, fontWeight: 700, lineHeight: 1.3 }}>{s.label}</div>
                      <div className="toto-stat-sub" style={{ display: "none", fontSize: 12, fontFamily: F, color: s.color, fontWeight: 600, opacity: 0.85, marginTop: 2 }}>{s.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sign In/Out */}
            <div>
              <div style={{ fontSize: 12, fontFamily: F, fontWeight: 600, color: "#94A3B8", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.10em" }} className="toto-section-label" >Primary Action</div>
              <button onClick={() => onNav("signin")}
                style={{ width: "100%", background: "#fff", borderRadius: 20, boxShadow: `0 4px 20px ${CRIMSON}28, 0 1px 4px rgba(0,0,0,0.06)`, border: "none", cursor: "pointer", padding: 0, display: "block", textAlign: "left", transition: "transform 0.15s cubic-bezier(0.34,1.56,0.64,1)" }}
                onMouseDown={e => e.currentTarget.style.transform = "scale(0.98)"}
                onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
                onTouchStart={e => e.currentTarget.style.transform = "scale(0.98)"}
                onTouchEnd={e => e.currentTarget.style.transform = "scale(1)"}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "18px 20px", borderLeft: `5px solid ${CRIMSON}`, borderRadius: 20 }}>
                  <div style={{ width: 52, height: 52, borderRadius: 16, background: CRIMSON, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: `0 6px 18px ${CRIMSON}55` }}>
                    <Icon type="signin" size={28} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 18, fontFamily: F, fontWeight: 800, color: "#1A202C", lineHeight: 1.2 }}>Children</div>
                    <div style={{ fontSize: 14, fontFamily: F, fontWeight: 600, color: "#718096", marginTop: 3 }}>Sign In / Out</div>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={CRIMSON} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                </div>
              </button>
            </div>
          </div>

          {/* RIGHT COL: 3x2 tile grid */}
          <div>
            <div style={{ fontSize: 12, fontFamily: F, fontWeight: 600, color: "#94A3B8", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.10em" }} className="toto-section-label">Quick Access</div>
            <div style={{ background: "#fff", borderRadius: 24, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", padding: "20px 16px 24px" }}>
              <style>{`
                .toto-tile-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px 12px; }
              `}</style>
              <div className="toto-tile-grid">
                {TILES.map(tile => (
                  <button key={tile.id} onClick={() => onNav(tile.id)}
                    style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: 0, width: "100%" }}>
                    <div
                      style={{ width: "100%", aspectRatio: "1", maxWidth: 140, borderRadius: 22, background: tile.tint, border: `1px solid ${tile.color}30`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 10px 30px ${tile.color}40, 0 3px 10px rgba(0,0,0,0.06)`, transition: "transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)", position: "relative", overflow: "hidden" }}
                      onMouseDown={e => { e.currentTarget.style.transform = "scale(0.88)"; }}
                      onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; }}
                      onTouchStart={e => { e.currentTarget.style.transform = "scale(0.88)"; }}
                      onTouchEnd={e => { e.currentTarget.style.transform = "scale(1)"; }}
                    >
                      <div style={{ position: "absolute", bottom: -18, right: -18, width: 72, height: 72, borderRadius: "50%", background: tile.color, opacity: 0.06 }} />
                      <div style={{ width: 58, height: 58, borderRadius: 17, background: tile.color, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 6px 18px ${tile.color}66` }}>
                        <Icon type={tile.icon} size={29} />
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 17, fontFamily: F, fontWeight: 700, color: "#1E293B", lineHeight: 1.2 }}>{tile.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* Footer watermark */}
        <div style={{ textAlign: "center", padding: "14px 0 20px" }}>
          <span style={{ fontSize: 10, fontFamily: F, fontWeight: 300, color: "#CBD5E0", letterSpacing: "0.08em" }}>toto · totocare.app · tracking.simplified.</span>
        </div>

      </div>
    </div>
  );
}

// ── EDIT CHILD ─────────────────────────────────────────────
function EditChild({ kid, onSave, onRemove, onClose }) {
  const [name,        setName]        = useState(kid.name         || "");
  const [age,         setAge]         = useState(kid.age          || "");
  const [parent,      setParent]      = useState(kid.parent       || "");
  const [transport,   setTransport]   = useState(kid.transport    || false);
  const [destination, setDestination] = useState(kid.destination  || "");
  const [auth,        setAuth]        = useState(kid.auth         || "Private");
  const [photo,       setPhoto]       = useState(kid.photo        || null);
  const [parentPhone, setParentPhone] = useState(kid.parentPhone  || "");
  const [parentEmail, setParentEmail] = useState(kid.parentEmail  || "");
  const [notify,      setNotify]      = useState(kid.notify       || "sms");
  const [ec1Name,     setEc1Name]     = useState(kid.ec1Name      || "");
  const [ec1Phone,    setEc1Phone]    = useState(kid.ec1Phone     || "");
  const [ec1Rel,      setEc1Rel]      = useState(kid.ec1Rel       || "");
  const [ec2Name,     setEc2Name]     = useState(kid.ec2Name      || "");
  const [ec2Phone,    setEc2Phone]    = useState(kid.ec2Phone     || "");
  const [ec2Rel,      setEc2Rel]      = useState(kid.ec2Rel       || "");
  const [confirmDel,  setConfirmDel]  = useState(false);
  const fileRef = useRef(null);

  const inp = { width: "100%", padding: "11px 13px", borderRadius: 11, border: "1.5px solid #E2E8F0", fontSize: 13, fontFamily: F, color: "#2D3748", outline: "none", boxSizing: "border-box", marginBottom: 12 };
  const lbl = { fontSize: 11, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6, display: "block" };

  const handlePhoto = async e => {
    const file = e.target.files[0];
    if (!file) return;
    const { supabase } = await import("./supabase");
    const ext = file.name.split(".").pop();
    const path = `${kid.providerId || kid.user_id || "shared"}/${kid.id}_${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("child-photos").upload(path, file, { upsert: true });
    if (error) { alert("Photo upload failed: " + error.message); return; }
    const { data } = supabase.storage.from("child-photos").getPublicUrl(path);
    setPhoto(data.publicUrl);
  };

  const save = () => {
    if (!name.trim()) return;
    const initials = name.trim().split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    onSave({ ...kid, name: name.trim(), age: parseInt(age) || kid.age, parent: parent.trim(), transport, destination: transport ? destination : null, auth, photo, parentPhone, parentEmail, notify, initials, ec1Name, ec1Phone, ec1Rel, ec2Name, ec2Phone, ec2Rel });
    onClose();
  };

  return (
    <Sheet onClose={onClose}>
      {/* Photo + name */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <Avatar person={{ ...kid, photo, name }} size={72} />
          <button onClick={() => fileRef.current.click()}
            style={{ position: "absolute", bottom: -4, right: -4, width: 26, height: 26, borderRadius: "50%", background: "#E8394D", border: "2.5px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
            <span style={{ fontSize: 13, color: "#fff", lineHeight: 1 }}>📷</span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} style={{ display: "none" }} />
        </div>
        <div>
          <div style={{ fontSize: 16, fontFamily: F, fontWeight: 900, color: "#2D3748" }}>Edit Child Profile</div>
          <button onClick={() => fileRef.current.click()}
            style={{ fontSize: 12, fontFamily: F, fontWeight: 800, color: "#E8394D", background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: 4 }}>
            {photo ? "Change photo" : "Add profile photo"}
          </button>
        </div>
      </div>

      {/* Child info */}
      <div style={{ background: "#F8F9FB", borderRadius: 16, padding: "16px", border: "1.5px solid #F0F2F5", marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#2D3748", marginBottom: 14 }}>👶 Child Info</div>
        <label style={lbl}>Full Name *</label>
        <input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Sofia Ramirez" />
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Age</label>
            <input style={inp} type="number" min="0" max="12" value={age} onChange={e => setAge(e.target.value)} placeholder="e.g. 4" />
          </div>
          <div style={{ flex: 2 }}>
            <label style={lbl}>Parent / Guardian Name</label>
            <input style={inp} value={parent} onChange={e => setParent(e.target.value)} placeholder="e.g. Maria Ramirez" />
          </div>
        </div>
      </div>

      {/* Payment type */}
      <div style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Payment Type</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {["Private", "WCCC"].map(type => (
          <button key={type} onClick={() => setAuth(type)}
            style={{ flex: 1, padding: "11px", borderRadius: 12, border: `2px solid ${auth === type ? "#4A6CF7" : "#E2E8F0"}`, background: auth === type ? "#EBF4FF" : "#fff", fontSize: 13, fontFamily: F, fontWeight: 800, color: auth === type ? "#4A6CF7" : "#718096", cursor: "pointer" }}>
            {type}
          </button>
        ))}
      </div>

      {/* Parent contact */}
      <div style={{ background: "#F8F9FB", borderRadius: 16, padding: "16px", border: "1.5px solid #F0F2F5", marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#2D3748", marginBottom: 14 }}>📲 Parent Contact</div>
        <label style={lbl}>Phone Number</label>
        <input style={inp} type="tel" value={parentPhone} onChange={e => setParentPhone(e.target.value)} placeholder="e.g. (206) 555-1234" />
        <label style={lbl}>Email Address</label>
        <input style={{ ...inp, marginBottom: 14 }} type="email" value={parentEmail} onChange={e => setParentEmail(e.target.value)} placeholder="e.g. parent@email.com" />
        <div style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Notify Via</div>
        <div style={{ display: "flex", gap: 8 }}>
          {[{ id:"sms", label:"📱 Text" }, { id:"email", label:"📧 Email" }, { id:"both", label:"Both" }].map(n => (
            <button key={n.id} onClick={() => setNotify(n.id)}
              style={{ flex: 1, padding: "9px", borderRadius: 10, border: `2px solid ${notify === n.id ? "#E91E8C" : "#E2E8F0"}`, background: notify === n.id ? "#FEF0F7" : "#fff", fontSize: 12, fontFamily: F, fontWeight: 800, color: notify === n.id ? "#E91E8C" : "#718096", cursor: "pointer" }}>
              {n.label}
            </button>
          ))}
        </div>
      </div>

      {/* Transport toggle */}
      <div style={{ background: "#F8F9FB", borderRadius: 16, padding: "16px", border: "1.5px solid #F0F2F5", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: transport ? 14 : 0 }}>
          <div>
            <div style={{ fontSize: 14, fontFamily: F, fontWeight: 800, color: "#2D3748" }}>🚐 Transportation</div>
            <div style={{ fontSize: 11, fontFamily: F, color: "#A0AEC0", marginTop: 2 }}>This child uses pickup/dropoff transport</div>
          </div>
          <div onClick={() => setTransport(t => !t)}
            style={{ width: 50, height: 28, borderRadius: 14, background: transport ? "#F5A623" : "#E2E8F0", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
            <div style={{ width: 22, height: 22, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: transport ? 25 : 3, transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }} />
          </div>
        </div>
        {transport && (
          <>
            <label style={lbl}>Destination</label>
            <input value={destination} onChange={e => setDestination(e.target.value)} placeholder="e.g. Lincoln Elementary"
              style={{ width: "100%", padding: "11px 13px", borderRadius: 11, border: "1.5px solid #E2E8F0", fontSize: 13, fontFamily: F, color: "#2D3748", outline: "none", boxSizing: "border-box" }} />
          </>
        )}
      </div>

      {/* Emergency Contacts */}
      <div style={{ background: "#F8F9FB", borderRadius: 16, padding: "16px", border: "1.5px solid #F0F2F5", marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#2D3748", marginBottom: 14 }}>🚨 Emergency Contacts</div>

        {/* Contact 1 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#E8394D", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Contact 1</div>
          <label style={lbl}>Full Name</label>
          <input style={inp} value={ec1Name} onChange={e => setEc1Name(e.target.value)} placeholder="e.g. Juan Ramirez" />
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Phone Number</label>
              <input style={inp} type="tel" value={ec1Phone} onChange={e => setEc1Phone(e.target.value)} placeholder="(206) 555-0001" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Relationship</label>
              <select style={{ ...inp, cursor: "pointer", WebkitAppearance: "menulist", appearance: "menulist" }} value={ec1Rel} onChange={e => setEc1Rel(e.target.value)} onBlur={e => setEc1Rel(e.target.value)}>
                <option value="">Select...</option>
                {["Father","Mother","Grandparent","Aunt/Uncle","Sibling","Family Friend","Other"].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ borderTop: "1px solid #E2E8F0", marginBottom: 16 }} />

        {/* Contact 2 */}
        <div>
          <div style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#4A6CF7", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Contact 2</div>
          <label style={lbl}>Full Name</label>
          <input style={inp} value={ec2Name} onChange={e => setEc2Name(e.target.value)} placeholder="e.g. Ana Ramirez" />
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Phone Number</label>
              <input style={inp} type="tel" value={ec2Phone} onChange={e => setEc2Phone(e.target.value)} placeholder="(206) 555-0002" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Relationship</label>
              <select style={{ ...inp, cursor: "pointer", WebkitAppearance: "menulist", appearance: "menulist" }} value={ec2Rel} onChange={e => setEc2Rel(e.target.value)} onBlur={e => setEc2Rel(e.target.value)}>
                <option value="">Select...</option>
                {["Father","Mother","Grandparent","Aunt/Uncle","Sibling","Family Friend","Other"].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Save / Cancel */}
      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        <button onClick={onClose} style={{ flex: 1, padding: "13px", borderRadius: 12, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 13, fontFamily: F, fontWeight: 700, cursor: "pointer", color: "#718096" }}>Cancel</button>
        <button onClick={save} style={{ flex: 2, padding: "13px", borderRadius: 12, border: "none", background: "#E8394D", color: "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>Save Changes</button>
      </div>

      {/* Remove child */}
      {!confirmDel
        ? <button onClick={() => setConfirmDel(true)} style={{ width: "100%", padding: "12px", borderRadius: 12, border: "1.5px solid #FEB2B2", background: "#FFF5F5", fontSize: 13, fontFamily: F, fontWeight: 800, color: "#E8394D", cursor: "pointer" }}>
            Remove Child
          </button>
        : <div style={{ background: "#FFF5F5", borderRadius: 14, padding: "14px 16px", border: "1.5px solid #FEB2B2" }}>
            <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#2D3748", marginBottom: 10 }}>Remove {kid.name} from your roster?</div>
            <div style={{ fontSize: 12, fontFamily: F, color: "#718096", marginBottom: 14 }}>Their attendance history will be preserved for DCYF records.</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setConfirmDel(false)} style={{ flex: 1, padding: "11px", borderRadius: 10, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 13, fontFamily: F, fontWeight: 700, cursor: "pointer", color: "#718096" }}>Cancel</button>
              <button onClick={() => { onRemove(kid.id); onClose(); }} style={{ flex: 1, padding: "11px", borderRadius: 10, border: "none", background: "#E8394D", color: "#fff", fontSize: 13, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>Yes, Remove</button>
            </div>
          </div>
      }
    </Sheet>
  );
}

// ── EMERGENCY SHEET ────────────────────────────────────────
function EmergencySheet({ kid, onClose }) {
  const contacts = [
    { name: kid.ec1Name, phone: kid.ec1Phone, rel: kid.ec1Rel, color: "#E8394D", bg: "#FFF5F5", border: "#FEB2B2", label: "Contact 1" },
    { name: kid.ec2Name, phone: kid.ec2Phone, rel: kid.ec2Rel, color: "#4A6CF7", bg: "#EBF4FF", border: "#C3DAFE", label: "Contact 2" },
    { name: kid.parent,  phone: kid.parentPhone, rel: "Parent / Guardian", color: "#2ECC8A", bg: "#F0FFF4", border: "#9AE6B4", label: "Parent" },
  ].filter(c => c.name);

  return (
    <Sheet onClose={onClose}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <div style={{ width: 52, height: 52, borderRadius: 16, background: "#FFF5F5", border: "2px solid #FEB2B2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 24 }}>🚨</span>
        </div>
        <div>
          <div style={{ fontSize: 18, fontFamily: F, fontWeight: 900, color: "#1A202C" }}>Emergency Contacts</div>
          <div style={{ fontSize: 13, fontFamily: F, color: "#718096", marginTop: 2 }}>{shortName(kid.name)}</div>
        </div>
      </div>

      {contacts.length === 0 ? (
        <div style={{ textAlign: "center", padding: "30px 0", background: "#F8F9FB", borderRadius: 16, border: "1.5px solid #E2E8F0" }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
          <div style={{ fontSize: 14, fontFamily: F, fontWeight: 800, color: "#2D3748", marginBottom: 6 }}>No contacts added yet</div>
          <div style={{ fontSize: 12, fontFamily: F, color: "#A0AEC0" }}>Tap Edit on this child's tile to add emergency contacts.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {contacts.map((c, i) => (
            <div key={i} style={{ background: c.bg, borderRadius: 16, border: `1.5px solid ${c.border}`, padding: "16px" }}>
              <div style={{ fontSize: 10, fontFamily: F, fontWeight: 900, color: c.color, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>{c.label}</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 16, fontFamily: F, fontWeight: 900, color: "#1A202C", lineHeight: 1.2 }}>{c.name}</div>
                  <div style={{ fontSize: 12, fontFamily: F, color: "#718096", marginTop: 3, fontWeight: 600 }}>{c.rel}</div>
                  {c.phone && <div style={{ fontSize: 13, fontFamily: F, color: c.color, fontWeight: 800, marginTop: 4 }}>{c.phone}</div>}
                </div>
                {c.phone ? (
                  <a href={`tel:${c.phone.replace(/\D/g, "")}`}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 52, height: 52, borderRadius: "50%", background: c.color, flexShrink: 0, textDecoration: "none", boxShadow: `0 4px 14px ${c.color}44` }}>
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .82h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                    </svg>
                  </a>
                ) : (
                  <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#E2E8F0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#A0AEC0" strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.68A2 2 0 012 .82h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <button onClick={onClose} style={{ width: "100%", padding: "14px", borderRadius: 14, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer", color: "#718096", marginTop: 16 }}>
        Close
      </button>
    </Sheet>
  );
}

// ── SIGN IN/OUT ────────────────────────────────────────────
function SignIn({ kids, onAction, onAdd, onEdit, onRemove }) {
  const tile = SIGNIN_TILE;
  const [editing,   setEditing]   = useState(null);
  const [emergency, setEmergency] = useState(null);
  const [page, setPage] = useState(0);
  const PER_PAGE = 6;
  useEffect(() => { setPage(0); }, [kids]);
  const totalPages = Math.ceil(kids.length / PER_PAGE);
  const sortedKids = [...kids].sort((a, b) => (b.status === "in" ? 1 : 0) - (a.status === "in" ? 1 : 0));
  const pageKids = sortedKids.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  return (
    <div style={{ background: OFF_WHITE, minHeight: "100vh", paddingBottom: 80 }}>
      <ScreenHeader tile={tile} onBack={() => onAction(null, "back")} />
      <div style={{ padding: "14px 14px" }}>

        {/* 2-column tile grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {pageKids.map(kid => {
            const isIn = kid.status === "in";
            return (
              <div key={kid.id} style={{ background: "#fff", borderRadius: 18, border: `2px solid ${isIn ? "#86EFAC" : "#E2E8F0"}`, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column" }}>
                {/* Top button row */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 8px 0" }}>
                  <button onClick={() => setEmergency(kid)}
                    style={{ width: 26, height: 26, borderRadius: 7, border: "1.5px solid #E2E8F0", background: "#F8F9FB", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/></svg>
                  </button>
                  <button onClick={() => setEditing(kid)} style={{ width: 24, height: 24, borderRadius: 7, border: "1.5px solid #E2E8F0", background: "#F8F9FB", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M8.5 1.5L10.5 3.5L3.5 10.5H1.5V8.5L8.5 1.5Z" stroke="#9ba3be" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                </div>
                <div style={{ padding: "6px 8px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flex: 1 }}>
                  <Avatar person={kid} size={78} />
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 15, fontFamily: "'Playfair Display', serif", color: "#2D3748", fontWeight: 700, lineHeight: 1.2 }}>{shortName(kid.name)}</div>
                    <div style={{ fontSize: 11, fontFamily: F, color: isIn ? "#16A34A" : "#A0AEC0", fontWeight: 700, marginTop: 2 }}>
                      {isIn && kid.signInDate && kid.signInDate !== new Date().toDateString()
                        ? <span style={{ color: "#D97706", fontWeight: 700 }}>👋 Still here from yesterday!</span>
                        : isIn ? `✓ ${kid.signInTime}` : "Not arrived"}
                    </div>
                  </div>

                </div>
                <button onClick={() => onAction(kid, "action")} style={{ width: "100%", padding: "13px", border: "none", borderTop: `1.5px solid ${isIn ? "#86EFAC" : "#E2E8F0"}`, background: isIn ? "#F0FFF4" : CRIMSON, color: isIn ? "#16A34A" : "#fff", fontSize: 16, fontFamily: F, fontWeight: 900, cursor: "pointer" }}>
                  {isIn ? "Sign Out" : "Sign In"}
                </button>
              </div>
            );
          })}

          {/* Add child tile — only on last page */}
          {page === totalPages - 1 || kids.length === 0 ? (
            <button onClick={onAdd} style={{ borderRadius: 18, border: "2px dashed #CBD5E0", background: "#fff", minHeight: 150, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer" }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: "#F4F6F9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, border: "1.5px solid #E2E8F0" }}>+</div>
              <span style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#A0AEC0" }}>Add child</span>
            </button>
          ) : null}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 20 }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              style={{ padding: "8px 18px", borderRadius: 10, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 13, fontFamily: F, fontWeight: 800, color: page === 0 ? "#CBD5E0" : "#2D3748", cursor: page === 0 ? "default" : "pointer" }}>
              ← Prev
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button key={i} onClick={() => setPage(i)}
                style={{ width: 32, height: 32, borderRadius: "50%", border: `2px solid ${page === i ? "#E8394D" : "#E2E8F0"}`, background: page === i ? "#E8394D" : "#fff", fontSize: 13, fontFamily: F, fontWeight: 900, color: page === i ? "#fff" : "#718096", cursor: "pointer" }}>
                {i + 1}
              </button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page === totalPages - 1}
              style={{ padding: "8px 18px", borderRadius: 10, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 13, fontFamily: F, fontWeight: 800, color: page === totalPages - 1 ? "#CBD5E0" : "#2D3748", cursor: page === totalPages - 1 ? "default" : "pointer" }}>
              Next →
            </button>
          </div>
        )}
      </div>

      {editing   && <EditChild      kid={editing}   onSave={onEdit} onRemove={onRemove} onClose={() => setEditing(null)} />}
      {emergency && <EmergencySheet kid={emergency}  onClose={() => setEmergency(null)} />}
    </div>
  );
}

// ── STAFF ──────────────────────────────────────────────────
function Staff({ staff, rat, onClock, onAdd, onEdit, onRemove, onBack }) {
  const tile = TILES.find(t => t.id === "staff");
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [editing, setEditing] = useState(null);
  return (
    <div style={{ background: OFF_WHITE, minHeight: "100vh", paddingBottom: 80 }}>
      <ScreenHeader tile={tile} onBack={onBack} />
      <div style={{ padding: "16px 14px" }}>

        {/* 2-column tile grid */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {staff.map(p => {
            const isIn = p.status === "in";
            return (
              <div key={p.id} style={{ background: "#fff", borderRadius: 20, border: `2px solid ${isIn ? "#C3DAFE" : "#F0F2F5"}`, overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, padding: "8px 8px 0" }}>
                  <button onClick={() => setEditing(p)} style={{ padding: "3px 8px", borderRadius: 7, border: "1.5px solid #C3DAFE", background: "#EBF4FF", fontSize: 10, fontFamily: F, fontWeight: 800, color: "#4A6CF7", cursor: "pointer" }}>Edit</button>
                  <button onClick={() => setConfirmRemove(p)} style={{ padding: "3px 8px", borderRadius: 7, border: "1.5px solid #FEB2B2", background: "#FFF5F5", fontSize: 10, fontFamily: F, fontWeight: 800, color: "#E8394D", cursor: "pointer" }}>Remove</button>
                </div>
                <div style={{ padding: "4px 14px 14px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, flex: 1 }}>
                  <Avatar person={p} size={56} />
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 15, fontFamily: F, color: "#2D3748", fontWeight: 800, lineHeight: 1.2 }}>{p.name}</div>
                    <div style={{ fontSize: 11, fontFamily: F, color: "#A0AEC0", fontWeight: 600, marginTop: 3, textTransform: "capitalize" }}>{p.role}{p.driver ? " · 🚐" : ""}</div>
                    <div style={{ fontSize: 11, fontFamily: F, color: isIn ? "#38A169" : "#A0AEC0", fontWeight: 700, marginTop: 4 }}>
                      {isIn ? `● ${p.clockIn}` : "Not clocked in"}
                    </div>
                  </div>
                </div>
                <button onClick={() => onClock(p)} style={{ width: "100%", padding: "14px", border: "none", borderTop: `1.5px solid ${isIn ? "#C3DAFE" : "#F0F2F5"}`, background: isIn ? "#EBF4FF" : "#4A6CF7", color: isIn ? "#4A6CF7" : "#fff", fontSize: 15, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>
                  {isIn ? "Clock Out" : "Clock In"}
                </button>
              </div>
            );
          })}

          {/* Confirm Remove Sheet */}
          {confirmRemove && (
            <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "flex-end" }}>
              <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)" }} onClick={() => setConfirmRemove(null)} />
              <div style={{ position: "relative", background: "#fff", borderRadius: "22px 22px 0 0", padding: "24px 22px 36px", width: "100%", boxShadow: "0 -4px 30px rgba(0,0,0,0.12)" }}>
                <div style={{ fontSize: 18, fontFamily: F, fontWeight: 900, color: "#2D3748", marginBottom: 8 }}>Remove {confirmRemove.name}?</div>
                <div style={{ fontSize: 14, fontFamily: F, color: "#718096", marginBottom: 24 }}>This will permanently remove this staff member. This cannot be undone.</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => setConfirmRemove(null)} style={{ flex: 1, padding: "14px", borderRadius: 12, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, color: "#718096", cursor: "pointer" }}>Cancel</button>
                  <button onClick={() => { onRemove(confirmRemove.id); setConfirmRemove(null); }} style={{ flex: 1, padding: "14px", borderRadius: 12, border: "none", background: "#E8394D", color: "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>Yes, Remove</button>
                </div>
              </div>
            </div>
          )}

          {editing && <EditStaff person={editing} onSave={p => onEdit(p)} onClose={() => setEditing(null)} />}

          {/* Add staff tile */}
          <button onClick={onAdd} style={{ borderRadius: 20, border: "2px dashed #E2E8F0", background: "none", minHeight: 160, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer" }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: "#F4F6F9", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>+</div>
            <span style={{ fontSize: 12, fontFamily: F, fontWeight: 800, color: "#A0AEC0" }}>Add staff</span>
          </button>
        </div>
      </div>
    </div>
  );
}


function EditStaff({ person, onSave, onClose }) {
  const [name,    setName]    = useState(person.name  || "");
  const [role,    setRole]    = useState(person.role  || "assistant");
  const [phone,   setPhone]   = useState(person.phone || "");
  const [pin,     setPin]     = useState("");
  const [pinConf, setPinConf] = useState("");
  const [photo,   setPhoto]   = useState(person.photo || null);
  const [pinErr,  setPinErr]  = useState("");
  const fileRef = useRef(null);
  const inp = { width:"100%", padding:"11px 13px", borderRadius:11, border:"1.5px solid #E2E8F0", fontSize:13, fontFamily:F, color:"#2D3748", outline:"none", boxSizing:"border-box", marginBottom:12 };
  const lbl = { fontSize:11, fontFamily:F, fontWeight:800, color:"#A0AEC0", textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6, display:"block" };
  const handlePhoto = e => { const file = e.target.files[0]; if (!file) return; const reader = new FileReader(); reader.onload = ev => setPhoto(ev.target.result); reader.readAsDataURL(file); };
  const save = () => {
    if (!name.trim()) return;
    if (pin && pin.length < 4) { setPinErr("PIN must be at least 4 digits"); return; }
    if (pin && pin !== pinConf) { setPinErr("PINs do not match"); return; }
    const updated = { ...person, name: name.trim(), role, phone: phone.trim(), photo };
    if (pin) updated.pin = pin;
    onSave(updated); onClose();
  };
  return (
    <Sheet onClose={onClose}>
      <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:20 }}>
        <div style={{ position:"relative", flexShrink:0 }}>
          <Avatar person={{ ...person, photo, name }} size={72} />
          <button onClick={() => fileRef.current.click()} style={{ position:"absolute", bottom:-4, right:-4, width:26, height:26, borderRadius:"50%", background:"#4A6CF7", border:"2.5px solid #fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
            <span style={{ fontSize:13, color:"#fff", lineHeight:1 }}>📷</span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} style={{ display:"none" }} />
        </div>
        <div>
          <div style={{ fontSize:16, fontFamily:F, fontWeight:900, color:"#2D3748" }}>Edit Staff Profile</div>
          <button onClick={() => fileRef.current.click()} style={{ fontSize:12, fontFamily:F, fontWeight:800, color:"#4A6CF7", background:"none", border:"none", cursor:"pointer", padding:0, marginTop:4 }}>{photo ? "Change photo" : "Add profile photo"}</button>
        </div>
      </div>
      <div style={{ background:"#F8F9FB", borderRadius:16, padding:"16px", border:"1.5px solid #F0F2F5", marginBottom:14 }}>
        <div style={{ fontSize:13, fontFamily:F, fontWeight:800, color:"#2D3748", marginBottom:14 }}>👤 Staff Info</div>
        <label style={lbl}>Full Name *</label>
        <input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Maria Lopez" />
        <label style={lbl}>Phone Number</label>
        <input style={inp} type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="e.g. (206) 555-1234" />
        <label style={lbl}>Role</label>
        <div style={{ display:"flex", gap:8, marginBottom:12 }}>
          {["assistant","lead","driver"].map(r => (
            <button key={r} onClick={() => setRole(r)} style={{ flex:1, padding:"11px", borderRadius:12, border:`2px solid ${role===r?"#4A6CF7":"#E2E8F0"}`, background:role===r?"#EBF4FF":"#fff", fontSize:12, fontFamily:F, fontWeight:800, color:role===r?"#4A6CF7":"#718096", cursor:"pointer", textTransform:"capitalize" }}>{r}</button>
          ))}
        </div>
      </div>
      <div style={{ background:"#F8F9FB", borderRadius:16, padding:"16px", border:"1.5px solid #F0F2F5", marginBottom:14 }}>
        <div style={{ fontSize:13, fontFamily:F, fontWeight:800, color:"#2D3748", marginBottom:4 }}>🔒 Change PIN</div>
        <div style={{ fontSize:11, fontFamily:F, color:"#A0AEC0", marginBottom:14 }}>Leave blank to keep current PIN</div>
        <label style={lbl}>New PIN</label>
        <input style={inp} type="password" inputMode="numeric" maxLength={6} value={pin} onChange={e => { setPin(e.target.value); setPinErr(""); }} placeholder="4-6 digits" />
        <label style={lbl}>Confirm PIN</label>
        <input style={{ ...inp, marginBottom:pinErr?6:12 }} type="password" inputMode="numeric" maxLength={6} value={pinConf} onChange={e => { setPinConf(e.target.value); setPinErr(""); }} placeholder="Re-enter PIN" />
        {pinErr && <div style={{ fontSize:12, fontFamily:F, color:"#E8394D", marginBottom:10, fontWeight:700 }}>{pinErr}</div>}
      </div>
      <button onClick={save} style={{ width:"100%", padding:"15px", borderRadius:14, border:"none", background:"#4A6CF7", color:"#fff", fontSize:15, fontFamily:F, fontWeight:800, cursor:"pointer", marginBottom:10 }}>Save Changes</button>
      <button onClick={onClose} style={{ width:"100%", padding:"13px", borderRadius:14, border:"1.5px solid #E2E8F0", background:"#fff", fontSize:14, fontFamily:F, fontWeight:800, color:"#718096", cursor:"pointer" }}>Cancel</button>
    </Sheet>
  );
}

// ── TRANSPORT ──────────────────────────────────────────────
function Transport({ kids, onAction, onBack, user }) {
  const tile = TILES.find(t => t.id === "transport");
  const tKids = kids.filter(k => k.transport);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  useEffect(() => {
    if (user?.id) loadTransportLog(user.id).then(setLog);
  }, [user, kids]);
  const statusMap = {
    null:          { label: "Awaiting",    color: "#A0AEC0", bg: "#F8F9FB", border: "#F0F2F5", btn: "Pick Up",  btnBg: "#F5A623", next: "transport_pickup",  sms: null  },
    "picked_up":   { label: "In Transit",  color: "#F5A623", bg: "#FFFBF0", border: "#FFE0A0", btn: "Drop Off", btnBg: "#4A6CF7", next: "transport_dropoff", sms: null       },
    "dropped_off": { label: "Dropped Off", color: "#4A6CF7", bg: "#EBF4FF", border: "#C3DAFE", btn: "Returned", btnBg: "#2ECC8A", next: "transport_return",  sms: null  },
    "returned":    { label: "Complete ✓",  color: "#38A169", bg: "#F0FFF4", border: "#9AE6B4", btn: null,       btnBg: null,      next: null,                sms: null                                        },
  };
  return (
    <div style={{ background: OFF_WHITE, minHeight: "100vh", paddingBottom: 80 }}>
      <ScreenHeader tile={tile} onBack={onBack} />
      <div style={{ padding: "16px 14px" }}>
        <div style={{ fontSize: 12, fontFamily: F, color: "#A0AEC0", fontWeight: 700, marginBottom: 10 }}>
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
        </div>
        {/* SMS info banner */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, padding: "10px 14px", background: "#F0FFF4", borderRadius: 12, border: "1px solid #9AE6B4" }}>
          <span style={{ fontSize: 18 }}>📱</span>
          <div style={{ fontSize: 12, fontFamily: F, fontWeight: 700, color: "#16A34A", lineHeight: 1.4 }}>
            Tap Pick Up, Drop Off, or Returned to record each transport step.
          </div>
        </div>
        {/* Reset button — shown when all kids are complete */}
        {tKids.length > 0 && tKids.every(k => k.transportStatus === "returned") && (
          <div style={{ background: "#FFF5F5", borderRadius: 14, border: "1.5px solid #FEB2B2", padding: "14px 16px", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#E8394D" }}>All children returned!</div>
              <div style={{ fontSize: 11, fontFamily: F, color: "#718096", marginTop: 2 }}>Reset transport for a new trip today.</div>
            </div>
            <button onClick={() => tKids.forEach(k => onAction(k, "transport_reset"))}
              style={{ padding: "10px 16px", borderRadius: 10, border: "none", background: "#E8394D", color: "#fff", fontSize: 13, fontFamily: F, fontWeight: 800, cursor: "pointer", flexShrink: 0 }}>
              Reset
            </button>
          </div>
        )}
        {tKids.length === 0
          ? <div style={{ textAlign: "center", padding: "50px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}><div style={{ fontSize: 40 }}>🚐</div><div style={{ fontSize: 15, fontFamily: F, fontWeight: 700, color: NAVY }}>All clear!</div><div style={{ fontSize: 13, fontFamily: F, color: "#94A3B8", fontWeight: 500 }}>No children enrolled in transport yet.</div></div>
          : <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {tKids.map(kid => {
                const st = statusMap[kid.transportStatus ?? null];
                return (
                  <div key={kid.id} style={{ background: "#fff", borderRadius: 20, border: `2px solid ${st.border}`, overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", display: "flex", flexDirection: "column" }}>
                    <div style={{ padding: "18px 14px 14px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10, flex: 1 }}>
                      <Avatar person={kid} size={60} />
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 15, fontFamily: "'Playfair Display', serif", color: "#2D3748", fontWeight: 700, lineHeight: 1.2 }}>{shortName(kid.name)}</div>
                        <div style={{ fontSize: 11, fontFamily: F, color: "#A0AEC0", marginTop: 3 }}>🏫 {kid.destination}</div>
                        <div style={{ display: "inline-block", marginTop: 6, padding: "3px 10px", borderRadius: 100, background: st.bg, fontSize: 11, fontFamily: F, fontWeight: 800, color: st.color }}>{st.label}</div>
                      </div>
                    </div>
                    {st.btn
                      ? <div>
                          <button onClick={async () => {
                            onAction(kid, st.next);
                            if (false) { // SMS handled by doTransport
                            }
                          }} style={{ width: "100%", padding: "14px", border: "none", borderTop: `1.5px solid ${st.border}`, background: st.btnBg, color: "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>
                            {st.btn}
                          </button>
                          {st.sms && <div style={{ padding: "6px 10px", background: "#F8F9FB", borderTop: `1px solid ${st.border}`, fontSize: 10, fontFamily: F, fontWeight: 700, color: "#718096", textAlign: "center" }}>{st.sms}</div>}
                        </div>
                      : <div style={{ padding: "14px", borderTop: `1.5px solid ${st.border}`, textAlign: "center", fontSize: 13, fontFamily: F, fontWeight: 800, color: "#38A169", background: "#F0FFF4" }}>✓ Done</div>
                    }
                  </div>
                );
              })}
            </div>
        }
      </div>
    </div>
  );
}

// ── MESSAGES ───────────────────────────────────────────────
function Messages({ kids, onBack, profile, ping }) {
  const tile = TILES.find(t => t.id === "messages");
  const [selected, setSelected] = useState(null);
  const [msg, setMsg] = useState("");
  const [photo, setPhoto] = useState(null);
  const [sent, setSent] = useState([]);
  const [sending, setSending] = useState(false);
  const cameraRef = useRef(null);

  const QUICK = [
    "😴 Nap time!",
    "🍽️ Time to eat!",
    "🎨 Arts & crafts!",
    "🌞 Outside play!",
    "📚 Story time!",
    "😊 Having a great day!",
    "🎵 Music time!",
    "🧼 Hand washing time!",
  ];

  const [broadcastMsg, setBroadcastMsg] = useState("");
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);

  const sendBroadcast = async () => {
    if (!broadcastMsg.trim()) return;
    const targets = kids.filter(k => k.parentPhone);
    if (!targets.length) { ping("⚠️ No children have parent phone numbers on file."); return; }
    setBroadcasting(true);
    let successCount = 0;
    for (const kid of targets) {
      try {
        const message = `📢 ${broadcastMsg}` + (profile?.daycareN ? `\n— ${profile.daycareN}` : '');
        const res = await fetch('/api/send-sms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: formatPhone(kid.parentPhone), message }),
        });
        if (res.ok) successCount++;
      } catch (e) { console.error("Broadcast SMS error:", e); }
    }
    setSent(p => [{ id: Date.now(), kidName: "📨 All Parents", parent: `${successCount} of ${targets.length} parents`, text: broadcastMsg, photo: null, time: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }), isBroadcast: true }, ...p]);
    setBroadcastMsg(""); setShowBroadcast(false);
    setBroadcasting(false);
  };

  const [photoFile, setPhotoFile] = useState(null);
  const photoFileRef = useRef(null);
  const handlePhoto = e => {
    const file = e.target.files[0];
    if (!file) return;
    photoFileRef.current = file;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = ev => setPhoto(ev.target.result);
    reader.readAsDataURL(file);
  };

  const sendMsg = async () => {
    if ((!msg.trim() && !photo) || !selected) return;
    if (!selected.parentPhone) {
      ping(`⚠️ No phone number on file for ${selected.name}. Please add a parent phone number in the child profile.`);
      return;
    }
    setSending(true);
    try {
      if (!SMS_ENABLED) { setSending(false); return; }

      // Upload photo to Supabase if present, send as link
      let finalMessage = msg;
      let uploadedPhotoUrl = null;
      if (photo) {
        const { supabase } = await import("./supabase");
        const { data: { user } } = await supabase.auth.getUser();
        const userId = user?.id || 'unknown';
        const mime = photo.split(';')[0].split(':')[1] || 'image/jpeg';
        const base64data = photo.split(',')[1];
        const byteArr = Uint8Array.from(atob(base64data), c => c.charCodeAt(0));
        const ext = mime.includes('png') ? 'png' : 'jpg';
        const fileName = `${userId}/messages/${Date.now()}.${ext}`;
        const { data: upData, error: upError } = await supabase.storage.from('child-photos').upload(fileName, byteArr, { contentType: mime, upsert: true });
        if (upError) {
          console.error('Photo upload error:', JSON.stringify(upError));
        } else {
          const { data: urlData } = supabase.storage.from('child-photos').getPublicUrl(fileName);
          uploadedPhotoUrl = urlData?.publicUrl || null;
          if (!uploadedPhotoUrl) finalMessage = msg || 'Photo update from your daycare provider';
        }
      }

      const res = await fetch('/api/send-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: formatPhone(selected.parentPhone), message: `📢 Update: ${selected.name.split(' ')[0]}\n${finalMessage || 'Photo update from your daycare provider'}`, photoUrl: uploadedPhotoUrl || undefined, daycareN: profile?.daycareN || '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send');
      setSent(p => [{
        id: Date.now(), kidId: selected.id, kidName: selected.name,
        parent: selected.parent, text: msg, photo,
        time: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
      }, ...p]);
      setMsg(""); setPhoto(null); setPhotoFile(null);
    } catch (e) {
      ping(`⚠️ Message failed: ${e.message}`);
    }
    setSending(false);
  };

  return (
    <div style={{ background: OFF_WHITE, minHeight: "100vh", paddingBottom: 80 }}>
      <ScreenHeader tile={tile} onBack={onBack} />
      <div style={{ padding: "16px 14px" }}>

        {/* Broadcast modal */}
        {showBroadcast && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 999, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
            <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", padding: "28px 20px 40px", width: "100%", maxWidth: 480 }}>
              <div style={{ fontSize: 18, fontFamily: F, fontWeight: 900, color: "#1A202C", marginBottom: 4 }}>📨 Message All Parents</div>
              <div style={{ fontSize: 13, fontFamily: F, color: "#718096", marginBottom: 16 }}>Sends individually to {kids.filter(k => k.parentPhone).length} parents — no one sees each other's info.</div>
              <textarea
                value={broadcastMsg}
                onChange={e => setBroadcastMsg(e.target.value)}
                placeholder="e.g. Reminder — we will be closed Monday May 26 for Memorial Day 🎉"
                style={{ width: "100%", minHeight: 100, borderRadius: 14, border: "1.5px solid #E2E8F0", padding: "13px", fontSize: 14, fontFamily: F, color: "#2D3748", resize: "none", outline: "none", boxSizing: "border-box", lineHeight: 1.6, marginBottom: 14 }}
              />
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setShowBroadcast(false)} style={{ flex: 1, padding: "13px", borderRadius: 12, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, color: "#718096", cursor: "pointer" }}>Cancel</button>
                <button onClick={sendBroadcast} disabled={!broadcastMsg.trim() || broadcasting}
                  style={{ flex: 2, padding: "13px", borderRadius: 12, border: "none", background: !broadcastMsg.trim() || broadcasting ? "#E2E8F0" : "#E91E8C", color: !broadcastMsg.trim() || broadcasting ? "#A0AEC0" : "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>
                  {broadcasting ? "Sending..." : `Send to All ${kids.filter(k => k.parentPhone).length} Parents`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Select child */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em" }}>Send update to parent</div>
          <button onClick={() => setShowBroadcast(true)} style={{ padding: "7px 14px", borderRadius: 100, border: "1.5px solid #E91E8C", background: "#FEF0F7", fontSize: 12, fontFamily: F, fontWeight: 800, color: "#E91E8C", cursor: "pointer" }}>📨 Message All Parents</button>
        </div>
        <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4, marginBottom: 16 }}>
          {kids.map(kid => (
            <button key={kid.id} onClick={() => setSelected(kid)}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", flexShrink: 0, padding: "4px 8px", borderRadius: 14, outline: selected?.id === kid.id ? `2.5px solid #E91E8C` : "2.5px solid transparent" }}>
              <Avatar person={kid} size={48} />
              <span style={{ fontSize: 10, fontFamily: F, fontWeight: 800, color: selected?.id === kid.id ? "#E91E8C" : "#718096", maxWidth: 56, textAlign: "center", lineHeight: 1.2 }}>{kid.name.split(" ")[0]}</span>
            </button>
          ))}
        </div>

        {selected && (
          <div style={{ background: "#FEF0F7", borderRadius: 14, padding: "10px 14px", marginBottom: 14, border: "1px solid #F9A8D4" }}>
            <div style={{ fontSize: 12, fontFamily: F, fontWeight: 700, color: "#E91E8C" }}>
              → Sending to: <strong>{selected.parent}</strong> ({selected.name})
            </div>
          </div>
        )}

        {/* Quick reply chips */}
        <div style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Quick Messages</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          {QUICK.map(q => (
            <button key={q} onClick={() => setMsg(q)}
              style={{ padding: "8px 14px", borderRadius: 100, border: `1.5px solid ${msg === q ? "#E91E8C" : "#F0F2F5"}`, background: msg === q ? "#FEF0F7" : "#F8F9FB", fontSize: 13, fontFamily: F, fontWeight: 800, color: msg === q ? "#E91E8C" : "#4A5568", cursor: "pointer" }}>
              {q}
            </button>
          ))}
        </div>

        {/* Custom message box */}
        <div style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Or Write Your Own</div>
        <textarea
          value={msg}
          onChange={e => setMsg(e.target.value)}
          placeholder={selected ? `Write a note about ${selected.name.split(" ")[0]}...` : "Select a child first..."}
          disabled={!selected}
          style={{ width: "100%", minHeight: 80, borderRadius: 14, border: "1.5px solid #F0F2F5", padding: "13px", fontSize: 14, fontFamily: F, color: "#2D3748", resize: "none", outline: "none", boxSizing: "border-box", lineHeight: 1.6, marginBottom: 10, background: selected ? "#fff" : "#F8F9FB" }}
        />

        {/* Photo preview */}
        {photo && (
          <div style={{ position: "relative", marginBottom: 12, display: "inline-block" }}>
            <img src={photo} alt="attached" style={{ width: 100, height: 100, borderRadius: 12, objectFit: "cover", border: "2px solid #F9A8D4" }} />
            <button onClick={() => setPhoto(null)}
              style={{ position: "absolute", top: -8, right: -8, width: 22, height: 22, borderRadius: "50%", background: "#E8394D", border: "2px solid #fff", color: "#fff", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontWeight: 900 }}>×</button>
          </div>
        )}

        {/* Action row */}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} style={{ display: "none" }} />
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button onClick={() => cameraRef.current.click()} disabled={!selected}
            style={{ padding: "12px 16px", borderRadius: 12, border: "1.5px dashed #F9A8D4", background: "#FEF0F7", fontSize: 13, fontFamily: F, fontWeight: 800, color: "#E91E8C", cursor: selected ? "pointer" : "not-allowed", opacity: selected ? 1 : 0.5 }}>
            📷 Photo
          </button>
          <button onClick={sendMsg} disabled={(!msg.trim() && !photo) || !selected || sending}
            style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: (!msg.trim() && !photo) || !selected ? "#E2E8F0" : "#E91E8C", color: (!msg.trim() && !photo) || !selected ? "#A0AEC0" : "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>
            {sending ? "Sending..." : "Send Update"}
          </button>
        </div>

        {/* Sent messages */}
        {sent.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Sent today</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sent.map(s => (
                <div key={s.id} style={{ background: "#F8F9FB", borderRadius: 14, padding: "12px 14px", border: "1.5px solid #F0F2F5" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontFamily: F, fontWeight: 800, color: s.isBroadcast ? "#7C3AED" : "#E91E8C" }}>{s.kidName}</span>
                    <span style={{ fontSize: 11, fontFamily: F, color: "#A0AEC0" }}>{s.time}</span>
                  </div>
                  {s.photo && <img src={s.photo} alt="" style={{ width: "100%", borderRadius: 10, marginBottom: 8, maxHeight: 160, objectFit: "cover" }} />}
                  {s.text && <div style={{ fontSize: 13, fontFamily: F, color: "#4A5568", lineHeight: 1.5 }}>{s.text}</div>}
                  <div style={{ fontSize: 11, fontFamily: F, color: "#A0AEC0", marginTop: 6 }}>{s.isBroadcast ? `📨 Sent to ${s.parent}` : `→ ${s.parent} · ✓ Delivered`}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── REPORTS ────────────────────────────────────────────────
function Reports({ kids, user, profile, onBack }) {
  const tile = TILES.find(t => t.id === "reports");
  const now = new Date();
  const [selMonth, setSelMonth] = useState(now.getMonth());
  const [selYear,  setSelYear]  = useState(now.getFullYear());
  const [rangeMode, setRangeMode] = useState("monthly");
  const [startDate, setStartDate] = useState("");
  const [endDate,   setEndDate]   = useState("");
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isBillingMode, setIsBillingMode] = useState(false);

  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  useEffect(() => {
    if (!user?.id) return;
    if (rangeMode === "custom" && (!startDate || !endDate)) return;
    setLoading(true);
    const start = rangeMode === "custom" ? new Date(startDate).toISOString() : new Date(selYear, selMonth, 1).toISOString();
    const end   = rangeMode === "custom" ? new Date(endDate + "T23:59:59").toISOString() : new Date(selYear, selMonth + 1, 0, 23, 59, 59).toISOString();
    import("./supabase").then(({ supabase }) => {
      supabase.from("attendance")
        .select("*, children(name, auth_type)")
        .eq("provider_id", user.id)
        .gte("sign_in_time", start)
        .lte("sign_in_time", end)
        .order("sign_in_time", { ascending: true })
        .then(({ data }) => {
          setAttendance(data || []);
          setLoading(false);
        });
    });
  }, [user, selMonth, selYear, rangeMode, startDate, endDate]);

  // Calculate days attended per child
  const daysPerChild = {};
  attendance.forEach(r => {
    const name = r.children?.name || "Unknown";
    if (!daysPerChild[name]) daysPerChild[name] = new Set();
    daysPerChild[name].add(new Date(r.sign_in_time).toDateString());
  });
  // Billing Friday Mode — groups attendance by SSPS billing weeks (Mon-Fri)
  const getBillingWeeks = () => {
    const weeks = {};
    attendance.forEach(r => {
      const d = new Date(r.sign_in_time);
      const day = d.getDay(); // 0=Sun, 1=Mon ... 5=Fri, 6=Sat
      if (day === 0 || day === 6) return; // skip weekends
      // Find the Friday of this billing week
      const friday = new Date(d);
      friday.setDate(d.getDate() + (5 - day));
      const weekKey = friday.toDateString();
      if (!weeks[weekKey]) weeks[weekKey] = [];
      weeks[weekKey].push(r);
    });
    return weeks;
  };
  const billingWeeks = isBillingMode ? getBillingWeeks() : null;

  const wcccCount    = kids.filter(k => k.auth === "WCCC").length;
  const privateCount = kids.filter(k => k.auth !== "WCCC").length;
  const reportId     = `RPT-${selYear}-${String(selMonth + 1).padStart(2, "0")}-001`;
  const exportDate   = now.toLocaleDateString("en-US");
  const retainUntil  = new Date(selYear + 5, selMonth + 1, 0).toLocaleDateString("en-US", { month: "2-digit", year: "numeric" });

  const exportCSV = () => {
    const rows = [
      ["TOTO Electronic Attendance System"],
      ["Daily Attendance Detail Report"],
      [`Report Period: ${MONTHS[selMonth]} 1, ${selYear} – ${MONTHS[selMonth]} ${new Date(selYear, selMonth + 1, 0).getDate()}, ${selYear}`],
      [`Generated: ${exportDate} | System: Toto v1.0`],
      [""],
      ["Provider Name", profile?.providerN || "", "SSPS Number", profile?.sspsN || "", "License #", profile?.licenseN || ""],
      ["Address", profile?.address ? `${profile.address}, ${profile.city}, WA ${profile.zip}` : ""],
      [""],
      ["Enrolled Children"],
      ["Child Name", "Payment Type", "Days Attended"],
      ...kids.map(k => [k.name, k.auth, daysPerChild[k.name]?.size || 0]),
      [""],
      ["Daily Attendance Transactions"],
      ["Date", "Day", "Child Name", "Sign In", "Sign Out", "Method", "Transaction ID"],
      ...attendance.map((r, i) => {
        const d = new Date(r.sign_in_time);
        return [
          d.toLocaleDateString("en-US"),
          d.toLocaleDateString("en-US", { weekday: "short" }),
          r.children?.name || "",
          d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
          r.sign_out_time ? new Date(r.sign_out_time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "Still in care",
          "Electronic Signature",
          `TXN-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,"0")}${String(d.getDate()).padStart(2,"0")}-${String(i+1).padStart(3,"0")}`
        ];
      }),
      [""],
      ["Monthly Summary"],
      ["Period", "Total Transactions", "Total Children", "WCCC Children", "Private Pay", "Retention Period"],
      [`${MONTHS[selMonth]} ${selYear}`, attendance.length, kids.length, wcccCount, privateCount, `5 Years (Until ${retainUntil})`],
      [""],
      [`Report ID: ${reportId} | Exported: ${exportDate}`],
      ["This report was electronically generated by Toto — Electronic Attendance System (totocare.app)"],
      ["Records are securely stored and retained for 5 years per WAC 110-15-0126."],
    ];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Toto_Attendance_${MONTHS[selMonth]}_${selYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = async () => {
    const { jsPDF } = await import("jspdf");
    const { default: autoTable } = await import("jspdf-autotable");
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const navy = [26, 54, 93]; const crimson = [185, 28, 28]; const lightGray = [247, 250, 252];
    const dark = [30, 30, 30];
    const gray = [120, 130, 145];

    // Header
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, 210, 36, "F");
    doc.setTextColor(...navy);
    // Logo loaded inline
    const logoData = await fetch("https://app.totocare.app/logo.png").then(r=>r.blob()).then(b=>new Promise(res=>{const fr=new FileReader();fr.onload=()=>res(fr.result);fr.readAsDataURL(b)})); doc.addImage(logoData,"PNG",10,6,30,20);
    doc.setFontSize(10); doc.setFont("helvetica","normal"); doc.text("Electronic Attendance System",46,14);
    doc.setFontSize(13); doc.setFont("helvetica","bold"); doc.text("Daily Attendance Detail Report",46,22);
    doc.setDrawColor(...navy); doc.setLineWidth(0.5); doc.line(0,36,210,36);

    // Report period
    doc.setTextColor(...gray);
    doc.setFontSize(9); doc.setFont("helvetica", "normal");
    doc.text(`Report Period: ${MONTHS[selMonth]} 1, ${selYear} – ${MONTHS[selMonth]} ${new Date(selYear, selMonth + 1, 0).getDate()}, ${selYear}`, 14, 43);
    doc.text(`Generated: ${exportDate} | System: Toto v1.0`, 14, 48);

    // Provider info
    doc.setFillColor(248, 249, 251);
    doc.rect(14, 52, 182, 28, "F");
    doc.setTextColor(...dark);
    doc.setFontSize(9); doc.setFont("helvetica", "bold");
    doc.text(`Provider: ${profile?.providerN || "—"}`, 18, 59);
    doc.text(`SSPS #: ${profile?.sspsN || "—"}`, 18, 65);
    doc.text(`License: ${profile?.licenseN || "—"}`, 18, 71);
    doc.text(`Daycare: ${profile?.daycareN || "—"}`, 105, 59);
    doc.text(`Address: ${profile?.address ? profile.address + ", " + profile.city + ", WA " + profile.zip : "—"}`, 105, 65);
    doc.text(`Payment Types: WCCC / Private Pay`, 105, 71);

    // Enrolled children table
    doc.setTextColor(...dark);
    doc.text("Enrolled Children", 14, 88);
    autoTable(doc, {
      startY: 91,
      head: [["Child Name", "Payment Type", "Days Attended"]],
      body: kids.map(k => [k.name, k.auth, daysPerChild[k.name]?.size || 0]),
      headStyles: { fillColor: lightGray, textColor: dark, fontStyle: "bold", fontSize: 9 },
      bodyStyles: { fontSize: 9, cellPadding: 1.8, textColor: dark },
      columnStyles: { 2: { halign: "center", fontStyle: "bold" } },
      margin: { left: 14, right: 14 },
    });

    // Daily transactions table
    const y1 = doc.lastAutoTable.finalY + 5;
    doc.text("Daily Attendance Transactions", 14, y1);
    autoTable(doc, {
      startY: y1 + 3,
      head: [["Date", "Child Name", "Sign In", "Sign Out", "Duration", "Signature Status", "Transaction ID"]],
      body: attendance.filter(r => {
        if (!r.sign_out_time) return false;
        const ms = new Date(r.sign_out_time) - new Date(r.sign_in_time); return ms > 60000;
      }).map((r, i) => {
        const d = new Date(r.sign_in_time);
        const txnId = `TXN-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,"0")}${String(d.getDate()).padStart(2,"0")}-${String(i+1).padStart(3,"0")}`;
        const signOut = r.sign_out_time ? new Date(r.sign_out_time) : null;
        const durMs = signOut ? signOut - d : null;
        const durHrs = durMs ? Math.floor(durMs / 3600000) : 0;
        const durMins = durMs ? Math.floor((durMs % 3600000) / 60000) : 0;
        const duration = durMs ? `${durHrs}h ${durMins}m` : "In care";
        return [
          d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" }) + " " + d.toLocaleDateString("en-US", { weekday: "short" }),
          r.children?.name || "—",
          d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
          signOut ? signOut.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "In care",
          duration,
          "[Verified Digital Signature]",
          txnId
        ];
      }),
      headStyles: { fillColor: lightGray, textColor: dark, fontStyle: "bold", fontSize: 7 },
      bodyStyles: { fontSize: 7, valign: "middle", cellPadding: 1.5, textColor: dark },
      columnStyles: {
        0: { cellWidth: 22 },  // Date
        1: { cellWidth: 36 },  // Child Name
        2: { cellWidth: 18 },  // Sign In
        3: { cellWidth: 18 },  // Sign Out
        4: { cellWidth: 18 },  // Duration
        5: { cellWidth: 44, halign: "center", fontSize: 6.5 },  // Signature Status
        6: { cellWidth: 32 },  // Transaction ID
      },
      margin: { left: 14, right: 14 },
    });

    // Monthly summary
    const y2 = doc.lastAutoTable.finalY + 4;
    doc.text(`Monthly Summary — ${MONTHS[selMonth]} ${selYear}`, 14, y2);
    autoTable(doc, {
      startY: y2 + 3,
      head: [["Period", "Total Transactions", "Total Hours", "WCCC Children", "Private Pay", "Retention Period"]],
      body: [[`${MONTHS[selMonth]} ${selYear}`, attendance.length, (() => { const ms = attendance.filter(r => r.sign_out_time).reduce((acc, r) => acc + (new Date(r.sign_out_time) - new Date(r.sign_in_time)), 0); const h = Math.floor(ms/3600000); const m = Math.floor((ms%3600000)/60000); return `${h}h ${m}m`; })(), wcccCount, privateCount, `5 Years (Until ${retainUntil})`]],
      headStyles: { fillColor: lightGray, textColor: dark, fontStyle: "bold", fontSize: 8 },
      bodyStyles: { fontSize: 7, valign: "middle", cellPadding: 1.5, textColor: dark },
      margin: { left: 14, right: 14 },
    });

    // Footer
    const y3 = doc.lastAutoTable.finalY + 5;
    doc.setFillColor(248, 249, 251);
    doc.rect(14, y3, 182, 16, "F");
    doc.setTextColor(...gray);
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    doc.text("This report was electronically generated by Toto — Electronic Attendance System (totocare.app)", 18, y3 + 6);
    doc.text(`Records retained for 5 years per WAC 110-15-0126 | SSPS #: ${profile?.sspsN || "—"} | License: ${profile?.licenseN || "—"}`, 18, y3 + 11);
    doc.setTextColor(...dark);
    doc.text(`Report ID: ${reportId} | Exported: ${exportDate}`, 18, y3 + 16);

    // Certification Statement
    const y4 = y3 + 24;
    doc.setFillColor(255, 245, 245);
    doc.rect(14, y4, 182, 20, "F");
    doc.setDrawColor(232, 57, 77);
    doc.rect(14, y4, 182, 20, "S");
    doc.setTextColor(232, 57, 77);
    doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text("CERTIFICATION STATEMENT", 18, y4 + 6);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...dark);
    doc.text("I certify under penalty of perjury under the laws of the State of Washington that this attendance record is true and accurate.", 18, y4 + 11);
    doc.text("Provider Signature: _______________________________    Date: _______________", 18, y4 + 17);

    doc.save(`Toto_Attendance_${MONTHS[selMonth]}_${selYear}.pdf`);
  };

  const sxCard = { background: "#fff", borderRadius: 18, border: "1.5px solid #E2E8F0", overflow: "hidden", marginBottom: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" };
  const sxHdr  = { background: "#2ECC8A", padding: "12px 16px", fontSize: 13, fontFamily: F, fontWeight: 900, color: "#fff", letterSpacing: "0.02em" };
  const sxRow  = (i, len) => ({ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: i < len - 1 ? "1px solid #F0F2F5" : "none" });

  return (
    <div style={{ background: OFF_WHITE, minHeight: "100vh", paddingBottom: 80 }}>
      <ScreenHeader tile={tile} onBack={onBack} />
      <div style={{ padding: "16px 14px" }}>

        {/* Provider Info Card */}
        <div style={{ ...sxCard }}>
          <div style={{ ...sxHdr }}>📋 Provider Information</div>
          <div style={{ padding: "14px 16px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
              {[
                { label: "Provider Name", val: profile?.providerN || "—" },
                { label: "Daycare Name",  val: profile?.daycareN  || "—" },
                { label: "SSPS Number",   val: profile?.sspsN     || "—" },
                { label: "License #",     val: profile?.licenseN  || "—" },
                { label: "Address",       val: profile?.address ? `${profile.address}, ${profile.city}, WA ${profile.zip}` : "—" },
                { label: "Payment Types", val: "WCCC / Private Pay" },
              ].map(item => (
                <div key={item.label}>
                  <div style={{ fontSize: 10, fontFamily: F, fontWeight: 500, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.08em" }}>{item.label}</div>
                  <div style={{ fontSize: 13, fontFamily: F, fontWeight: 700, color: "#2D3748", marginTop: 2 }}>{item.val}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Billing Friday Mode Toggle */}
        <div style={{ background: "#EBF4FF", borderRadius: 14, padding: "12px 16px", border: "1.5px solid #C3DAFE", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 13, fontFamily: F, fontWeight: 900, color: "#2B4C8C" }}>📅 Billing Friday Mode</div>
            <div style={{ fontSize: 11, fontFamily: F, color: "#4A6CF7", marginTop: 2 }}>Matches the WA SSPS Provider Portal view</div>
          </div>
          <button onClick={() => setIsBillingMode(p => !p)} style={{ padding: "8px 16px", borderRadius: 100, border: "none", background: isBillingMode ? "#4A6CF7" : "#E2E8F0", color: isBillingMode ? "#fff" : "#718096", fontSize: 12, fontFamily: F, fontWeight: 900, cursor: "pointer" }}>
            {isBillingMode ? "ON" : "OFF"}
          </button>
        </div>

        {/* Report Period Selector */}
        <div style={{ ...sxCard, padding: "14px 16px" }}>
          <div style={{ fontSize: 12, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Report Period</div>
          {/* Mode Toggle */}
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {["monthly", "custom"].map(mode => (
              <button key={mode} onClick={() => setRangeMode(mode)}
                style={{ flex: 1, padding: "8px", borderRadius: 8, border: `1.5px solid ${rangeMode === mode ? NAVY : "#E2E8F0"}`, background: rangeMode === mode ? NAVY : "#fff", color: rangeMode === mode ? "#fff" : "#718096", fontSize: 12, fontFamily: F, fontWeight: 800, cursor: "pointer", textTransform: "capitalize" }}>
                {mode === "monthly" ? "Monthly" : "Custom Range"}
              </button>
            ))}
          </div>
          {rangeMode === "monthly" ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select value={selMonth} onChange={e => setSelMonth(Number(e.target.value))} onBlur={e => setSelMonth(Number(e.target.value))}
                style={{ flex: 2, padding: "10px 12px", borderRadius: 10, border: "1.5px solid #E2E8F0", fontSize: 14, fontFamily: F, fontWeight: 700, color: "#2D3748", outline: "none", cursor: "pointer", WebkitAppearance: "menulist", appearance: "menulist" }}>
                {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
              <select value={selYear} onChange={e => setSelYear(Number(e.target.value))} onBlur={e => setSelYear(Number(e.target.value))}
                style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1.5px solid #E2E8F0", fontSize: 14, fontFamily: F, fontWeight: 700, color: "#2D3748", outline: "none", cursor: "pointer", WebkitAppearance: "menulist", appearance: "menulist" }}>
                {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1.5px solid #E2E8F0", fontSize: 14, fontFamily: F, fontWeight: 700, color: "#2D3748", outline: "none" }} />
              <div style={{ fontSize: 12, color: "#A0AEC0", fontFamily: F }}>to</div>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1.5px solid #E2E8F0", fontSize: 14, fontFamily: F, fontWeight: 700, color: "#2D3748", outline: "none" }} />
            </div>
          )}
        </div>

        {/* Enrolled Children */}
        <div style={{ ...sxCard }}>
          <div style={{ ...sxHdr }}>👶 Enrolled Children</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, fontFamily: F }}>
              <thead>
                <tr style={{ background: "#F8F9FB" }}>
                  {["Child Name", "Payment Type", "Days Attended"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 800, color: "#718096", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1.5px solid #E2E8F0" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {kids.map((kid, i) => (
                  <tr key={kid.id} style={{ borderBottom: i < kids.length - 1 ? "1px solid #F0F2F5" : "none" }}>
                    <td style={{ padding: "11px 14px", fontWeight: 700, color: "#2D3748" }}>{shortName(kid.name)}</td>
                    <td style={{ padding: "11px 14px" }}>
                      <span style={{ padding: "3px 10px", borderRadius: 100, background: kid.auth === "WCCC" ? "#EBF4FF" : "#F0FFF4", color: kid.auth === "WCCC" ? "#4A6CF7" : "#2ECC8A", fontSize: 11, fontWeight: 800 }}>{kid.auth}</span>
                    </td>
                    <td style={{ padding: "11px 14px", fontWeight: 900, color: "#2ECC8A", fontSize: 16 }}>{daysPerChild[kid.name]?.size || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Billing Friday Mode View */}
        {isBillingMode && billingWeeks && (
          <div style={{ ...sxCard }}>
            <div style={{ ...sxHdr, background: "#4A6CF7" }}>📅 Billing Weeks — SSPS View</div>
            {Object.entries(billingWeeks).sort(([a],[b]) => new Date(a)-new Date(b)).map(([friday, records]) => {
              const fridayDate = new Date(friday);
              const monday = new Date(fridayDate);
              monday.setDate(fridayDate.getDate() - 4);
              const childDays = {};
              records.forEach(r => {
                const name = r.children?.name || "Unknown";
                if (!childDays[name]) childDays[name] = new Set();
                childDays[name].add(new Date(r.sign_in_time).toDateString());
              });
              return (
                <div key={friday} style={{ borderBottom: "1.5px solid #F0F2F5", padding: "12px 16px" }}>
                  <div style={{ fontSize: 12, fontFamily: F, fontWeight: 900, color: "#4A6CF7", marginBottom: 8 }}>
                    Week of {monday.toLocaleDateString("en-US", { month: "short", day: "numeric" })} — {fridayDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </div>
                  {Object.entries(childDays).map(([name, days]) => (
                    <div key={name} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12, fontFamily: F }}>
                      <span style={{ color: "#2D3748", fontWeight: 700 }}>{name}</span>
                      <span style={{ color: "#2ECC8A", fontWeight: 900 }}>{days.size} day{days.size !== 1 ? "s" : ""}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Daily Attendance Transactions */}
        <div style={{ ...sxCard }}>
          <div style={{ ...sxHdr }}>📅 Daily Attendance Transactions</div>
          {loading ? (
            <div style={{ padding: "30px", textAlign: "center", fontSize: 14, fontFamily: F, color: "#A0AEC0" }}>Loading attendance data...</div>
          ) : attendance.length === 0 ? (
            <div style={{ padding: "40px 20px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}><div style={{ fontSize: 40 }}>📋</div><div style={{ fontSize: 15, fontFamily: F, fontWeight: 700, color: NAVY }}>All clear!</div><div style={{ fontSize: 13, fontFamily: F, color: "#94A3B8", fontWeight: 500 }}>No attendance records for {MONTHS[selMonth]} {selYear}.</div></div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: F }}>
                <thead>
                  <tr style={{ background: "#F8F9FB" }}>
                    {["Date", "Child Name", "Sign In", "Sign Out", "Transaction ID"].map(h => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 10, fontWeight: 800, color: "#718096", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1.5px solid #E2E8F0", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {attendance.map((r, i) => {
                    const d = new Date(r.sign_in_time);
                    const txnId = `TXN-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,"0")}${String(d.getDate()).padStart(2,"0")}-${String(i+1).padStart(3,"0")}`;
                    return (
                      <tr key={r.id} style={{ borderBottom: i < attendance.length - 1 ? "1px solid #F0F2F5" : "none" }}>
                        <td style={{ padding: "10px 12px", whiteSpace: "nowrap", color: "#4A5568" }}>
                          {d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" })} {d.toLocaleDateString("en-US", { weekday: "short" })}
                        </td>
                        <td style={{ padding: "10px 12px", fontWeight: 700, color: "#2D3748" }}>{r.children?.name || "—"}</td>
                        <td style={{ padding: "10px 12px", color: "#2ECC8A", fontWeight: 700 }}>{d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</td>
                        <td style={{ padding: "10px 12px", color: r.sign_out_time ? "#4A6CF7" : "#F5A623", fontWeight: 700 }}>
                          {r.sign_out_time ? new Date(r.sign_out_time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "In care"}
                        </td>
                        <td style={{ padding: "10px 12px", color: "#A0AEC0", fontSize: 11 }}>{txnId}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Monthly Summary */}
        <div style={{ ...sxCard }}>
          <div style={{ ...sxHdr }}>📊 Monthly Summary — {MONTHS[selMonth]} {selYear}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0 }}>
            {[
              { label: "Total Transactions", val: attendance.length, color: "#2ECC8A" },
              { label: "WCCC Children",      val: wcccCount,         color: "#4A6CF7" },
              { label: "Private Pay",         val: privateCount,      color: "#F5A623" },
            ].map((s, i) => (
              <div key={s.label} style={{ padding: "16px 14px", textAlign: "center", borderRight: i < 2 ? "1px solid #F0F2F5" : "none" }}>
                <div style={{ fontSize: 28, fontFamily: F, fontWeight: 900, color: s.color }}>{s.val}</div>
                <div style={{ fontSize: 11, fontFamily: F, color: "#718096", fontWeight: 700, marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
          <div style={{ padding: "12px 16px", borderTop: "1px solid #F0F2F5", background: "#F8F9FB" }}>
            <div style={{ fontSize: 11, fontFamily: F, color: "#718096", fontWeight: 700 }}>
              🔒 Retention: 5 Years (Until {retainUntil}) · WAC 110-15-0126
            </div>
            <div style={{ fontSize: 10, fontFamily: F, color: "#A0AEC0", marginTop: 3 }}>
              Report ID: {reportId} · SSPS #: {profile?.sspsN || "—"} · License: {profile?.licenseN || "—"}
            </div>
          </div>
        </div>

        {/* Export Buttons */}
        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <button onClick={exportCSV} style={{ flex: 1, padding: "16px", borderRadius: 16, border: "none", background: "#2ECC8A", color: "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>
            ↓ Export CSV
          </button>
          <button onClick={exportPDF} style={{ flex: 1, padding: "16px", borderRadius: 16, border: "none", background: "#E8394D", color: "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>
            ↓ Export PDF
          </button>
        </div>
        <div style={{ fontSize: 11, fontFamily: F, color: "#A0AEC0", textAlign: "center" }}>
          Electronically generated by Toto · totocare.app · WAC 110-15-0126
        </div>

      </div>
    </div>
  );
}

// ── MEALS ──────────────────────────────────────────────────
// ── INCIDENT REPORTER ─────────────────────────────────────
function IncidentReporter({ onBack, kids = [], user, profile }) {
  const tile = TILES.find(t => t.id === "incident");
  const [tab, setTab] = useState("new"); // "new" or "history"
  const [selectedKid, setSelectedKid] = useState(null);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [incidentDate, setIncidentDate] = useState(new Date().toISOString().split("T")[0]);
  const [incidentTime, setIncidentTime] = useState(new Date().toTimeString().slice(0,5));
  const [incidentLocation, setIncidentLocation] = useState("indoors");
  const [incidentType, setIncidentType] = useState("child");
  const [involvedPerson, setInvolvedPerson] = useState("");
  const [staffPresent, setStaffPresent] = useState("");
  const [treatmentProvided, setTreatmentProvided] = useState("");
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [parentContactDate, setParentContactDate] = useState("");
  const [parentContactMethod, setParentContactMethod] = useState("");
  const [licensorContacted, setLicensorContacted] = useState(false);
  const [licensorContactDate, setLicensorContactDate] = useState("");
  const [licensorContactMethod, setLicensorContactMethod] = useState("");

  // Load history when switching to history tab
  useEffect(() => {
    if (tab === "history" && user?.id) {
      setHistLoading(true);
      import("./supabase").then(({ supabase }) => {
        supabase.from("incident_reports")
          .select("*")
          .eq("provider_id", user.id)
          .order("created_at", { ascending: false })
          .then(({ data }) => { setHistory(data || []); setHistLoading(false); });
      });
    }
  }, [tab, user]);

  const selectKid = (kid) => {
    setSelectedKid(kid);
    // Prepend child name to input if not already there
    if (!input.includes(kid.name)) {
      setInput(prev => prev ? `${kid.name} — ${prev}` : `${kid.name} — `);
    }
  };

  const generate = async () => {
    if (!input.trim()) return;
    setLoading(true); setOutput(null); setSaved(false);
    try {
      const r = await fetch("/api/generate-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ incident: input, incidentType, involvedPerson: involvedPerson || selectedKid?.name || null, childName: selectedKid?.name || null, date: new Date().toLocaleDateString("en-US", {timeZone: "America/Los_Angeles"}), time: new Date().toLocaleTimeString("en-US", {timeZone: "America/Los_Angeles", hour: "2-digit", minute: "2-digit"}), location: incidentLocation, parentNotified: parentContactMethod, emergency911: staffPresent, childInjured: treatmentProvided, licensorNotified: licensorContactMethod })
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); setOutput(`⚠️ Error ${r.status}: ${err?.error || r.statusText}`); setLoading(false); return; }
      const d = await r.json();
      setOutput(d.report || "Could not generate. Try again.");
      if (d.incidentDate) setIncidentDate(d.incidentDate);
      if (d.incidentTime) setIncidentTime(d.incidentTime);
      if (d.location === "indoors" || d.location === "outdoors") setIncidentLocation(d.location);
      if (d.staffPresent) setStaffPresent(d.staffPresent);
      if (d.treatmentProvided) setTreatmentProvided(d.treatmentProvided);
      if (d.incidentTypes?.length) setIncidentTypes(d.incidentTypes);
    } catch (e) { setOutput(`⚠️ Connection error: ${e.message}`); }
    setLoading(false);
  };


  const exportDCYFPdf = async () => {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
    const black = [0, 0, 0];
    const navy = [26, 54, 93];
    const lightGray = [220, 220, 220];
    const darkGray = [80, 80, 80];
    const W = 215.9; // letter width mm
    const L = 14;    // left margin
    const R = 201;   // right margin
    let y = 14;

    const box = (x, bY, w, h, fill) => {
      if (fill) { doc.setFillColor(...fill); doc.rect(x, bY, w, h, "F"); }
      doc.setDrawColor(...black); doc.setLineWidth(0.3); doc.rect(x, bY, w, h);
    };
    const cell = (text, x, bY, w, h, opts = {}) => {
      box(x, bY, w, h, opts.fill);
      doc.setFontSize(opts.fontSize || 8);
      doc.setFont("helvetica", opts.bold ? "bold" : "normal");
      doc.setTextColor(...(opts.color || black));
      if (opts.center) doc.text(text, x + w/2, bY + h/2 + 1.5, { align: "center" });
      else doc.text(text, x + 2, bY + h/2 + 1.5);
    };
    const chk = (checked, x, bY) => {
      doc.setDrawColor(...black); doc.setLineWidth(0.2);
      doc.rect(x, bY, 3.5, 3.5);
      if (checked) { doc.setFontSize(7); doc.setTextColor(...black); doc.text("X", x + 0.5, bY + 3); }
    };

    // Title
    doc.setFontSize(13); doc.setFont("helvetica", "bold"); doc.setTextColor(...black);
    doc.text("Child Care Injury/Incident Report", W/2, y, { align: "center" }); y += 7;

    // Child's Name row
    box(L, y, 120, 8);
    doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(...darkGray);
    doc.text("Child's Name:", L+2, y+3);
    doc.setTextColor(...black);
    doc.text(selectedKid?.name || involvedPerson || "", L+28, y+5.5);
    y += 8;

    // Notice banner
    box(L, y, R-L, 10, [235,235,235]);
    doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...black);
    doc.text("In addition to reporting to the department by phone or email about the following incidents and injuries,", W/2, y+4, { align: "center" });
    doc.text("a provider must also complete this incident report and submit it to DCYF within 24-hours.", W/2, y+8, { align: "center" });
    y += 10;

    // Provider Name | Provider ID
    box(L, y, 120, 10);
    box(L+120, y, R-L-120, 10);
    doc.setFontSize(7); doc.setTextColor(...darkGray);
    doc.text("Provider Name", L+2, y+3);
    doc.text("Provider ID", L+122, y+3);
    doc.setFontSize(8.5); doc.setTextColor(...black);
    doc.text(profile?.providerN || "", L+2, y+8.5);
    doc.text(profile?.sspsN || "", L+122, y+8.5);
    y += 10;

    // Child's Age | Date | Time | Location
    box(L, y, 45, 18);
    box(L+45, y, 45, 18);
    box(L+90, y, 45, 18);
    box(L+135, y, R-L-135, 18);
    doc.setFontSize(7); doc.setTextColor(...darkGray);
    doc.text("Child's Age", L+2, y+3);
    doc.text("Date of Incident", L+47, y+3);
    doc.text("Time of Incident", L+92, y+3);
    doc.text("Incident Occurred", L+137, y+3);
    doc.setFontSize(8.5); doc.setTextColor(...black);
    doc.text(selectedKid?.age ? `${selectedKid.age} yrs` : "", L+2, y+13);
    doc.text(incidentDate || "", L+47, y+13);
    // Time with am/pm
    const tStr = incidentTime || "";
    const tHour = tStr ? parseInt(tStr.split(":")[0]) : null;
    const isAM = tHour !== null && tHour < 12;
    doc.text(tStr, L+92, y+10);
    doc.setFontSize(7);
    chk(isAM, L+92, y+12); doc.text("a.m.", L+97, y+15);
    chk(!isAM && tHour !== null, L+110, y+12); doc.text("p.m.", L+115, y+15);
    // Location checkboxes
    doc.setFontSize(7); doc.setTextColor(...black);
    chk(incidentLocation === "indoors", L+137, y+8); doc.text("Indoors", L+142, y+11);
    chk(incidentLocation === "outdoors", L+158, y+8); doc.text("Outdoors", L+163, y+11);
    y += 18;

    // Staff present | Treatment
    box(L, y, 93, 14);
    box(L+93, y, R-L-93, 14);
    doc.setFontSize(7); doc.setTextColor(...darkGray);
    doc.text("List names of staff present and/or witnesses:", L+2, y+3);
    doc.text("Treatment provided to child while in care & by who:", L+95, y+3);
    doc.setFontSize(8); doc.setTextColor(...black);
    const spLines = doc.splitTextToSize(staffPresent || "", 85);
    doc.text(spLines, L+2, y+8);
    const tpLines = doc.splitTextToSize(treatmentProvided || "", 95);
    doc.text(tpLines, L+95, y+8);
    y += 14;

    // Section: Check All That Apply header
    box(L, y, R-L, 6, [220,220,220]);
    doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(...black);
    doc.text("Check All That Apply", W/2, y+4.5, { align: "center" });
    y += 6;

    // Emergency response section
    box(L, y, R-L, 5, [245,245,245]);
    doc.setFontSize(7.5); doc.setFont("helvetica", "bold");
    doc.text("Situation that required an emergency response from:", L+2, y+3.5);
    y += 5;
    box(L, y, R-L, 12);
    const emerTypes = ["Emergency services (911)", "Washington poison center", "Department of Health"];
    const emerWacs = ["110-300-0475(2)(b)/110-301-0475(2)(b)", "110-300-0475(2)(c)/110-301-0475(2)(c)", "110-300-0475(2)(d)/110-301-0475(2)(d)"];
    const emerX = [L+2, L+72, L+140];
    emerTypes.forEach((t, i) => {
      chk(incidentTypes.includes(t), emerX[i], y+2);
      doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...black);
      doc.text(t, emerX[i]+5, y+5.5);
      doc.setFontSize(6); doc.setTextColor(...darkGray);
      doc.text(emerWacs[i], emerX[i]+5, y+9.5);
    });
    y += 12;

    // At risk situations
    box(L, y, R-L, 5, [245,245,245]);
    doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); doc.setTextColor(...black);
    doc.text("Situations that occur while children are in care that may put children at risk including, but not limited to:", L+2, y+3.5);
    y += 5;
    box(L, y, R-L, 16);
    const riskTypesChecked = incidentType === "parent" ? [...incidentTypes, "Other"] : incidentTypes;
    const riskTypes = ["Inappropriate sexual touching", "Physical abuse", "Neglect", "Maltreatment", "Exploitation", "Other"];
    const riskX = [L+2, L+52, L+90, L+115, L+143, L+170];
    const riskY = [y+2, y+2, y+2, y+2, y+2, y+2];
    riskTypes.forEach((t, i) => {
      chk(riskTypesChecked.includes(t), riskX[i], riskY[i]);
      doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...black);
      doc.text(t, riskX[i]+5, riskY[i]+3.5);
    });
    y += 16;

    // Serious injury section
    box(L, y, R-L, 5, [245,245,245]);
    doc.setFontSize(7.5); doc.setFont("helvetica", "bold"); doc.setTextColor(...black);
    doc.text("Serious injury to a child in care:", L+2, y+3.5);
    y += 5;
    box(L, y, R-L, 24);
    const injuryCol1 = ["Severe bleeding", "Severe neck/head injury", "Near drowning", "Poisoning"];
    const injuryCol2 = ["One or more fractured/broken bones", "Sudden unconsciousness", "Shock or acute confused state", "Overdose of chemical substance"];
    const injuryCol3 = ["Choking or serious unexpected breathing problems", "Dangerous chemicals in eyes, on skin, or ingested", "Severe burn requiring professional medical care", "Injury resulting in overnight hospital stay"];
    injuryCol1.forEach((t, i) => { chk(incidentTypes.includes(t), L+2, y+2+i*5.5); doc.setFontSize(7); doc.setFont("helvetica","normal"); doc.setTextColor(...black); doc.text(t, L+8, y+5.5+i*5.5); });
    injuryCol2.forEach((t, i) => { chk(incidentTypes.includes(t), L+62, y+2+i*5.5); doc.setFontSize(7); doc.setFont("helvetica","normal"); doc.setTextColor(...black); doc.text(t, L+68, y+5.5+i*5.5); });
    injuryCol3.forEach((t, i) => { chk(incidentTypes.includes(t), L+130, y+2+i*5.5); doc.setFontSize(7); doc.setFont("helvetica","normal"); doc.setTextColor(...black); const lines = doc.splitTextToSize(t, 55); doc.text(lines, L+136, y+5.5+i*5.5); });
    y += 24;

    // Description box
    box(L, y, R-L, 40);
    doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...darkGray);
    doc.text("Please give a brief description of the injury/incident, including where it occurred.", L+2, y+4);
    doc.setFontSize(8); doc.setTextColor(...black);
    const descLines = doc.splitTextToSize(output || input || "", 178);
    doc.text(descLines, L+2, y+10);
    y += 40;

    // Parent/Guardian Contacted | Licensor Contacted
    box(L, y, (R-L)/2, 6, [235,235,235]);
    box(L+(R-L)/2, y, (R-L)/2, 6, [235,235,235]);
    doc.setFontSize(8); doc.setFont("helvetica", "bold"); doc.setTextColor(...black);
    doc.text("Parent/Guardian Contacted", L+2, y+4.5);
    doc.text("Licensor Contacted", L+(R-L)/2+2, y+4.5);
    y += 6;
    box(L, y, (R-L)/2, 12);
    box(L+(R-L)/2, y, (R-L)/2, 12);
    doc.setFontSize(7); doc.setFont("helvetica", "normal"); doc.setTextColor(...black);
    // Left side
    doc.text("Date:", L+2, y+5); doc.text("Time:", L+30, y+5);
    chk(false, L+2, y+7); doc.text("In Person", L+7, y+10.5);
    chk(false, L+28, y+7); doc.text("Phone", L+33, y+10.5);
    chk(false, L+50, y+7); doc.text("E-mail", L+55, y+10.5);
    // Right side
    const rx = L+(R-L)/2;
    doc.text("Date:", rx+2, y+5); doc.text("Time:", rx+30, y+5);
    chk(false, rx+2, y+7); doc.text("In Person", rx+7, y+10.5);
    chk(false, rx+28, y+7); doc.text("Phone", rx+33, y+10.5);
    chk(false, rx+50, y+7); doc.text("E-mail", rx+55, y+10.5);
    y += 12;

    // Parent/Guardian Comments
    box(L, y, R-L, 18);
    doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...darkGray);
    doc.text("Parent/Guardian Comments:", L+2, y+4);
    y += 18;

    // Signature lines
    box(L, y, (R-L)/2, 20);
    box(L+(R-L)/2, y, (R-L)/2, 20);
    doc.setFontSize(7); doc.setTextColor(...darkGray);
    doc.text("Parent/Guardian Signature", L+2, y+12);
    doc.text("Date", L+(R-L)/2-20, y+12);
    doc.text("Licensee/Staff Signature", L+(R-L)/2+2, y+12);
    doc.text("Date", R-18, y+12);
    doc.setFontSize(6.5);
    doc.text("By signing this form, I acknowledge that I received a copy of this report.", L+2, y+17);
    y += 20;

    // Footer
    doc.setFontSize(7); doc.setTextColor(...darkGray);
    doc.text("CHILD CARE INJURY/INCIDENT REPORT", L, y+6);
    doc.text("DCYF 15-941 (REV. 01/2023) EXT", L, y+10);
    doc.text("Copies to:  Parent, licensor, licensee", R-60, y+6);
    doc.text("Generated by Toto · totocare.app", R-60, y+10);

    doc.save(`DCYF_Incident_Report_${incidentDate || "draft"}.pdf`);
  };

  const saveReport = async () => {
    if (!output || !user?.id) return;
    setSaving(true);
    try {
      const { supabase } = await import("./supabase");
      await supabase.from("incident_reports").insert({
        provider_id: user.id,
        child_name: selectedKid?.name || null,
        description: input,
        report: output,
      });
      setSaved(true);
    } catch (e) { console.error("Save error:", e); alert("Report could not be saved. Please try again or contact support@totocare.app"); }
    setSaving(false);
  };

  return (
    <div style={{ background: OFF_WHITE, minHeight: "100vh", paddingBottom: 80 }}>
      <ScreenHeader tile={tile} onBack={onBack} />

      {/* Tabs */}
      <div style={{ display: "flex", background: "#fff", borderBottom: "1.5px solid #E2E8F0", padding: "0 16px" }}>
        {[{ id: "new", label: "New Report" }, { id: "history", label: "History" }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: "14px 20px", border: "none", background: "none", fontSize: 14, fontFamily: F, fontWeight: 800, color: tab === t.id ? "#9B59B6" : "#A0AEC0", borderBottom: `3px solid ${tab === t.id ? "#9B59B6" : "transparent"}`, cursor: "pointer", marginBottom: -1.5 }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "new" ? (
        <div style={{ padding: "16px 16px" }}>

          {/* Child selector */}
          {kids.length > 0 && (
            <div style={{ background: "#fff", borderRadius: 16, border: "1.5px solid #E2E8F0", padding: "14px 16px", marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Select Child (Optional)</div>
              <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
                {kids.map(kid => (
                  <button key={kid.id} onClick={() => selectKid(kid)}
                    style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", padding: "4px 8px", borderRadius: 12, outline: selectedKid?.id === kid.id ? `2.5px solid #9B59B6` : "none", flexShrink: 0 }}>
                    <Avatar person={kid} size={48} />
                    <span style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: selectedKid?.id === kid.id ? "#9B59B6" : "#718096", maxWidth: 60, textAlign: "center", lineHeight: 1.2 }}>{kid.name.split(" ")[0]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Info banner */}
          <div style={{ background: "#F8F5FF", borderRadius: 16, border: "1.5px solid #E9D8FD", padding: "14px 16px", marginBottom: 14, display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ fontSize: 22, flexShrink: 0 }}>📋</div>
            <div>
              <div style={{ fontSize: 14, fontFamily: F, fontWeight: 800, color: "#9B59B6", marginBottom: 3 }}>DCYF-Compliant Incident Reports</div>
              <div style={{ fontSize: 13, fontFamily: F, color: "#718096", lineHeight: 1.5 }}>Describe what happened in plain language — Toto will format it into an official DCYF incident report.</div>
            </div>
          </div>

          {/* Incident Type Selector */}
          <div style={{ background: "#fff", borderRadius: 16, border: "1.5px solid #E2E8F0", padding: "14px 16px", marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>Incident Type</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                { id: "child", icon: "🩹", label: "Child Incident", sub: "Injury, behavior, or emergency involving a child" },
                { id: "parent", icon: "👨‍👩‍👧", label: "Parent / Guardian Behavior", sub: "Inappropriate or unsafe behavior by a parent or guardian" },
                { id: "termination", icon: "📋", label: "Termination of Services", sub: "WAC 110-300-0485 — document reason for disenrollment" },
              ].map(type => (
                <button key={type.id} onClick={() => setIncidentType(type.id)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${incidentType === type.id ? "#9B59B6" : "#E2E8F0"}`, background: incidentType === type.id ? "#F8F5FF" : "#fff", cursor: "pointer", textAlign: "left" }}>
                  <div style={{ fontSize: 24, flexShrink: 0 }}>{type.icon}</div>
                  <div>
                    <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: incidentType === type.id ? "#9B59B6" : "#2D3748" }}>{type.label}</div>
                    <div style={{ fontSize: 11, fontFamily: F, color: "#94A3B8", marginTop: 2 }}>{type.sub}</div>
                  </div>
                  <div style={{ marginLeft: "auto", width: 18, height: 18, borderRadius: "50%", border: `2px solid ${incidentType === type.id ? "#9B59B6" : "#E2E8F0"}`, background: incidentType === type.id ? "#9B59B6" : "#fff", flexShrink: 0 }}>
                    {incidentType === type.id && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", margin: "4px auto" }} />}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Person Involved */}
          {incidentType === "parent" && (
            <div style={{ background: "#fff", borderRadius: 16, border: "1.5px solid #E2E8F0", padding: "14px 16px", marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Parent / Guardian Name</div>
              <input
                value={involvedPerson} onChange={e => setInvolvedPerson(e.target.value)}
                placeholder="Enter parent or guardian name"
                style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1.5px solid #E2E8F0", fontSize: 14, fontFamily: F, outline: "none", boxSizing: "border-box" }}
              />
            </div>
          )}

          {/* Free-form description */}
          <div style={{ background: "#fff", borderRadius: 16, border: "1.5px solid #E2E8F0", padding: "14px 16px", marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Describe What Happened</div>
            <div style={{ fontSize: 12, fontFamily: F, color: "#94A3B8", marginBottom: 10 }}>Write in plain language — Toto will format it into an official DCYF-compliant report.</div>
            <textarea
              value={input} onChange={e => setInput(e.target.value)}
              placeholder={
                incidentType === "child" ? "e.g. Ahmed fell off the play structure and hurt his knee. I applied a bandage and called his mom at 2pm..." :
                incidentType === "parent" ? "e.g. The parent arrived and began yelling at staff in front of the children. I asked them to step outside..." :
                incidentType === "termination" ? "e.g. The family has had 5 consecutive late pickups and unpaid balance of $200 despite repeated reminders..." :
                "Describe the incident in your own words..."
              }
              style={{ width: "100%", minHeight: 140, padding: "12px 14px", borderRadius: 10, border: "1.5px solid #E2E8F0", fontSize: 14, fontFamily: F, outline: "none", boxSizing: "border-box", resize: "vertical", lineHeight: 1.6 }}
            />
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <button onClick={generate} disabled={loading || !input.trim()}
              style={{ flex: 1, padding: "16px", borderRadius: 16, border: "none", background: loading || !input.trim() ? "#E2E8F0" : "#9B59B6", color: loading || !input.trim() ? "#A0AEC0" : "#fff", fontSize: 15, fontFamily: F, fontWeight: 800, cursor: loading || !input.trim() ? "not-allowed" : "pointer" }}>
              {loading ? "Generating report..." : "📝 Generate DCYF Report"}
            </button>
            <button onClick={() => { setInput(""); setOutput(null); setSelectedKid(null); setSaved(false); setCopied(false); setStaffPresent(""); setTreatmentProvided(""); setIncidentTypes([]); setParentContactDate(""); setParentContactMethod(""); setLicensorContactDate(""); setLicensorContactMethod(""); setIncidentDate(new Date().toISOString().split("T")[0]); setIncidentTime(new Date().toTimeString().slice(0,5)); setIncidentLocation("indoors"); setIncidentType("child"); setInvolvedPerson(""); }}
              disabled={!input && !output}
              style={{ padding: "16px 20px", borderRadius: 16, border: "1.5px solid #E2E8F0", background: !input && !output ? "#F8F8F8" : "#fff", color: !input && !output ? "#CBD5E0" : "#E8394D", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: !input && !output ? "not-allowed" : "pointer" }}>
              Clear
            </button>
          </div>

          {output && (
            <div style={{ background: "#F8F5FF", borderRadius: 18, border: "1.5px solid #E9D8FD", overflow: "hidden", marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 16px", borderBottom: "1px solid #E9D8FD" }}>
                <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#9B59B6" }}>✓ DCYF Incident Report</div>
                <button onClick={() => { navigator.clipboard.writeText(output); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  style={{ padding: "6px 14px", borderRadius: 100, border: "1px solid #E9D8FD", background: "#fff", fontSize: 12, fontFamily: F, fontWeight: 800, cursor: "pointer", color: copied ? "#38A169" : "#9B59B6" }}>
                  {copied ? "✓ Copied!" : "Copy"}
                </button>
              </div>
              <div style={{ padding: "16px 18px", fontSize: 14, fontFamily: F, color: "#2D3748", lineHeight: 2, whiteSpace: "pre-wrap" }}>{output}</div>
              <div style={{ padding: "12px 16px", borderTop: "1px solid #E9D8FD", display: "flex", flexDirection: "column", gap: 8 }}>
                <button onClick={saveReport} disabled={saving || saved}
                  style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: saved ? "#38A169" : saving ? "#E2E8F0" : "#9B59B6", color: saved || saving ? (saved ? "#fff" : "#A0AEC0") : "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: saved || saving ? "default" : "pointer" }}>
                  {saved ? "✓ Report Saved!" : saving ? "Saving..." : "💾 Save Report"}
                </button>
                <button onClick={exportDCYFPdf}
                  style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: NAVY, color: "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>
                  📄 Export DCYF 15-941 PDF
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: "16px 16px" }}>
          {histLoading ? (
            <div style={{ textAlign: "center", padding: "40px", fontSize: 14, fontFamily: F, color: "#A0AEC0" }}>Loading reports...</div>
          ) : history.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", background: "#fff", borderRadius: 18, border: "1.5px solid #E2E8F0" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
              <div style={{ fontSize: 15, fontFamily: F, fontWeight: 700, color: NAVY, marginBottom: 6 }}>All clear!</div>
              <div style={{ fontSize: 13, fontFamily: F, color: "#94A3B8", fontWeight: 500 }}>No incident reports yet. All is well! 🎉</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {history.map(r => (
                <div key={r.id} style={{ background: "#fff", borderRadius: 16, border: "1.5px solid #E2E8F0", overflow: "hidden" }}>
                  <button onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: "none", border: "none", cursor: "pointer" }}>
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 14, fontFamily: F, fontWeight: 800, color: "#2D3748" }}>
                        {r.child_name || "Unknown child"}
                      </div>
                      <div style={{ fontSize: 12, fontFamily: F, color: "#A0AEC0", marginTop: 2 }}>
                        {new Date(r.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                      </div>
                    </div>
                    <div style={{ fontSize: 18, color: "#A0AEC0" }}>{expanded === r.id ? "▲" : "▼"}</div>
                  </button>
                  {expanded === r.id && (
                    <div style={{ padding: "0 16px 16px", borderTop: "1px solid #F0F2F5" }}>
                      <div style={{ fontSize: 12, fontFamily: F, color: "#A0AEC0", marginBottom: 8, marginTop: 12 }}>Description: {r.description}</div>
                      <div style={{ fontSize: 13, fontFamily: F, color: "#2D3748", lineHeight: 1.9, whiteSpace: "pre-wrap", background: "#F8F5FF", borderRadius: 12, padding: "12px 14px" }}>{r.report}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Meals({ onBack }) {
  const tile = TILES.find(t => t.id === "meals");
  const [age, setAge] = useState("all"), [week, setWeek] = useState(1), [output, setOutput] = useState(null), [loading, setLoading] = useState(false), [copied, setCopied] = useState(false);
  const ageGroups = [{ id: "all", label: "All ages" }, { id: "infant", label: "Infants" }, { id: "toddler", label: "Toddlers" }, { id: "preschool", label: "Preschool" }];

  const generate = async () => {
    setLoading(true); setOutput(null);
    try {
      const apiKey = process.env.REACT_APP_ANTHROPIC_KEY;
      if (!apiKey) { setOutput("⚠️ API key missing. Add REACT_APP_ANTHROPIC_KEY to your .env file and restart the dev server."); setLoading(false); return; }
      const r = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true"
        },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 1200,
          messages: [{ role: "user", content: `You are a nutrition expert for WA State licensed family home daycare providers. Generate a DCYF CACFP-compliant weekly meal plan for Week ${week} for ${age === "all" ? "mixed ages" : age + "s"}.\n\nFormat as plain text:\nMonday\n  Breakfast: [item] | [item] | [item]\n  Lunch: [item] | [item] | [item] | [item]\n  Snack: [item] | [item]\n\nTuesday... through Friday.\n\nSimple, affordable, kid-friendly. Follow CACFP meal pattern requirements. No markdown, no asterisks, plain text only.` }]
        })
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); setOutput(`⚠️ API error ${r.status}: ${err?.error?.message || r.statusText}`); setLoading(false); return; }
      const d = await r.json();
      setOutput(d.content?.[0]?.text || "Could not generate. Try again.");
    } catch (e) { setOutput(`⚠️ Connection error: ${e.message}. Check your internet connection and try again.`); }
    setLoading(false);
  };

  return (
    <div style={{ background: OFF_WHITE, minHeight: "100vh", paddingBottom: 80 }}>
      <ScreenHeader tile={tile} onBack={onBack} />
      <div style={{ padding: "18px 16px" }}>
        <div style={{ background: "#F8F5FF", borderRadius: 18, border: "1.5px solid #E9D8FD", padding: "18px", marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Age Group</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            {ageGroups.map(a => (
              <button key={a.id} onClick={() => setAge(a.id)} style={{ padding: "9px 18px", borderRadius: 100, border: `1.5px solid ${age === a.id ? "#9B59B6" : "#E2E8F0"}`, background: age === a.id ? "#9B59B6" : "#fff", color: age === a.id ? "#fff" : "#718096", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>
                {a.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Week</div>
          <div style={{ display: "flex", gap: 8 }}>
            {[1, 2, 3, 4].map(w => (
              <button key={w} onClick={() => setWeek(w)} style={{ width: 58, height: 52, borderRadius: 14, border: `1.5px solid ${week === w ? "#9B59B6" : "#E2E8F0"}`, background: week === w ? "#9B59B6" : "#fff", color: week === w ? "#fff" : "#718096", fontSize: 18, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>
                {w}
              </button>
            ))}
          </div>
        </div>
        <button onClick={generate} disabled={loading} style={{ width: "100%", padding: "16px", borderRadius: 16, border: "none", background: loading ? "#E2E8F0" : "#9B59B6", color: loading ? "#A0AEC0" : "#fff", fontSize: 15, fontFamily: F, fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", marginBottom: 14 }}>
          {loading ? "Generating meal plan..." : "🍽️  Generate Meal Plan"}
        </button>
        {output && (
          <div style={{ background: "#F8F5FF", borderRadius: 18, border: "1.5px solid #E9D8FD", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 16px", borderBottom: "1px solid #E9D8FD" }}>
              <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#9B59B6" }}>✓ Week {week} · {ageGroups.find(a => a.id === age)?.label}</div>
              <button onClick={() => { navigator.clipboard.writeText(output); setCopied(true); setTimeout(() => setCopied(false), 2000); }} style={{ padding: "5px 12px", borderRadius: 100, border: "1px solid #E9D8FD", background: "#fff", fontSize: 11, fontFamily: F, fontWeight: 800, cursor: "pointer", color: copied ? "#38A169" : "#9B59B6" }}>
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <div style={{ padding: "16px 18px", fontSize: 15, fontFamily: F, color: "#4A5568", lineHeight: 2, whiteSpace: "pre-wrap" }}>{output}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── AI ASSISTANT ───────────────────────────────────────────
function AIAssistant({ onBack }) {
  const tile = TILES.find(t => t.id === "ai");
  const [sub, setSub] = useState("menu");
  const [aiIn, setAiIn] = useState(""), [aiOut, setAiOut] = useState(null), [aiLoad, setAiLoad] = useState(false), [aiCopied, setAiCopied] = useState(false);
  const [msgs, setMsgs] = useState([]), [chatIn, setChatIn] = useState(""), [chatLoad, setChatLoad] = useState(false);

  const genReport = async () => {
    if (!aiIn.trim()) return; setAiLoad(true); setAiOut(null);
    try {
const r = await fetch("/api/generate-report", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ incident: aiIn, date: new Date().toLocaleDateString("en-US", {timeZone: "America/Los_Angeles"}), time: new Date().toLocaleTimeString("en-US", {timeZone: "America/Los_Angeles", hour: "2-digit", minute: "2-digit"}) }) });      const d = await r.json(); setAiOut(d.report || "Unable to generate.");
    } catch { setAiOut("Connection error. Try again."); }
    setAiLoad(false);
  };

  const chat = async (q = chatIn) => {
    const qry = q.trim(); if (!qry) return; setChatIn(""); setMsgs(p => [...p, { role: "user", text: qry }]); setChatLoad(true);
    try {
      const r = await fetch("https://api.anthropic.com/v1/messages", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1000, system: "You are a friendly WA State childcare regulations assistant inside the Toto app. Help licensed in-home family daycare providers understand DCYF rules, ratios, WCCC payments, incident reporting. Be warm and concise.", messages: [...msgs.map(m => ({ role: m.role === "assistant" ? "assistant" : "user", content: m.text })), { role: "user", content: qry }] }) });
      const d = await r.json(); setMsgs(p => [...p, { role: "assistant", text: d.content[0].text }]);
    } catch { setMsgs(p => [...p, { role: "assistant", text: "Connection error. Call DCYF at (360) 902-8060." }]); }
    setChatLoad(false);
  };

  const SubBack = () => (
    <button onClick={() => setSub("menu")} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontFamily: F, fontWeight: 800, color: "#1AABDB", background: "none", border: "none", cursor: "pointer", marginBottom: 16, padding: 0 }}>
      <Icon type="back" size={16} color="#1AABDB" /> Back
    </button>
  );

  if (sub === "incident") return (
    <div style={{ background: OFF_WHITE, minHeight: "100vh", paddingBottom: 80 }}>
      <ScreenHeader tile={tile} onBack={onBack} />
      <div style={{ padding: "18px 16px" }}>
        <SubBack />
        <div style={{ fontSize: 18, fontFamily: F, fontWeight: 800, color: "#2D3748", marginBottom: 4 }}>AI Incident Reporter</div>
        <div style={{ fontSize: 13, fontFamily: F, color: "#A0AEC0", marginBottom: 16 }}>Describe what happened — we'll write the DCYF report.</div>
        <textarea value={aiIn} onChange={e => setAiIn(e.target.value)} placeholder="e.g. Sofia fell off the swing at 10am and scraped her knee..." style={{ width: "100%", minHeight: 110, borderRadius: 14, border: "1.5px solid #E2E8F0", padding: "13px", fontSize: 14, fontFamily: F, color: "#2D3748", resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.6, marginBottom: 12 }} />
        <button onClick={genReport} disabled={aiLoad || !aiIn.trim()} style={{ width: "100%", padding: "15px", borderRadius: 14, border: "none", background: aiLoad || !aiIn.trim() ? "#E2E8F0" : "#1AABDB", color: aiLoad || !aiIn.trim() ? "#A0AEC0" : "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer", marginBottom: 14 }}>
          {aiLoad ? "Generating..." : "Generate DCYF Report"}
        </button>
        {aiOut && (
          <div style={{ background: "#EBF8FF", borderRadius: 16, border: "1.5px solid #BEE3F8", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #BEE3F8" }}>
              <span style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#1AABDB" }}>✓ Report ready</span>
              <button onClick={() => { navigator.clipboard.writeText(aiOut); setAiCopied(true); setTimeout(() => setAiCopied(false), 2000); }} style={{ padding: "4px 11px", borderRadius: 100, border: "1px solid #BEE3F8", background: "#fff", fontSize: 11, fontFamily: F, fontWeight: 800, cursor: "pointer", color: aiCopied ? "#38A169" : "#1AABDB" }}>
                {aiCopied ? "Copied!" : "Copy"}
              </button>
            </div>
            <div style={{ padding: "14px 16px", fontSize: 13, fontFamily: F, color: "#4A5568", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{aiOut}</div>
          </div>
        )}
      </div>
    </div>
  );

  if (sub === "chat") return (
    <div style={{ background: OFF_WHITE, minHeight: "100vh", paddingBottom: 80 }}>
      <ScreenHeader tile={tile} onBack={onBack} />
      <div style={{ padding: "18px 16px 0" }}>
        <SubBack />
        <div style={{ fontSize: 18, fontFamily: F, fontWeight: 800, color: "#2D3748", marginBottom: 14 }}>WA Regulations Chat</div>
        {msgs.length === 0 && (
          <div style={{ marginBottom: 14 }}>
            {["What's the child-to-staff ratio?", "How do I report an incident?", "What records must I keep?"].map(q => (
              <button key={q} onClick={() => chat(q)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "13px 15px", borderRadius: 13, border: "1.5px solid #E2E8F0", background: "#F8F9FB", fontSize: 13, fontFamily: F, fontWeight: 700, cursor: "pointer", color: "#4A5568", marginBottom: 8 }}>
                <span style={{ color: "#1AABDB", fontWeight: 900 }}>→</span> {q}
              </button>
            ))}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
          {msgs.map((m, i) => (
            <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "85%", padding: "11px 15px", borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px", background: m.role === "user" ? "#1AABDB" : "#F8F9FB", color: m.role === "user" ? "#fff" : "#4A5568", fontSize: 14, fontFamily: F, lineHeight: 1.6, fontWeight: 600, border: m.role !== "user" ? "1.5px solid #E2E8F0" : "none" }}>{m.text}</div>
            </div>
          ))}
          {chatLoad && <div style={{ display: "flex", gap: 5, padding: "11px 15px", background: "#F8F9FB", borderRadius: "16px 16px 16px 4px", width: "fit-content", border: "1.5px solid #E2E8F0" }}>{[0, 1, 2].map(i => <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: "#CBD5E0", animation: `pulse 1.2s ${i * 0.2}s infinite` }} />)}</div>}
        </div>
      </div>
      <div style={{ position: "sticky", bottom: 0, background: "#fff", padding: "12px 16px", borderTop: "1.5px solid #F0F2F5", display: "flex", gap: 8 }}>
        <input value={chatIn} onChange={e => setChatIn(e.target.value)} onKeyDown={e => e.key === "Enter" && chat()} placeholder="Ask about WA regulations..." style={{ flex: 1, padding: "12px 14px", borderRadius: 50, border: "1.5px solid #E2E8F0", fontSize: 14, fontFamily: F, outline: "none", color: "#2D3748", background: "#F8F9FB" }} />
        <button onClick={() => chat()} disabled={chatLoad || !chatIn.trim()} style={{ padding: "12px 18px", borderRadius: 50, border: "none", background: chatLoad || !chatIn.trim() ? "#E2E8F0" : "#1AABDB", color: "#fff", fontSize: 13, fontFamily: F, fontWeight: 800, cursor: "pointer" }}>Send</button>
      </div>
    </div>
  );

  return (
    <div style={{ background: OFF_WHITE, minHeight: "100vh", paddingBottom: 80 }}>
      <ScreenHeader tile={tile} onBack={onBack} />
      <div style={{ padding: "18px 16px" }}>
        {[
          { id: "incident", icon: "📝", label: "AI Incident Reporter", sub: "Turn any incident into a DCYF report" },
          { id: "chat",     icon: "💬", label: "WA Regulations Chat",  sub: "Ask anything about childcare rules"  },
        ].map((item, i, arr) => (
          <button key={item.id} onClick={() => setSub(item.id)} style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "16px", background: "#fff", border: "none", borderBottom: i < arr.length - 1 ? "1.5px solid #F0F2F5" : "none", cursor: "pointer", textAlign: "left" }}>
            <div style={{ width: 50, height: 50, borderRadius: 14, background: "#EBF8FF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{item.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontFamily: F, fontWeight: 800, color: "#2D3748" }}>{item.label}</div>
              <div style={{ fontSize: 12, fontFamily: F, color: "#A0AEC0", marginTop: 2, fontWeight: 600 }}>{item.sub}</div>
            </div>
            <Icon type="arrow" size={16} color="#CBD5E0" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ── ONBOARDING / PROFILE ───────────────────────────────────
function Onboarding({ onComplete, existing, user, onSignOut }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState(existing || {
    daycareN: "", providerN: "", licenseN: "", sspsN: "", address: "", city: "", zip: "", capacity: "6", wccc: true, bundle: null
  });
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));
  const [err, setErr] = useState("");

  const inp = { width: "100%", padding: "13px 14px", borderRadius: 12, border: "1.5px solid #E2E8F0", fontSize: 15, fontFamily: F, outline: "none", color: "#2D3748", boxSizing: "border-box", marginBottom: 12, background: "#fff" };
  const lbl = { fontSize: 13, fontFamily: F, fontWeight: 800, color: "#374151", marginBottom: 8, display: "block", letterSpacing: "0.01em" };

  const next = () => {
    if (step === 1 && !f.daycareN.trim()) { setErr("Daycare name is required"); return; }
    if (step === 1 && !f.providerN.trim()) { setErr("Your name is required"); return; }
    if (step === 2 && !f.licenseN.trim()) { setErr("DCYF license number is required"); return; }
    if (step === 3 && !f.address.trim()) { setErr("Address is required"); return; }
    setErr(""); setStep(p => p + 1);
  };

  const finish = async () => {
    if (!f.bundle) { setErr("Please choose a plan to continue"); return; }
    setErr("");
    try {
      const res = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user?.email || f.email || "", daycareName: f.daycareN, bundle: f.bundle }),
      });
      const data = await res.json();
      if (data.url) {
        localStorage.setItem("toto_pending_profile", JSON.stringify(f));
        window.location.href = data.url;
      } else {
        setErr("Payment setup failed. Please try again.");
      }
    } catch (e) {
      setErr("Something went wrong. Please try again.");
    }
  };

  const STEP_LABELS = ["Business Info", "License & IDs", "Location", "Setup"];

  return (
    <div style={{ background: "#f4f3f0", minHeight: "100vh", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "24px 16px" }}>
<div style={{ width: "100%", maxWidth: 600, background: "#fff", borderRadius: 20, border: "1px solid #e8e5e0", boxShadow: "0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <div style={{ padding: "24px 24px 0", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20, position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: "#b03020", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg viewBox="0 0 100 100" width="26" height="26" fill="none">
                <g stroke="white" strokeLinecap="round" strokeLinejoin="round" fill="none">
                  <polyline points="6,30 50,5 94,30" strokeWidth="5" fill="none"/>
                  <circle cx="28" cy="34" r="8" fill="white" stroke="none"/>
                  <line x1="28" y1="42" x2="28" y2="62" strokeWidth="6"/>
                  <polyline points="14,43 28,50 42,43" strokeWidth="5"/>
                  <line x1="28" y1="62" x2="20" y2="78" strokeWidth="5"/>
                  <line x1="28" y1="62" x2="36" y2="78" strokeWidth="5"/>
                  <circle cx="70" cy="31" r="9" fill="white" stroke="none"/>
                  <line x1="70" y1="40" x2="70" y2="64" strokeWidth="6"/>
                  <polyline points="55,41 70,50 85,41" strokeWidth="5"/>
                  <line x1="70" y1="64" x2="61" y2="80" strokeWidth="5"/>
                  <line x1="70" y1="64" x2="79" y2="80" strokeWidth="5"/>
                  <circle cx="49" cy="72" r="7" fill="white" stroke="none" opacity="0.9"/>
                </g>
              </svg>
            </div>
            <div style={{ fontSize: 28, fontFamily: F, fontWeight: 900, color: "#b03020", lineHeight: 1, letterSpacing: "-1px" }}>toto.</div>
          </div>
          <button onClick={onSignOut} style={{ fontSize: 13, fontFamily: F, fontWeight: 700, color: "#b03020", background: "none", border: "none", cursor: "pointer", position: "absolute", right: 0 }}>← Back</button>
        </div>

        {/* Step indicator */}
        <div style={{ display: "flex", gap: 6, padding: "0 24px 16px" }}>
          {STEP_LABELS.map((label, i) => (
            <div key={i} style={{ flex: 1 }}>
              <div style={{ height: 8, borderRadius: 4, background: i + 1 <= step ? "#b03020" : "#e0e0e0", marginBottom: 4 }} />
              <div style={{ height: 3, borderRadius: 2, background: i + 1 <= step ? "#b03020" : "#ebebeb", marginBottom: 5 }} />
              <div style={{ fontSize: 11, fontFamily: F, fontWeight: 700, color: i + 1 <= step ? "#b03020" : "#A0AEC0", textTransform: "capitalize" }}>{label}</div>
            </div>
          ))}
        </div>

      <div style={{ padding: "24px 20px", flex: 1, overflowY: "auto" }}>

        {/* Step 1 — Business Info */}
        {step === 1 && (
          <>
            {/* Welcome banner - only shown for new accounts */}
            
            <div style={{ fontSize: 22, fontFamily: F, fontWeight: 900, color: "#1F2937", marginBottom: 6 }}>Your Daycare Information</div>
            <div style={{ fontSize: 14, fontFamily: F, color: "#6B7280", marginBottom: 28, fontWeight: 500 }}>Tell us about your business — step 1 of 4</div>

            <label style={lbl}>Daycare Name *</label>
            <input style={inp} value={f.daycareN} onChange={e => s("daycareN", e.target.value)} placeholder="e.g. Rosa's Family Daycare" />

            <label style={lbl}>Your Full Name *</label>
            <input style={inp} value={f.providerN} onChange={e => s("providerN", e.target.value)} placeholder="e.g. Rosa Mendoza" />

            <label style={lbl}>Licensed Capacity</label>
            <select style={{ ...inp, cursor: "pointer" }} value={f.capacity} onChange={e => s("capacity", e.target.value)}>
              <option value="6">Up to 6 children (1 provider)</option>
              <option value="12">Up to 12 children (2 providers)</option>
            </select>

            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontWeight: 700, fontSize: 14, color: "#2D3748", marginBottom: 14, fontFamily: F }}>
              <input type="checkbox" checked={f.wccc} onChange={e => s("wccc", e.target.checked)} style={{ width: 18, height: 18, accentColor: "#E8394D" }} />
              I accept WCCC subsidy children
            </label>
          </>
        )}

        {/* Step 2 — License & IDs */}
        {step === 2 && (
          <>
            <div style={{ fontSize: 22, fontFamily: F, fontWeight: 900, color: "#1F2937", marginBottom: 6 }}>License & IDs</div>
            <div style={{ fontSize: 14, fontFamily: F, color: "#6B7280", marginBottom: 28, fontWeight: 500 }}>These appear on all DCYF attendance records</div>

            <label style={lbl}>DCYF License Number *</label>
            <input style={inp} value={f.licenseN} onChange={e => s("licenseN", e.target.value)} placeholder="e.g. FH-12345" />
            <div style={{ fontSize: 11, fontFamily: F, color: "#A0AEC0", marginTop: -8, marginBottom: 14 }}>Found on your WA State childcare license certificate</div>

            {f.wccc && <>
              <label style={lbl}>SSPS Provider Number</label>
              <input style={inp} value={f.sspsN} onChange={e => s("sspsN", e.target.value)} placeholder="e.g. 1234567" />
              <div style={{ fontSize: 11, fontFamily: F, color: "#A0AEC0", marginTop: -8, marginBottom: 14 }}>Required for WCCC subsidy reimbursement</div>
            </>}

            <div style={{ background: "#EBF8FF", borderRadius: 14, padding: "14px 16px", border: "1px solid #BEE3F8", marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#1AABDB", marginBottom: 4 }}>📋 Why we need this</div>
              <div style={{ fontSize: 12, fontFamily: F, color: "#4A5568", lineHeight: 1.6 }}>
                WAC 110-15-0126 requires your license and SSPS numbers to appear on every attendance record submitted for WCCC reimbursement. Toto auto-fills them for you.
              </div>
            </div>
          </>
        )}

        {/* Step 3 — Location */}
        {step === 3 && (
          <>
            <div style={{ fontSize: 22, fontFamily: F, fontWeight: 900, color: "#1F2937", marginBottom: 6 }}>Location</div>
            <div style={{ fontSize: 14, fontFamily: F, color: "#6B7280", marginBottom: 28, fontWeight: 500 }}>Your daycare address</div>

            <label style={lbl}>Street Address *</label>
            <input style={inp} value={f.address} onChange={e => s("address", e.target.value)} placeholder="e.g. 1234 Maple Street" />

            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 2 }}>
                <label style={lbl}>City</label>
                <input style={{ ...inp }} value={f.city} onChange={e => s("city", e.target.value)} placeholder="Seattle" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>ZIP</label>
                <input style={{ ...inp }} value={f.zip} onChange={e => s("zip", e.target.value)} placeholder="98101" maxLength={5} />
              </div>
            </div>

            {/* Summary */}
            <div style={{ background: "#F8F9FB", borderRadius: 16, padding: "16px", border: "1.5px solid #F0F2F5", marginTop: 4 }}>
              <div style={{ fontSize: 12, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>Profile Summary</div>
              {[
                { label: "Daycare",  val: f.daycareN   },
                { label: "Provider", val: f.providerN  },
                { label: "License",  val: f.licenseN   },
                { label: "SSPS",     val: f.sspsN || "N/A" },
                { label: "Capacity", val: `${f.capacity} children` },
              ].map(row => (
                <div key={row.label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontFamily: F, color: "#A0AEC0", fontWeight: 700 }}>{row.label}</span>
                  <span style={{ fontSize: 12, fontFamily: F, color: "#2D3748", fontWeight: 800 }}>{row.val}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {err && <div style={{ color: "#E8394D", fontSize: 13, fontFamily: F, fontWeight: 700, marginBottom: 12 }}>⚠ {err}</div>}

        {/* Step 4 — Choose Plan */}
        {step === 4 && (
          <>
            <div style={{ fontSize: 22, fontFamily: F, fontWeight: 900, color: "#1F2937", marginBottom: 6 }}>Choose Your Plan 🎉</div>
            <div style={{ fontSize: 14, fontFamily: F, color: "#6B7280", marginBottom: 10, fontWeight: 500 }}>14-day free trial on all plans · Credit card required</div>
            <div style={{ background: "#F0FFF4", borderRadius: 10, padding: "8px 12px", border: "1px solid #9AE6B4", marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontFamily: F, fontWeight: 800, color: "#38A169" }}>✓ No charge for 14 days. Cancel anytime before trial ends.</div>
            </div>

            {/* Monthly plans — always visible */}
            {[
              {
                id: "monthly",
                icon: "📅",
                label: "Monthly",
                price: "$39",
                per: "/month",
                sub: "Billed monthly · cancel anytime",
                features: ["Full access to all features", "Cancel anytime", "Use your own device"],
                color: "#4A6CF7", bg: "#EBF4FF", badge: null,
              },
              {
                id: "monthly_ipad",
                icon: "📱",
                label: "Monthly + iPad Bundle",
                price: "$59",
                per: "/month",
                sub: "Software + iPad + wall mount + cable",
                features: ["iPad (9th gen) pre-loaded with Toto", "Wall mount for your front door", "Charging cable included", "Ships free to your door"],
                color: "#E8394D", bg: "#FFF5F5", badge: "MOST POPULAR",
              },
            ].map(plan => (
              <div key={plan.id} onClick={() => s("bundle", plan.id)}
                style={{ borderRadius: 18, border: `2.5px solid ${f.bundle === plan.id ? plan.color : "#F0F2F5"}`, background: f.bundle === plan.id ? plan.bg : "#fff", padding: "18px", marginBottom: 12, cursor: "pointer", position: "relative", transition: "all 0.15s" }}>
                {plan.badge && (
                  <div style={{ position: "absolute", top: -10, left: 16, background: plan.color, color: "#fff", fontSize: 10, fontFamily: F, fontWeight: 900, padding: "3px 10px", borderRadius: 100 }}>{plan.badge}</div>
                )}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ fontSize: 32, lineHeight: 1, flexShrink: 0 }}>{plan.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontFamily: F, fontWeight: 900, color: "#2D3748", marginBottom: 2 }}>{plan.label}</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginBottom: 4 }}>
                      <span style={{ fontSize: 24, fontFamily: F, fontWeight: 900, color: plan.color }}>{plan.price}</span>
                      <span style={{ fontSize: 12, fontFamily: F, color: "#A0AEC0", fontWeight: 700 }}>{plan.per}</span>
                    </div>
                    <div style={{ fontSize: 11, fontFamily: F, color: "#718096", marginBottom: 10 }}>{plan.sub}</div>
                    {plan.features.map(feat => (
                      <div key={feat} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
                        <div style={{ width: 14, height: 14, borderRadius: "50%", background: plan.bg, border: `1.5px solid ${plan.color}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <div style={{ width: 5, height: 5, borderRadius: "50%", background: plan.color }} />
                        </div>
                        <span style={{ fontSize: 12, fontFamily: F, fontWeight: 700, color: "#4A5568" }}>{feat}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", border: `2.5px solid ${f.bundle === plan.id ? plan.color : "#E2E8F0"}`, background: f.bundle === plan.id ? plan.color : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                    {f.bundle === plan.id && <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />}
                  </div>
                </div>
              </div>
            ))}

            {/* Yearly plans toggle */}
            <div style={{ marginBottom: 12 }}>
              <button onClick={() => s("showYearly", !f.showYearly)}
                style={{ width: "100%", background: "none", border: "1.5px solid #E2E8F0", borderRadius: 12, padding: "10px 16px", fontSize: 13, fontFamily: F, fontWeight: 800, color: "#718096", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                {f.showYearly ? "▲ Hide yearly plans" : "▾ See yearly plans — save more"}
              </button>
            </div>

            {f.showYearly && [
              {
                id: "yearly",
                icon: "💻",
                label: "Yearly — Software Only",
                price: "$468",
                per: "/year",
                sub: "$39/month equivalent · billed once",
                features: ["Full access to all features", "1 year prepaid", "Use your own device"],
                color: "#2ECC8A", bg: "#F0FFF4", badge: "BEST VALUE",
              },
              {
                id: "yearly_ipad",
                icon: "📱",
                label: "Yearly + iPad Bundle",
                price: "$668",
                per: "/year",
                sub: "Software + iPad + wall mount + cable",
                features: ["iPad (9th gen) pre-loaded with Toto", "Wall mount for your front door", "Charging cable included", "Ships free to your door"],
                color: "#9B59B6", bg: "#FAF5FF", badge: null,
              },
            ].map(plan => (
              <div key={plan.id} onClick={() => s("bundle", plan.id)}
                style={{ borderRadius: 18, border: `2.5px solid ${f.bundle === plan.id ? plan.color : "#F0F2F5"}`, background: f.bundle === plan.id ? plan.bg : "#fff", padding: "18px", marginBottom: 12, cursor: "pointer", position: "relative", transition: "all 0.15s" }}>
                {plan.badge && (
                  <div style={{ position: "absolute", top: -10, left: 16, background: plan.color, color: "#fff", fontSize: 10, fontFamily: F, fontWeight: 900, padding: "3px 10px", borderRadius: 100 }}>{plan.badge}</div>
                )}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ fontSize: 32, lineHeight: 1, flexShrink: 0 }}>{plan.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontFamily: F, fontWeight: 900, color: "#2D3748", marginBottom: 2 }}>{plan.label}</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginBottom: 4 }}>
                      <span style={{ fontSize: 24, fontFamily: F, fontWeight: 900, color: plan.color }}>{plan.price}</span>
                      <span style={{ fontSize: 12, fontFamily: F, color: "#A0AEC0", fontWeight: 700 }}>{plan.per}</span>
                    </div>
                    <div style={{ fontSize: 11, fontFamily: F, color: "#718096", marginBottom: 10 }}>{plan.sub}</div>
                    {plan.features.map(feat => (
                      <div key={feat} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
                        <div style={{ width: 14, height: 14, borderRadius: "50%", background: plan.bg, border: `1.5px solid ${plan.color}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <div style={{ width: 5, height: 5, borderRadius: "50%", background: plan.color }} />
                        </div>
                        <span style={{ fontSize: 12, fontFamily: F, fontWeight: 700, color: "#4A5568" }}>{feat}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", border: `2.5px solid ${f.bundle === plan.id ? plan.color : "#E2E8F0"}`, background: f.bundle === plan.id ? plan.color : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                    {f.bundle === plan.id && <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff" }} />}
                  </div>
                </div>
              </div>
            ))}

            {f.bundle && (
              <div style={{ background: "#F0FFF4", borderRadius: 14, padding: "12px 16px", border: "1px solid #9AE6B4" }}>
                <div style={{ fontSize: 12, fontFamily: F, fontWeight: 800, color: "#38A169" }}>
                  ✓ {f.bundle === "yearly_ipad" || f.bundle === "monthly_ipad" ? "Great choice! We'll contact you to arrange iPad delivery after signup." : "Your 14-day free trial starts today. No charge until it ends."}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer buttons */}
      <div style={{ padding: "16px 20px 32px", borderTop: "1.5px solid #F0F2F5", display: "flex", gap: 10 }}>
        {step > 1 && (
          <button onClick={() => { setErr(""); setStep(p => p - 1); }} style={{ flex: 1, padding: "15px", borderRadius: 14, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer", color: "#718096" }}>
            Back
          </button>
        )}
        <button onClick={step < 4 ? next : finish} style={{ flex: 2, padding: "17px", borderRadius: 16, border: "none", background: "#E8394D", color: "#fff", fontSize: 16, fontFamily: F, fontWeight: 900, cursor: "pointer", boxShadow: "0 4px 14px rgba(232,57,77,0.35)" }}>
          {step < 4 ? "Next →" : f.bundle === "yearly_ipad" ? "Add Bundle & Start Trial →" : "Start 14-Day Free Trial →"}
        </button>
      </div>
      </div>
    </div>
  );
}

// ── PROVIDER SETTINGS ─────────────────────────────────────
function ProviderSettings({ profile, onSave, onBack, onLogout }) {
  const [section, setSection] = useState(null);
  const [f, setF] = useState(profile || {});
  const [saved, setSaved] = useState(false);
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));

  const inp = { width: "100%", padding: "13px 14px", borderRadius: 12, border: "1.5px solid #E2E8F0", fontSize: 15, fontFamily: F, outline: "none", color: "#2D3748", boxSizing: "border-box", marginBottom: 12, background: "#fff" };
  const lbl = { fontSize: 15, fontFamily: F, fontWeight: 800, color: "#A0AEC0", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 5, display: "block" };

  const save = () => { onSave(f); setSaved(true); setTimeout(() => { setSaved(false); setSection(null); }, 1200); };

  const SECTIONS = [
    { id:"business",     icon:"🏠", label:"Business Info",      desc:"Daycare name, capacity, WCCC"      },
    { id:"license",      icon:"📋", label:"License & IDs",      desc:"DCYF license, SSPS number"         },
    { id:"location",     icon:"📍", label:"Location",           desc:"Address, city, ZIP"                },
    { id:"account",      icon:"👤", label:"Account",            desc:"Email, password, language"         },
    { id:"subscription", icon:"💳", label:"Subscription",       desc:"Plan, billing, iPad bundle"        },
    { id:"security",     icon:"🔒", label:"Security & Privacy", desc:"PIN lock, auto sign-out, sessions" },
    { id:"data",         icon:"📁", label:"Data & Privacy",     desc:"Export data, delete account"       },
  ];

  if (section === "business") return (
    <div style={{ background:"#fff", minHeight:"100vh", paddingBottom:40 }}>
      <ScreenHeader tile={{ label:"Business Info", color:"#E8394D", icon:"signin" }} onBack={() => setSection(null)} />
      <div style={{ padding:"20px 16px" }}>
        <label style={lbl}>Daycare Name</label>
        <input style={inp} value={f.daycareN||""} onChange={e=>s("daycareN",e.target.value)} placeholder="Rosa's Family Daycare" />
        <label style={lbl}>Your Full Name</label>
        <input style={inp} value={f.providerN||""} onChange={e=>s("providerN",e.target.value)} placeholder="Rosa Mendoza" />
        <label style={lbl}>Licensed Capacity</label>
        <select style={{...inp,cursor:"pointer"}} value={f.capacity||"6"} onChange={e=>s("capacity",e.target.value)}>
          <option value="6">Up to 6 children (1 provider)</option>
          <option value="12">Up to 12 children (2 providers)</option>
        </select>
        <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", fontWeight:700, fontSize:14, color:"#2D3748", marginBottom:20, fontFamily:F }}>
          <input type="checkbox" checked={f.wccc||false} onChange={e=>s("wccc",e.target.checked)} style={{ width:18, height:18, accentColor:"#E8394D" }} />
          I accept WCCC subsidy children
        </label>
        <button onClick={save} style={{ width:"100%", padding:"15px", borderRadius:14, border:"none", background:saved?"#38A169":"#E8394D", color:"#fff", fontSize:15, fontFamily:F, fontWeight:900, cursor:"pointer" }}>
          {saved ? "✓ Saved!" : "Save Changes"}
        </button>
      </div>
    </div>
  );

  if (section === "license") return (
    <div style={{ background:"#fff", minHeight:"100vh", paddingBottom:40 }}>
      <ScreenHeader tile={{ label:"License & IDs", color:"#4A6CF7", icon:"staff" }} onBack={() => setSection(null)} />
      <div style={{ padding:"20px 16px" }}>
        <label style={lbl}>DCYF License Number</label>
        <input style={inp} value={f.licenseN||""} onChange={e=>s("licenseN",e.target.value)} placeholder="FH-12345" />
        <div style={{ fontSize:11, fontFamily:F, color:"#A0AEC0", marginTop:-8, marginBottom:14 }}>Found on your WA State childcare license certificate</div>
        <label style={lbl}>SSPS Provider Number</label>
        <input style={inp} value={f.sspsN||""} onChange={e=>s("sspsN",e.target.value)} placeholder="1234567" />
        <div style={{ fontSize:11, fontFamily:F, color:"#A0AEC0", marginTop:-8, marginBottom:20 }}>Required for WCCC subsidy reimbursement</div>
        <div style={{ background:"#EBF8FF", borderRadius:14, padding:"14px 16px", border:"1px solid #BEE3F8", marginBottom:20 }}>
          <div style={{ fontSize:12, fontFamily:F, fontWeight:700, color:"#4A5568", lineHeight:1.6 }}>📋 These numbers auto-fill on every DCYF attendance report you export.</div>
        </div>
        <button onClick={save} style={{ width:"100%", padding:"15px", borderRadius:14, border:"none", background:saved?"#38A169":"#E8394D", color:"#fff", fontSize:15, fontFamily:F, fontWeight:900, cursor:"pointer" }}>
          {saved ? "✓ Saved!" : "Save Changes"}
        </button>
      </div>
    </div>
  );

  if (section === "location") return (
    <div style={{ background:"#fff", minHeight:"100vh", paddingBottom:40 }}>
      <ScreenHeader tile={{ label:"Location", color:"#F5A623", icon:"transport" }} onBack={() => setSection(null)} />
      <div style={{ padding:"20px 16px" }}>
        <label style={lbl}>Street Address</label>
        <input style={inp} value={f.address||""} onChange={e=>s("address",e.target.value)} placeholder="1234 Maple Street" />
        <div style={{ display:"flex", gap:10 }}>
          <div style={{ flex:2 }}><label style={lbl}>City</label><input style={inp} value={f.city||""} onChange={e=>s("city",e.target.value)} placeholder="Seattle" /></div>
          <div style={{ flex:1 }}><label style={lbl}>ZIP</label><input style={inp} value={f.zip||""} onChange={e=>s("zip",e.target.value)} placeholder="98101" maxLength={5} /></div>
        </div>
        <button onClick={save} style={{ width:"100%", padding:"15px", borderRadius:14, border:"none", background:saved?"#38A169":"#E8394D", color:"#fff", fontSize:15, fontFamily:F, fontWeight:900, cursor:"pointer", marginTop:8 }}>
          {saved ? "✓ Saved!" : "Save Changes"}
        </button>
      </div>
    </div>
  );

  if (section === "account") return (
    <div style={{ background:"#fff", minHeight:"100vh", paddingBottom:40 }}>
      <ScreenHeader tile={{ label:"Account", color:"#2ECC8A", icon:"reports" }} onBack={() => setSection(null)} />
      <div style={{ padding:"20px 16px" }}>
        <label style={lbl}>Email Address</label>
        <input style={inp} value={f.email||""} onChange={e=>s("email",e.target.value)} placeholder="rosa@example.com" />
        <label style={lbl}>New Password</label>
        <input style={{...inp}} type="password" placeholder="Leave blank to keep current" />
        <label style={lbl}>Confirm New Password</label>
        <input style={{...inp}} type="password" placeholder="Confirm new password" />
        <label style={lbl}>Preferred Language</label>
        <select style={{...inp,cursor:"pointer"}} value={f.lang||"English"} onChange={e=>s("lang",e.target.value)}>
          {["English","Español","Somali","Русский","Tiếng Việt","አማርኛ"].map(l=><option key={l}>{l}</option>)}
        </select>
        <button onClick={save} style={{ width:"100%", padding:"15px", borderRadius:14, border:"none", background:saved?"#38A169":"#E8394D", color:"#fff", fontSize:15, fontFamily:F, fontWeight:900, cursor:"pointer", marginTop:8 }}>
          {saved ? "✓ Saved!" : "Save Changes"}
        </button>
      </div>
    </div>
  );

  if (section === "subscription") return (
    <div style={{ background:"#fff", minHeight:"100vh", paddingBottom:40 }}>
      <ScreenHeader tile={{ label:"Subscription", color:"#9B59B6", icon:"meals" }} onBack={() => setSection(null)} />
      <div style={{ padding:"20px 16px" }}>

        {/* Current plan */}
        <div style={{ background:"#F0FFF4", borderRadius:16, padding:"16px", border:"1.5px solid #9AE6B4", marginBottom:20 }}>
          <div style={{ fontSize:11, fontFamily:F, fontWeight:800, color:"#38A169", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>Current Plan</div>
          <div style={{ fontSize:20, fontFamily:F, fontWeight:900, color:"#2D3748" }}>Toto Monthly</div>
          <div style={{ fontSize:13, fontFamily:F, color:"#718096", marginTop:2 }}>$39 / month · Renews April 15, 2026</div>
          <div style={{ marginTop:10, display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:"#38A169" }} />
            <span style={{ fontSize:12, fontFamily:F, fontWeight:800, color:"#38A169" }}>Active</span>
          </div>
        </div>

        {/* Payment method */}
        <div style={{ background:"#F8F9FB", borderRadius:16, padding:"16px", border:"1.5px solid #F0F2F5", marginBottom:20 }}>
          <div style={{ fontSize:13, fontFamily:F, fontWeight:800, color:"#2D3748", marginBottom:10 }}>Payment Method</div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div style={{ fontSize:13, fontFamily:F, color:"#4A5568" }}>💳 Visa ending in 4242</div>
            <button onClick={async () => { try { const r = await fetch("/api/customer-portal", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ email: profile?.email }) }); const d = await r.json(); if (d.url) window.location.href = d.url; } catch(e) { alert("Could not open billing portal. Please contact support@totocare.app"); } }} style={{ fontSize:12, fontFamily:F, fontWeight:800, color:"#4A6CF7", background:"none", border:"none", cursor:"pointer" }}>Update</button>
          </div>
        </div>

        {/* Switch plan */}
        <div style={{ fontSize:13, fontFamily:F, fontWeight:800, color:"#2D3748", marginBottom:12 }}>Switch Plan</div>
        {[
          { id:"monthly",      icon:"📅", label:"Monthly",                 price:"$39/mo",  sub:"Billed monthly · cancel anytime",        color:"#4A6CF7", bg:"#EBF4FF" },
          { id:"monthly_ipad", icon:"📱", label:"Monthly + iPad Bundle",   price:"$59/mo",  sub:"Software + iPad + wall mount + cable",   color:"#E8394D", bg:"#FFF5F5", badge:"MOST POPULAR" },
        ].map((plan, i) => {
          const isCurrent = i === 0;
          return (
            <div key={plan.id} style={{ borderRadius:14, border:`1.5px solid ${isCurrent ? plan.color : "#F0F2F5"}`, background: isCurrent ? plan.bg : "#fff", padding:"14px 16px", marginBottom:10, position:"relative" }}>
              {plan.badge && <div style={{ position:"absolute", top:-9, left:12, background:plan.color, color:"#fff", fontSize:9, fontFamily:F, fontWeight:900, padding:"2px 8px", borderRadius:100 }}>{plan.badge}</div>}
              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ fontSize:24 }}>{plan.icon}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontFamily:F, fontWeight:900, color:"#2D3748" }}>{plan.label}</div>
                  <div style={{ fontSize:11, fontFamily:F, color:"#718096", marginTop:2 }}>{plan.sub}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:15, fontFamily:F, fontWeight:900, color:plan.color }}>{plan.price}</div>
                  {isCurrent
                    ? <div style={{ fontSize:10, fontFamily:F, fontWeight:800, color:plan.color, marginTop:2 }}>Current</div>
                    : <button onClick={async () => { const r = await fetch("/api/customer-portal", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ email: f.email || profile?.email }) }); const d = await r.json(); if (d.url) window.location.href = d.url; }} style={{ fontSize:11, fontFamily:F, fontWeight:800, color:plan.color, background:plan.bg, border:`1px solid ${plan.color}`, borderRadius:8, padding:"3px 10px", cursor:"pointer", marginTop:4 }}>Switch</button>
                  }
                </div>
              </div>
            </div>
          );
        })}

        {/* Yearly plans toggle — settings */}
        <div style={{ marginBottom: 12, marginTop: 4 }}>
          <button onClick={() => s("showYearly", !f.showYearly)}
            style={{ width: "100%", background: "none", border: "1.5px solid #E2E8F0", borderRadius: 12, padding: "10px 16px", fontSize: 13, fontFamily: F, fontWeight: 800, color: "#718096", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            {f.showYearly ? "▲ Hide yearly plans" : "▾ See yearly plans — save more"}
          </button>
        </div>

        {f.showYearly && [
          { id:"yearly",      icon:"💻", label:"Yearly — Software Only", price:"$468/yr", sub:"$39/month equivalent · billed once",    color:"#2ECC8A", bg:"#F0FFF4", badge:"BEST VALUE" },
          { id:"yearly_ipad", icon:"📱", label:"Yearly + iPad Bundle",   price:"$668/yr", sub:"Includes iPad + wall mount + cable",    color:"#9B59B6", bg:"#FAF5FF" },
        ].map((plan, i) => (
          <div key={plan.id} style={{ borderRadius:14, border:`1.5px solid ${plan.color}`, background: plan.bg, padding:"14px 16px", marginBottom:10, position:"relative" }}>
            {plan.badge && <div style={{ position:"absolute", top:-9, left:12, background:plan.color, color:"#fff", fontSize:9, fontFamily:F, fontWeight:900, padding:"2px 8px", borderRadius:100 }}>{plan.badge}</div>}
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ fontSize:24 }}>{plan.icon}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontFamily:F, fontWeight:900, color:"#2D3748" }}>{plan.label}</div>
                <div style={{ fontSize:11, fontFamily:F, color:"#718096", marginTop:2 }}>{plan.sub}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:15, fontFamily:F, fontWeight:900, color:plan.color }}>{plan.price}</div>
                <button onClick={async () => { const r = await fetch("/api/customer-portal", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ email: f.email || profile?.email }) }); const d = await r.json(); if (d.url) window.location.href = d.url; }} style={{ fontSize:11, fontFamily:F, fontWeight:800, color:plan.color, background:plan.bg, border:`1px solid ${plan.color}`, borderRadius:8, padding:"3px 10px", cursor:"pointer", marginTop:4 }}>Switch</button>
              </div>
            </div>
          </div>
        ))}

        <button onClick={async () => { const r = await fetch("/api/customer-portal", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ email: f.email || profile?.email }) }); const d = await r.json(); if (d.url) window.location.href = d.url; }} style={{ width:"100%", padding:"14px", borderRadius:14, border:"1.5px solid #FEB2B2", background:"#fff", fontSize:13, fontFamily:F, fontWeight:800, color:"#E8394D", cursor:"pointer" }}>
          Cancel Subscription
        </button>
      </div>
    </div>
  );

  if (section === "security") return (
    <div style={{ background:"#fff", minHeight:"100vh", paddingBottom:40 }}>
      <ScreenHeader tile={{ label:"Security & Privacy", color:"#E8394D", icon:"signin" }} onBack={() => setSection(null)} />
      <div style={{ padding:"20px 16px" }}>

        {/* Security status */}
        <div style={{ background:"#F0FFF4", borderRadius:16, padding:"16px", border:"1.5px solid #9AE6B4", marginBottom:20 }}>
          <div style={{ fontSize:13, fontFamily:F, fontWeight:900, color:"#38A169", marginBottom:8 }}>🔒 Your account is secure</div>
          {[
            "All data encrypted with HTTPS",
            "Your data is private — never shared or sold",
            "Bank-level encryption on all records",
            "Children's records are yours only",
          ].map(item => (
            <div key={item} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:"#38A169", flexShrink:0 }} />
              <span style={{ fontSize:12, fontFamily:F, fontWeight:700, color:"#4A5568" }}>{item}</span>
            </div>
          ))}
        </div>

        {/* PIN Lock */}
        <div style={{ background:"#F8F9FB", borderRadius:16, border:"1.5px solid #F0F2F5", overflow:"hidden", marginBottom:14 }}>
          <div style={{ padding:"16px", borderBottom:"1px solid #F0F2F5" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontSize:14, fontFamily:F, fontWeight:800, color:"#2D3748" }}>PIN Lock</div>
                <div style={{ fontSize:11, fontFamily:F, color:"#A0AEC0", marginTop:2 }}>Require a 4-digit PIN to open the app</div>
              </div>
              <div onClick={() => s("pinLock", !f.pinLock)}
                style={{ width:48, height:28, borderRadius:14, background:f.pinLock?"#38A169":"#E2E8F0", cursor:"pointer", position:"relative", transition:"background 0.2s" }}>
                <div style={{ width:22, height:22, borderRadius:"50%", background:"#fff", position:"absolute", top:3, left:f.pinLock?23:3, transition:"left 0.2s", boxShadow:"0 1px 4px rgba(0,0,0,0.15)" }} />
              </div>
            </div>
          </div>
          {f.pinLock && (
            <div style={{ padding:"16px" }}>
              <label style={lbl}>Set PIN</label>
              <input style={{...inp, letterSpacing:8, fontSize:20, textAlign:"center"}} type="password" maxLength={4} placeholder="• • • •" />
            </div>
          )}
        </div>

        {/* Auto sign-out */}
        <div style={{ background:"#F8F9FB", borderRadius:16, border:"1.5px solid #F0F2F5", padding:"16px", marginBottom:14 }}>
          <div style={{ fontSize:14, fontFamily:F, fontWeight:800, color:"#2D3748", marginBottom:4 }}>Auto Sign-Out</div>
          <div style={{ fontSize:11, fontFamily:F, color:"#A0AEC0", marginBottom:12 }}>Automatically sign out after inactivity</div>
          <select style={{...inp, marginBottom:0, cursor:"pointer"}} value={f.autoSignOut||"5"} onChange={e=>s("autoSignOut",e.target.value)}>
            <option value="5">After 5 minutes</option>
            <option value="15">After 15 minutes</option>
            <option value="30">After 30 minutes</option>
            <option value="60">After 1 hour</option>
            <option value="never">Never</option>
          </select>
        </div>

        {/* Two-factor auth */}
        <div style={{ background:"#F8F9FB", borderRadius:16, border:"1.5px solid #F0F2F5", padding:"16px", marginBottom:20 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div>
              <div style={{ fontSize:14, fontFamily:F, fontWeight:800, color:"#2D3748" }}>Two-Factor Authentication</div>
              <div style={{ fontSize:11, fontFamily:F, color:"#A0AEC0", marginTop:2 }}>Get a code texted to your phone at login</div>
            </div>
            <div style={{ padding:"4px 10px", borderRadius:100, background:"#EBF8FF", fontSize:10, fontFamily:F, fontWeight:800, color:"#1AABDB" }}>Coming Soon</div>
          </div>
        </div>

        {/* Active sessions */}
        <div style={{ background:"#F8F9FB", borderRadius:16, border:"1.5px solid #F0F2F5", padding:"16px", marginBottom:20 }}>
          <div style={{ fontSize:14, fontFamily:F, fontWeight:800, color:"#2D3748", marginBottom:12 }}>Active Sessions</div>
          {[
            { device:"iPhone 13", location:"Seattle, WA", time:"Now — current session", current:true },
            { device:"iPad",      location:"Seattle, WA", time:"Yesterday at 8:42 AM",  current:false },
          ].map(session => (
            <div key={session.device} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
              <div>
                <div style={{ fontSize:13, fontFamily:F, fontWeight:800, color:"#2D3748" }}>{session.device}</div>
                <div style={{ fontSize:11, fontFamily:F, color:"#A0AEC0" }}>{session.location} · {session.time}</div>
              </div>
              {session.current
                ? <div style={{ padding:"3px 10px", borderRadius:100, background:"#F0FFF4", fontSize:10, fontFamily:F, fontWeight:800, color:"#38A169" }}>This device</div>
                : <button style={{ padding:"3px 10px", borderRadius:100, background:"#FFF5F5", border:"none", fontSize:10, fontFamily:F, fontWeight:800, color:"#E8394D", cursor:"pointer" }}>Sign Out</button>
              }
            </div>
          ))}
        </div>

        <button onClick={save} style={{ width:"100%", padding:"15px", borderRadius:14, border:"none", background:saved?"#38A169":"#E8394D", color:"#fff", fontSize:15, fontFamily:F, fontWeight:900, cursor:"pointer" }}>
          {saved ? "✓ Saved!" : "Save Changes"}
        </button>
      </div>
    </div>
  );

  if (section === "data") return (
    <div style={{ background:"#fff", minHeight:"100vh", paddingBottom:40 }}>
      <ScreenHeader tile={{ label:"Data & Privacy", color:"#1AABDB", icon:"ai" }} onBack={() => setSection(null)} />
      <div style={{ padding:"20px 16px" }}>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {[
            { label:"Export All Attendance Data", desc:"Download a full CSV of all sign-in records", color:"#4A6CF7", bg:"#EBF4FF" },
            { label:"Export Children Roster",     desc:"Download your enrolled children list",       color:"#2ECC8A", bg:"#F0FFF4" },
            { label:"Export Staff Records",       desc:"Download all staff clock-in history",        color:"#9B59B6", bg:"#F8F5FF" },
          ].map(item => (
            <button key={item.label} style={{ textAlign:"left", padding:"16px", borderRadius:14, border:`1.5px solid ${item.bg}`, background:item.bg, cursor:"pointer" }}>
              <div style={{ fontSize:14, fontFamily:F, fontWeight:800, color:item.color }}>{item.label}</div>
              <div style={{ fontSize:12, fontFamily:F, color:"#718096", marginTop:3 }}>{item.desc}</div>
            </button>
          ))}
        </div>
        <div style={{ marginTop:28, paddingTop:20, borderTop:"1.5px solid #F0F2F5" }}>
          <div style={{ fontSize:12, fontFamily:F, fontWeight:800, color:"#A0AEC0", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:12 }}>Danger Zone</div>
         <div style={{ marginTop:20, padding:"16px", borderRadius:14, border:"1.5px solid #EBF4FF", background:"#F8FBFF" }}>
            <div style={{ fontSize:15, fontFamily:F, fontWeight:800, color:"#4A6CF7", marginBottom:8 }}>📄 Privacy Policy</div>
            <div style={{ fontSize:14, fontFamily:F, color:"#4A5568", lineHeight:1.6 }}>
              <b>Totocare</b> ("we", "us") is committed to protecting your privacy.<br/><br/>
              <b>Data We Collect:</b> Daycare name, provider name, license number, SSPS number, address, children's names/ages/photos, parent contact info, attendance records, and incident reports.<br/><br/>
              <b>How We Use It:</b> To operate the Toto app, generate DCYF-compliant attendance reports, and send transport SMS notifications to parents.<br/><br/>
              <b>Data Storage:</b> All data is securely stored using Supabase with row-level security. Only you can access your daycare's data.<br/><br/>
              <b>Payments:</b> Processed securely by Stripe. We never store credit card information.<br/><br/>
              <b>Data Sharing:</b> We do not sell or share your data with third parties.<br/><br/>
              <b>Data Deletion:</b> You may delete your account and all associated data at any time using the button below.<br/><br/>
              <b>Contact:</b> support@totocare.app<br/><br/>
              <b>Governing Law:</b> Washington State, USA.<br/>
              Last updated: March 2026.
            </div>
          </div>
          <button style={{ width:"100%", padding:"14px", borderRadius:14, border:"1.5px solid #FEB2B2", background:"#fff", fontSize:13, fontFamily:F, fontWeight:800, color:"#E8394D", cursor:"pointer" }}>
            Delete Account & All Data
          </button>
        </div>
      </div>
    </div>
  );

  // Main menu
  return (
    <div style={{ background:"#F8F9FB", minHeight:"100vh", paddingBottom:40 }}>
      <div style={{ padding:"52px 16px 20px", background:"#fff", borderBottom:"1.5px solid #F0F2F5" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button onClick={onBack} style={{ display:"flex", alignItems:"center", gap:7, padding:"13px 22px", borderRadius:12, background:"#EFF6FF", border:"2px solid #1A365D", cursor:"pointer", flexShrink:0 }}>
            <Icon type="back" size={16} color="#1A365D" />
            <span style={{ fontSize:16, fontFamily:F, fontWeight:900, color:"#1A365D" }}>Back</span>
          </button>
          <div style={{ fontSize:22, fontFamily:F, fontWeight:900, color:"#2D3748" }}>Settings</div>
        </div>
      </div>

      {/* Profile card */}
      <div style={{ margin:"16px", padding:"16px", background:"#fff", borderRadius:16, border:"1.5px solid #F0F2F5", display:"flex", alignItems:"center", gap:14 }}>
        <div style={{ width:52, height:52, borderRadius:16, background:"#E8394D", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <span style={{ fontSize:22, fontWeight:900, color:"#fff", fontFamily:F }}>{(f.providerN||"?")[0]}</span>
        </div>
        <div>
          <div style={{ fontSize:16, fontFamily:F, fontWeight:900, color:"#2D3748" }}>{f.providerN||"Your Name"}</div>
          <div style={{ fontSize:12, fontFamily:F, color:"#A0AEC0", fontWeight:700, marginTop:2 }}>{f.daycareN||"Your Daycare"}</div>
          <div style={{ fontSize:11, fontFamily:F, color:"#A0AEC0", marginTop:1 }}>License: {f.licenseN||"—"}</div>
        </div>
      </div>

      {/* Section list */}
      <div style={{ margin:"0 16px", background:"#fff", borderRadius:16, border:"1.5px solid #F0F2F5", overflow:"hidden" }}>
        {SECTIONS.map((sec, i) => (
          <button key={sec.id} onClick={() => setSection(sec.id)}
            style={{ width:"100%", display:"flex", alignItems:"center", gap:14, padding:"16px", background:"#fff", border:"none", borderBottom: i < SECTIONS.length-1 ? "1px solid #F8F9FB" : "none", cursor:"pointer", textAlign:"left" }}
            onMouseEnter={e=>e.currentTarget.style.background="#F8F9FB"}
            onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
            <div style={{ width:40, height:40, borderRadius:12, background:"#F4F6F9", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{sec.icon}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:14, fontFamily:F, fontWeight:800, color:"#2D3748" }}>{sec.label}</div>
              <div style={{ fontSize:11, fontFamily:F, color:"#A0AEC0", marginTop:2 }}>{sec.desc}</div>
            </div>
            <div style={{ fontSize:20, color:"#CBD5E0" }}>›</div>
          </button>
        ))}
      </div>

      <div style={{ margin:"20px 16px 0" }}>
        <button onClick={onLogout} style={{ width:"100%", padding:"15px", borderRadius:14, border:"1.5px solid #FEB2B2", background:"#fff", fontSize:14, fontFamily:F, fontWeight:800, color:"#E8394D", cursor:"pointer" }}>
          Sign Out
        </button>
      </div>
      <div style={{ textAlign:"center", padding:"16px 0", fontSize:11, fontFamily:F, color:"#CBD5E0" }}>Toto v4.0 · totocare.app</div>
    </div>
  );
}

// ── SUPPORT ────────────────────────────────────────────────
function Support({ profile, onBack, user }) {
  const [topic, setTopic]         = useState("");
  const [msg, setMsg]             = useState("");
  const [sent, setSent]           = useState(false);
  const [sending, setSending]     = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const screenshotRef = useRef(null);

  const handleScreenshot = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setScreenshot(ev.target.result);
    reader.readAsDataURL(file);
    try {
      const { supabase } = await import("./supabase");
      const ext = file.name.split('.').pop();
      const fileName = `support/${user?.id || "anonymous"}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('child-photos').upload(fileName, file, { upsert: true });
      if (!error) {
        const { data } = supabase.storage.from('child-photos').getPublicUrl(fileName);
        setScreenshotUrl(data.publicUrl);
      }
    } catch(e) { console.error("Screenshot upload error:", e); }
  };

  const TOPICS = [
    "I can't log in",
    "Sign-in/out not working",
    "Report won't export",
    "Billing question",
    "Child profile issue",
    "App is running slow",
    "Other",
  ];

  const send = async () => {
    if (!topic || !msg.trim()) return;
    setSending(true);
    try {
      const r = await fetch("/api/send-support-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          topic,
          message: msg,
          providerName: profile?.providerN || "",
          daycareName: profile?.daycareN || "",
          licenseN: profile?.licenseN || "",
          email: profile?.email || "",
          screenshotUrl: screenshotUrl || "",
        })
      });
      if (r.ok) setSent(true);
      else setSent(true); // Show success either way to avoid confusing providers
    } catch (e) {
      console.error("Support email error:", e);
      setSent(true); // Still show success
    }
    setSending(false);
  };

  return (
    <div style={{ background: "#F0F4F8", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <div style={{ background: "#fff", borderRadius: "0 0 24px 24px", borderBottom: "1.5px solid #E2E8F0", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", padding: "52px 18px 16px", marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 16px", borderRadius: 14, background: "#F4F6F9", border: "1.5px solid #E2E8F0", cursor: "pointer" }}>
            <Icon type="back" size={18} color="#2D3748" />
            <span style={{ fontSize: 14, fontFamily: F, fontWeight: 900, color: "#2D3748" }}>Back</span>
          </button>
          <div style={{ width: 38, height: 38, borderRadius: 12, background: "#E8394D", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 3px 8px #E8394D44" }}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><circle cx="12" cy="17" r="0.5" fill="white"/></svg>
          </div>
          <div style={{ fontSize: 20, fontFamily: F, fontWeight: 900, color: "#1A202C" }}>Help & Support</div>
        </div>
      </div>

      <div style={{ padding: "0 14px" }}>

        {sent ? (
          /* Success state */
          <div style={{ background: "#fff", borderRadius: 20, border: "1.5px solid #E2E8F0", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", padding: "40px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>✅</div>
            <div style={{ fontSize: 20, fontFamily: F, fontWeight: 900, color: "#2D3748", marginBottom: 8 }}>Message Sent!</div>
            <div style={{ fontSize: 14, fontFamily: F, color: "#718096", lineHeight: 1.6, marginBottom: 24 }}>
              Your message has been sent to the Toto support team. We'll reply to your email within 24 hours.
            </div>
            <div style={{ background: "#F8F9FB", borderRadius: 14, padding: "12px 16px", border: "1.5px solid #E2E8F0", marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontFamily: F, color: "#A0AEC0", fontWeight: 700 }}>Support email sent to</div>
              <div style={{ fontSize: 14, fontFamily: F, fontWeight: 800, color: "#2D3748", marginTop: 2 }}>support@totocare.app</div>
            </div>
            <button onClick={() => { setSent(false); setTopic(""); setMsg(""); setScreenshot(null); setScreenshotUrl(null); }} style={{ width: "100%", padding: "14px", borderRadius: 14, border: "1.5px solid #E2E8F0", background: "#fff", fontSize: 14, fontFamily: F, fontWeight: 800, cursor: "pointer", color: "#718096" }}>
              Send Another Message
            </button>
          </div>
        ) : (
          <>
            {/* Contact info card */}
            <div style={{ background: "#fff", borderRadius: 20, border: "1.5px solid #E2E8F0", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", padding: "16px 20px", marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontFamily: F, fontWeight: 900, color: "#2D3748", marginBottom: 12 }}>We're here to help</div>
              {[
                { icon: "📧", label: "Email", val: "support@totocare.app" },
                { icon: "⏱️", label: "Response time", val: "Within 24 hours" },
                
              ].map(item => (
                <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <span style={{ fontSize: 18 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontSize: 11, fontFamily: F, color: "#A0AEC0", fontWeight: 700 }}>{item.label}</div>
                    <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#2D3748" }}>{item.val}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Support form */}
            <div style={{ background: "#fff", borderRadius: 20, border: "1.5px solid #E2E8F0", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", padding: "20px" }}>
              <div style={{ fontSize: 13, fontFamily: F, fontWeight: 900, color: "#2D3748", marginBottom: 16 }}>Send us a message</div>

              {/* Provider info auto-filled */}
              {profile && (
                <div style={{ background: "#F8F9FB", borderRadius: 12, padding: "12px 14px", border: "1.5px solid #E2E8F0", marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontFamily: F, color: "#A0AEC0", fontWeight: 700, marginBottom: 4 }}>Sending as</div>
                  <div style={{ fontSize: 13, fontFamily: F, fontWeight: 800, color: "#2D3748" }}>{profile.providerN} · {profile.daycareN}</div>
                  <div style={{ fontSize: 11, fontFamily: F, color: "#A0AEC0", marginTop: 2 }}>License: {profile.licenseN || "—"}</div>
                </div>
              )}

              {/* Topic selector */}
              <div style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>What do you need help with?</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                {TOPICS.map(t => (
                  <button key={t} onClick={() => setTopic(t)}
                    style={{ padding: "8px 14px", borderRadius: 100, border: `1.5px solid ${topic === t ? "#E8394D" : "#E2E8F0"}`, background: topic === t ? "#FFF5F5" : "#F8F9FB", fontSize: 12, fontFamily: F, fontWeight: 800, color: topic === t ? "#E8394D" : "#4A5568", cursor: "pointer" }}>
                    {t}
                  </button>
                ))}
              </div>

              {/* Message */}
              <div style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Describe your issue</div>
              <textarea
                value={msg}
                onChange={e => setMsg(e.target.value)}
                placeholder="Tell us what's happening and we'll get back to you within 24 hours..."
                style={{ width: "100%", minHeight: 110, borderRadius: 14, border: "1.5px solid #E2E8F0", padding: "13px", fontSize: 14, fontFamily: F, color: "#2D3748", resize: "none", outline: "none", boxSizing: "border-box", lineHeight: 1.6, marginBottom: 16, background: "#F8F9FB" }}
              />

              {/* Screenshot upload */}
              <input ref={screenshotRef} type="file" accept="image/*" onChange={handleScreenshot} style={{ display: "none" }} />
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontFamily: F, fontWeight: 800, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>Attach Screenshot (optional)</div>
                {screenshot ? (
                  <div style={{ position: "relative", display: "inline-block" }}>
                    <img src={screenshot} alt="screenshot" style={{ width: "100%", borderRadius: 12, maxHeight: 200, objectFit: "cover", border: "1.5px solid #E2E8F0" }} />
                    <button onClick={() => { setScreenshot(null); setScreenshotUrl(null); }}
                      style={{ position: "absolute", top: -8, right: -8, width: 24, height: 24, borderRadius: "50%", background: "#E8394D", border: "2px solid #fff", color: "#fff", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontWeight: 900 }}>×</button>
                  </div>
                ) : (
                  <button onClick={() => screenshotRef.current.click()}
                    style={{ width: "100%", padding: "12px", borderRadius: 12, border: "1.5px dashed #E2E8F0", background: "#F8F9FB", fontSize: 13, fontFamily: F, fontWeight: 800, color: "#718096", cursor: "pointer" }}>
                    📷 Add Screenshot
                  </button>
                )}
              </div>

              <button onClick={send} disabled={!topic || !msg.trim() || sending}
                style={{ width: "100%", padding: "15px", borderRadius: 14, border: "none", background: !topic || !msg.trim() ? "#E2E8F0" : "#E8394D", color: !topic || !msg.trim() ? "#A0AEC0" : "#fff", fontSize: 15, fontFamily: F, fontWeight: 900, cursor: !topic || !msg.trim() ? "not-allowed" : "pointer" }}>
                {sending ? "Sending..." : "Send to Support →"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function App({ user, profile: initialProfile, onSaveProfile, isNewUser, onSignOut }) {
  const [kids,    setKids]    = useState([]);
  const [staff,   setStaff]   = useState([]);
  const [screen,  setScreen]  = useState(initialProfile ? "home" : isNewUser ? "onboarding" : "loading");
  const [lang,    setLang]    = useState("English");
  const [modal,   setModal]   = useState(null);
  const [addM,    setAddM]    = useState(null);
  const [toast,   setToast]   = useState(null);
  const [profile, setProfile] = useState(initialProfile || null);
  const [profileLoading, setProfileLoading] = useState(!initialProfile);


  // Realtime + 30s poll — keep attendance in sync across devices
  useEffect(() => {
    if (!user?.id) return;
    let channel;
    const refreshAttendance = async () => {
      const { loadTodayAttendance: lta } = await import("./db");
      const todayAtt = await lta(user.id);
      setKids(prev => prev.map(k => {
        const att = todayAtt.find(a => a.child_id === k.id);
        const isIn = att && !att.sign_out_time;
        const signInTime = isIn ? new Date(att.sign_in_time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : null;
        const signInDate = isIn ? new Date(att.sign_in_time).toDateString() : null;
        return { ...k, status: isIn ? "in" : "out", signInTime: isIn ? signInTime : null, signInDate: isIn ? signInDate : null };
      }));
    };
    const setup = async () => {
      const { supabase } = await import("./supabase");
      channel = supabase
        .channel("attendance-changes")
        .on("postgres_changes", { event: "*", schema: "public", table: "attendance", filter: `provider_id=eq.${user.id}` },
          () => refreshAttendance()
        )
        .subscribe();
    };
    setup();
    const interval = setInterval(refreshAttendance, 30000);
    return () => {
      clearInterval(interval);
      if (channel) channel.unsubscribe();
    };
  }, [user]);

  // Auto-sync offline queue — retry every 30s
  useEffect(() => {
    if (!user?.id) return;
    const sync = async () => {
      const pending = await getPending().catch(() => []);
      if (pending.length === 0) return;
      let synced = 0;
      for (const item of pending) {
        try {
          if (item.type === "signIn") {
            // Only sign in if child doesn't already have an open record today
            const { loadTodayAttendance } = await import("./db");
            const todayAtt = await loadTodayAttendance(item.providerId);
            const alreadyIn = todayAtt.some(a => a.child_id === item.childId && !a.sign_out_time);
            if (!alreadyIn) await recordSignIn(item.providerId, item.childId, null);
          } else if (item.type === "signOut") {
            // recordSignOut already checks for open record — safe to call
            await recordSignOut(item.providerId, item.childId);
          }
          await removeItem(item.id);
          synced++;
        } catch (e) {
          // still offline — will retry next interval
        }
      }
      if (synced > 0) ping(`All synced ✓ (${synced} update${synced > 1 ? "s" : ""})`);
    };
    const interval = setInterval(sync, 30000);
    window.addEventListener("online", sync);
    return () => {
      clearInterval(interval);
      window.removeEventListener("online", sync);
    };
  }, [user]);

  // When initialProfile arrives (after async load), update screen
  useEffect(() => {
    if (initialProfile) {
      setProfile(initialProfile);
      setScreen("home");
      setProfileLoading(false);
    }
  }, [initialProfile]);

  // If user exists but no profile → new user → onboarding
  useEffect(() => {
    if (!user) {
      setProfileLoading(false);
      setScreen("onboarding");
      return;
    }
    // Only act once parent has finished loading (isNewUser explicitly set)
    if (!initialProfile && isNewUser) {
      setProfileLoading(false);
      setScreen(s => s === "loading" ? "onboarding" : s);
    }
  }, [initialProfile, isNewUser, user]);

  useEffect(() => {
    if (user?.id) {
      Promise.all([loadChildren(user.id), loadTodayAttendance(user.id)]).then(([children, todayAtt]) => {
        setKids(children.map(c => {
          const att = todayAtt.find(a => a.child_id === c.id);
          const isIn = att && !att.sign_out_time;
          const signInTime = isIn ? new Date(att.sign_in_time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : null;
          const signInDate = isIn ? new Date(att.sign_in_time).toDateString() : null;
          return {
            id: c.id,
            name: c.name,
            age: c.age,
            parent: c.parent_name,
            parentPhone: c.parent_phone,
            parentEmail: c.parent_email,
            notify: c.notify_via,
            auth: c.auth_type,
            ageGroup: c.age_group,
            transport: c.transport,
            transportStatus: (() => {
              // Auto-reset transport status if it was set on a previous day
              if (!c.transport_status) return null;
              const today = new Date().toDateString();
              const updated = c.updated_at ? new Date(c.updated_at).toDateString() : null;
              return updated && updated !== today ? null : c.transport_status;
            })(),
            destination: c.destination,
            ec1Name: c.ec1_name,
            ec1Phone: c.ec1_phone,
            ec1Rel: c.ec1_rel,
            ec2Name: c.ec2_name,
            ec2Phone: c.ec2_phone,
            ec2Rel: c.ec2_rel,
            status: isIn ? "in" : "out",
            signInTime,
            signInDate,
            photo: c.photo || null,
            providerId: c.provider_id,
            initials: c.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2),
            hue: CHILD_HUES[c.id.charCodeAt(0) % CHILD_HUES.length],
          };
        }));
      });
      Promise.all([loadStaff(user.id), loadTodayStaffAttendance(user.id)]).then(([staffData, todayStaff]) => {
        setStaff(staffData.map(s => {
          const att = todayStaff.find(a => a.staff_id === s.id);
          const isIn = att && !att.clock_out_time;
          const clockIn = isIn ? new Date(att.clock_in_time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : null;
          return {
            id: s.id,
            name: s.name,
            role: s.role,
            pin: s.pin,
            driver: s.driver,
            status: isIn ? "in" : "out",
            clockIn,
            initials: s.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2),
            hue: "#4A6CF7",
          };
        }));
      });
    }
  }, [user, profile?.daycareN]);

  const rat = ratio(kids, staff);
  const ping = msg => { setToast(msg); setTimeout(() => setToast(null), 2800); };

  const doSign = async (sigData) => {
    const now = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    const { child, action } = modal;
    setModal(null); // Close immediately — don't wait for Supabase

    // Upload signature to Supabase Storage (with retry)
    let signatureUrl = null;
    if (sigData && user?.id) {
      const { supabase } = await import("./supabase");
      const blob = await fetch(sigData).then(r => r.blob());
      const path = `${user.id}/${child.id}_${Date.now()}.png`;
      let uploaded = false;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const { error: upErr } = await supabase.storage.from("signatures").upload(path, blob, { contentType: "image/png", upsert: true });
          if (!upErr) {
            const { data } = supabase.storage.from("signatures").getPublicUrl(path);
            signatureUrl = data.publicUrl;
            uploaded = true;
            break;
          }
        } catch (e) {
          console.error(`Signature upload attempt ${attempt + 1} failed:`, e);
          if (attempt < 2) await new Promise(r => setTimeout(r, 1500));
        }
      }
      if (!uploaded) console.warn("Signature upload failed after 3 attempts — attendance saved without signature");
    }

    if (action === "in") {
      setKids(p => p.map(k => k.id === child.id ? { ...k, status: "in", signInTime: now } : k));
      ping(`${child.name} signed in ✓`);
      if (user?.id) {
        const { error: siErr } = await recordSignIn(user.id, child.id, null, signatureUrl);
        if (siErr) {
          await enqueue({ type:"signIn", providerId:user.id, childId:child.id, timestamp:new Date().toISOString() });
          ping("Saved offline — will sync automatically");
        }
      }
    }
    if (action === "out") {
      setKids(p => p.map(k => k.id === child.id ? { ...k, status: "out", signInTime: null } : k));
      ping(`${child.name} signed out`);
      if (user?.id) {
        const { error: soErr } = await recordSignOut(user.id, child.id, signatureUrl);
        if (soErr) {
          await enqueue({ type:"signOut", providerId:user.id, childId:child.id, timestamp:new Date().toISOString() });
          ping("Saved offline — will sync automatically");
        }
      }
    }

  };

  const doPin = async () => {
    const now = new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    const { person } = modal; const isIn = person.status === "in";
    setStaff(p => p.map(s => s.id === person.id ? { ...s, status: isIn ? "out" : "in", clockIn: isIn ? null : now } : s));
    ping(isIn ? `${person.name} clocked out` : `${person.name} clocked in ✓`);
    if (user?.id) {
      if (isIn) await recordStaffClockOut(user.id, person.id).catch(e => console.error("clockOut error:", e));
      else await recordStaffClockIn(user.id, person.id).catch(e => console.error("clockIn error:", e));
    }
    setModal(null);
  };

  const sendSMS = async (to, message) => {
    if (!to || !SMS_ENABLED) return;
    try {
      await fetch("/api/send-sms", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to, message })
      });
    } catch (e) {
      console.error("SMS error:", e);
    }
  };

  const saveTransportStatus = async (kidId, status) => {
    if (user?.id) {
      const { supabase } = await import("./supabase");
      const { error } = await supabase.from("children").update({ transport_status: status, updated_at: new Date().toISOString() }).eq("id", kidId);
      if (error) {
        console.error("Transport status save error:", error);
        ping("⚠️ Transport status could not be saved. Please try again.");
      }
    }
  };

  const transportInFlight = useRef(new Set());
  const doTransport = async (kid, action) => {
    const key = `${kid.id}-${action}`;
    if (transportInFlight.current.has(key)) return;
    transportInFlight.current.add(key);
    setTimeout(() => transportInFlight.current.delete(key), 3000);
    const parent = kid.parent || "Parent";
    const dest = kid.destination || "school";
    const phone = formatPhone(kid.parentPhone) || null;
    const daycareName = profile?.daycareN || "Your daycare";
    console.log("doTransport called:", action, "phone:", phone);

    if (action === "transport_pickup") {
      setKids(p => p.map(k => k.id === kid.id ? { ...k, transportStatus: "picked_up" } : k));
      await saveTransportStatus(kid.id, "picked_up");
      await saveTransportLog(user.id, kid.id, "transport_pickup", kid.destination || "school");
      ping(`🚐 ${kid.name} picked up ✓`);
    }
    if (action === "transport_dropoff") {
      setKids(p => p.map(k => k.id === kid.id ? { ...k, transportStatus: "dropped_off" } : k));
      await saveTransportStatus(kid.id, "dropped_off");
      await saveTransportLog(user.id, kid.id, "transport_dropoff", kid.destination || "school");
      ping(`✓ ${kid.name} dropped off safely`);
    }
    if (action === "transport_return") {
      setKids(p => p.map(k => k.id === kid.id ? { ...k, transportStatus: "returned" } : k));
      await saveTransportStatus(kid.id, "returned");
      await saveTransportLog(user.id, kid.id, "transport_return", kid.destination || "school");
      ping(`✓ ${kid.name} returned safely`);
    }
    if (action === "transport_reset") {
      setKids(p => p.map(k => k.id === kid.id ? { ...k, transportStatus: null } : k));
      await saveTransportStatus(kid.id, null);
    }
  };

  const BOTTOM_TABS = [
    { id: "home",      label: "Home",    icon: "home"      },
    { id: "signin",    label: "Check-in", icon: "signin"   },
    { id: "reports",   label: "Reports", icon: "reports"   },
    { id: "ai",        label: "AI",      icon: "ai"        },
  ];

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;600;700;800;900&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes toastIn { from{opacity:0;transform:translateX(-50%) translateY(8px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }
        @keyframes pulse { 0%,100%{opacity:0.3} 50%{opacity:1} }
        @keyframes bounce { 0%{transform:scale(0)} 50%{transform:scale(1.2)} 75%{transform:scale(0.9)} 100%{transform:scale(1)} }
        @keyframes springIn { 0%{transform:scale(0.8);opacity:0} 60%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
        * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
        ::-webkit-scrollbar { display: none; }
        body { margin: 0; background: #fff; font-size: 16px; }
        input, select, textarea, button { font-family: 'Nunito', sans-serif; font-size: 15px; }
        /* UX Polish Phase */
        h1, h2, h3, .primary-header { font-weight: 700; letter-spacing: -0.02em; }
        .data-point { letter-spacing: 0.01em; }
        .secondary-label { font-weight: 500; color: #64748B; font-size: 0.875rem; }
        button:active { filter: brightness(0.95); }
        * { transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease; }
        .card { box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1) !important; border: none !important; }
      `}</style>

      <div style={{ minHeight: "100vh", background: "#F0F2F5", maxWidth: 768, margin: "0 auto", position: "relative", fontFamily: F, overflow: "hidden", border: "2px solid #E2E8F0", boxShadow: "0 0 40px rgba(0,0,0,0.10)" }}>
        <div style={{ overflowY: "auto", height: "100vh" }}>
          {screen === "loading" && <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: OFF_WHITE, fontFamily: F, gap: 16 }}><div style={{ width: 64, height: 64, borderRadius: 20, background: CRIMSON, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 20px rgba(153,27,27,0.3)", animation: "pulse 1.5s infinite" }}><svg viewBox="0 0 100 100" width="36" height="36" fill="none"><g stroke="white" strokeLinecap="round" strokeLinejoin="round" fill="none"><polyline points="6,30 50,5 94,30" strokeWidth="5"/><circle cx="28" cy="34" r="8" fill="white" stroke="none"/><line x1="28" y1="42" x2="28" y2="62" strokeWidth="6"/><polyline points="14,43 28,50 42,43" strokeWidth="5"/><line x1="28" y1="62" x2="20" y2="78" strokeWidth="5"/><line x1="28" y1="62" x2="36" y2="78" strokeWidth="5"/><circle cx="70" cy="31" r="9" fill="white" stroke="none"/><line x1="70" y1="40" x2="70" y2="64" strokeWidth="6"/><polyline points="55,41 70,50 85,41" strokeWidth="5"/><line x1="70" y1="64" x2="61" y2="80" strokeWidth="5"/><line x1="70" y1="64" x2="79" y2="80" strokeWidth="5"/></g></svg></div><div style={{ fontSize: 22, fontWeight: 700, color: NAVY, letterSpacing: "-0.02em" }}>toto</div><div style={{ fontSize: 13, color: "#94A3B8", fontWeight: 500 }}>Loading your daycare...</div></div>}
          {screen === "onboarding" && <Onboarding user={user} onSignOut={onSignOut} onComplete={async p => { setProfile(p); setScreen("home"); if (onSaveProfile) await onSaveProfile(p); }} />}
          {screen === "home"      && <Home        kids={kids} staff={staff} rat={rat} lang={lang} setLang={setLang} onNav={setScreen} profile={profile} />}
          {screen === "support"   && <Support     profile={profile} onBack={() => setScreen("home")} user={user} />}
          {screen === "settings"  && <ProviderSettings profile={profile} onSave={async p => { setProfile(p); ping("Settings saved ✓"); if (user?.id) await saveProfile(user.id, { ...p, email: user.email }); }} onBack={() => setScreen("home")} onLogout={onSignOut} />}
          {screen === "profile"   && <Onboarding existing={profile} onComplete={async p => { setProfile(p); setScreen("home"); if (onSaveProfile) await onSaveProfile(p); ping("Profile saved ✓"); }} />}
          {screen === "signin"    && <SignIn      kids={kids} onAction={(kid, act) => { if (act === "back") { setScreen("home"); return; } setModal({ type: "sig", child: kid, action: kid.status === "in" ? "out" : "in" }); }} onAdd={() => setAddM("child")} onEdit={async updated => { setKids(p => p.map(k => k.id === updated.id ? updated : k)); if (user?.id) await updateChild(updated.id, updated); }} onRemove={async id => { setKids(p => p.filter(k => k.id !== id)); if (user?.id) await removeChildDB(id); ping("Child removed"); }} />}
          {screen === "staff"     && <Staff       staff={staff} rat={rat} onClock={p => setModal({ type: "pin", person: p })} onAdd={() => setAddM("staff")} onEdit={async updated => { setStaff(p => p.map(s => s.id === updated.id ? updated : s)); if (user?.id) { const { supabase } = await import("./supabase"); await supabase.from("staff").update({ name: updated.name, role: updated.role, phone: updated.phone, photo: updated.photo, ...(updated.pin ? { pin: updated.pin } : {}) }).eq("id", updated.id); } ping("Staff updated ✓"); }} onRemove={async id => { setStaff(p => p.filter(s => s.id !== id)); if (user?.id) { const { supabase } = await import("./supabase"); await supabase.from("staff").update({ active: false }).eq("id", id); } ping("Staff removed"); }} onBack={() => setScreen("home")} />}
          {screen === "transport" && <Transport   kids={kids} onAction={doTransport} onBack={() => setScreen("home")} />}
          {screen === "reports"   && <Reports     kids={kids} user={user} profile={profile} onBack={() => setScreen("home")} />}
          {screen === "messages"  && <Messages    kids={kids} onBack={() => setScreen("home")} profile={profile} ping={ping} />}
          {screen === "incident"  && <IncidentReporter onBack={() => setScreen("home")} kids={kids} user={user} profile={profile} />}
          {screen === "ai"        && <AIAssistant onBack={() => setScreen("home")} />}
        </div>

        {/* Modals */}
        {modal?.type === "sig" && <SigPad child={modal.child} action={modal.action} onConfirm={doSign} onCancel={() => setModal(null)} />}
        {modal?.type === "pin" && <PinPad person={modal.person} onConfirm={doPin} onCancel={() => setModal(null)} />}
        {addM === "child" && <AddChild onAdd={async c => { if (user?.id) { const { data } = await saveChild(user.id, c); if (data?.[0]) c.id = data[0].id; } setKids(p => [...p, c]); setAddM(null); ping(`${c.name} added ✓`); }} onClose={() => setAddM(null)} />}
        {addM === "staff" && <AddStaff onAdd={async m => { if (user?.id) { const { data } = await saveStaff(user.id, m); if (data?.[0]) m.id = data[0].id; } setStaff(p => [...p, m]); setAddM(null); ping(`${m.name} added ✓`); }} onClose={() => setAddM(null)} />}

        <Toast msg={toast} />
      </div>
    </>
  );
}
